import {inject, Injectable, OnDestroy} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {firstValueFrom, Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {CmsApiService, UNKNOWN_ERROR_MESSAGE} from '../../core/cms-api.service';
import {LoggerService} from '../../core/logger.service';
import {JobStatusSubscriberService} from '../../core/jobstatussubcriber/job-status-subscriber.service';
import {IsActiveService} from '../../main-menu/jobstatus/is-active.service';
import {AppNotification, NotificationService} from '../../shared/notification.service';
import {CreateSectionsContainerParams, ObjectEditService} from '../../core/object-edit.service';
import {SectionsContainer} from '../../core/definitions/sections-container';
import {ObjectTypes} from '../../core/definitions/object-types';
import {System} from '../../core/definitions/system';
import {PrimusFormGroupService} from '../../core/primus-form-group.service';
import {ConfirmDialogComponent, ConfirmDialogData} from '../../object-edit/confirm-dialog/confirm-dialog.component';
import {
  ImportModuleAdminEventKind,
  ImportModuleBlankValues,
  ImportModuleCardKind,
  ImportModuleColumnProfile,
  ImportModuleColumnProfileColumn,
  ImportModuleConceptField,
  ImportModuleConceptRow,
  ImportModuleDecisionRow,
  ImportModuleFormPlacement,
  ImportModuleGroupKindSpec,
  ImportModuleIncompleteCard,
  ImportModuleLackingRight,
  ImportModuleLookupCheck,
  ImportModuleLookupSuggestion,
  ImportModuleMediaJoinSeveral,
  ImportModuleMediaMatch,
  ImportModuleMediaPatternError,
  ImportModuleObjectType,
  ImportModulePreflightResult,
  ImportModuleProfile,
  ImportModuleProfileSummary,
  ImportModuleRunSpec,
  ImportModuleSourceColumn,
  ImportModuleSourceRow,
  ImportModuleStoredDecision,
  ImportModuleTableStats,
  ImportModuleTargetField,
  ImportModuleTargetFields,
  ImportModuleUnresolvedId,
} from '../../core/definitions/import-module';
import {
  createGroupCardModel,
  createGroupMemberRow,
  createGroupSubBlockRow,
  GroupCardDescriptor,
  GroupCardModel,
  GroupHeaderFieldDef,
  GroupKind,
  GroupMemberFieldDef,
  GroupMemberRow,
  GroupSubBlockDef,
  GroupSubBlockRow,
  groupModelControls,
  groupModelMemberRows,
  memberRowControls,
  memberValueSourceKind,
  NAMED_OBJECT_TYPE_LABEL_KEY,
  subBlockRowControls
} from './group-card/group-card.model';
import {groupCardListTitle} from './group-card/group-card-title';
import {RunJob} from './run-job';
import {DebouncedRequest} from './debounced-request';
import {
  completeLookupKeys,
  createLookupDefinition,
  isLookupComplete,
  lookupControlList,
  lookupDisplayName,
  lookupPayload,
  LookupControls,
  LookupDefinition,
  lookupDefinitionControls,
  removeAlias,
} from './lookup-controls';
import {
  TemplatePart,
  unknownPlaceholders as unknownPlaceholderNames
} from './value-template';
import {ConceptOption} from './concept-picker/concept-option.model';
import {fieldProgress, FieldProgress, reviewTotals, ReviewTotals} from './value-review-page/value-review.model';
import {ComposeLineRow, composeLineRowControls, createComposeLineRow} from './compose-field/compose-field.model';
import {
  bareTemplateColumn,
  createValueSourceControls,
  FlatValueSource,
  flatValueSourceKind,
  setFlatValueSourceKind,
  SourcePiece,
  templateColumns,
  ValueSourceControls,
  LineIssue,
  ValueSourceKind,
  valueSourceIssue,
  valueSourceParts,
  valueSourceSummary,
} from './value-source';
import {
  BatchDefaultValues,
  batchFieldsRequest,
  buildMapping,
  buildMediaMapping,
  ConceptScanRequest,
  conceptScanRequest,
  ConfigurationState,
  CREATED_AT_TARGET,
  CREATED_BY_TARGET,
  DEFAULT_MEDIA_MATCH,
  DEFAULT_MEDIA_MATCH_PRESET,
  entryRows,
  GROUP_ID_KINDS,
  hasMappingEntry,
  heldBackCards,
  IDENTIFICATION_NUMBER_TARGET,
  livePreviewState,
  lookupCheckRequest,
  lookupLabelColumns,
  MappingEntry,
  MappingState,
  mediaEntryRows,
  MediaJoin,
  MediaSetting,
  MEDIA_MATCH_PRESETS,
  mediaMatchPresetFor,
  MediaMatchPresetId,
  mediaMatchPresetPattern,
  MediaOnMissing,
  memberFixedIsPick,
  PLACEMENT_EVENT_KIND,
  PreflightRequest,
  preflightRequest,
  profileChanged,
  ProfileComparison,
  profileComparison,
  ProfileSaveRequest,
  profileSaveRequest,
  RowRange,
  runSpec,
  SessionSnapshot,
  sessionSnapshot,
  SourceFile,
  subBlockPicked,
  usedGroupIds,
  usedSubBlockKeys,
} from './import-payload';
import {
  decidedRows,
  pendingRestore,
  PendingRestore,
  restoreProfile,
  storedDecisionRows,
} from './profile-restore';
import {
  BlankedPickKey,
  BlankedPlace,
  carryProfile,
  CarriedProfile,
  downloadProfileFile,
  parseProfileFile,
} from './profile-transfer';
import {ImportView, viewOfParam} from './step-bar/import-steps';
import {
  blankCellCount,
  BlankValueSuggestion,
  blankValueSuggestions,
  hasBlankValue,
  withBlankValue,
  withoutBlankValue,
} from './blank-values-step/blank-values.model';
import {
  ColumnUsage,
  columnUsages,
  fieldUsageKind,
  memberUsageKind,
  subBlockUsageKind,
  UsageSource,
} from './column-usages';
import {EventGroupKind, PreflightErrorSplit} from './preflight-result/preflight-warnings';
import {mediaPatternErrorOf, mediaPatternErrorText} from './media-pattern-error';
import {RowCount} from './row-count';
import {sharedRequest} from './shared-request';
import {
  fieldPlacement, groupBySection, MEDIA_FIELDS_PLACEMENT, otherFieldsPlacement, PlacedLine, TargetSection,
} from './mapping-step/target-sections';
import {
  buildLivePreviewView, CardRowValues, LivePreviewView, MEDIA_VALUES_SECTION, MEDIA_VALUES_TITLE_KEY, nextWarningRow,
  previewCardParts, PreviewLine, RowValues,
} from './live-preview/live-preview';
import {FixedPick, PickNamesService} from './live-preview/pick-names.service';

/**
 * One batch-config concept picker the mapping needs (a mapped title field
 * pulls in title status + language, a history-event field pulls in the
 * event type, …). `GET /batch_fields` decides which appear from the mapped
 * target fields' parent object types.
 */
export interface BatchField {
  key: string;
  label: string;
  required: boolean;
  default: string | null;
  options: ConceptOption[];
}

/**
 * One entry of `groupKindConfigs` - everything that varies between the
 * group-card kinds, so the shared plumbing (validity, hasTargets, add/
 * remove, batch-field hiding, the cards' place in the mapping list) can iterate one
 * list instead of a hand-written copy per kind. `sectionTitleKey` names the
 * kind in the add search, distinct from `GroupCardDescriptor.titleKey`
 * (the card's own per-group title, e.g. picked-type name); `hintKey` is
 * shown above an opened card of the kind.
 */
export interface GroupKindConfig {
  kind: GroupKind;
  sectionTitleKey: string;
  hintKey?: string;
  // The `object_type` naming this kind in `/target_fields`' `card_kinds`,
  // which places its cards in the mapping list.
  cardObjectType: string;
  buildDescriptor: () => GroupCardDescriptor;
  // Batch keys this kind's card header replaces once ≥1 group exists -
  // hidden from `genericBatchFields`. A function, not a static list - the
  // `flatGroupKindConfig` kinds derive theirs from `groupKindSpecs`,
  // which is empty until `GET /object_types` resolves.
  hiddenBatchKeys: () => readonly string[];
  // Whether a group must actually contribute a mapped member row to count -
  // false for a kind where a header-only group is a complete, meaningful
  // configuration on its own.
  requiresMappedMember: boolean;
}

/** The `ImportModuleGroupKindSpec.options` key that gives a kind's cards the
 * create-missing controls. */
const GROUP_OPTION_CREATE_MISSING = 'create_missing';

/** The one history-event batch field on the card *header* (the event
 * type); role / status sit on the Named / Place sub-blocks. */
const HISTORY_EVENT_TYPE_BATCH_KEY = 'history_event_type_id';

/**
 * A Named-party sub-block header select ← the batch field its option list
 * comes from. The keys are the wire names the `history_event_groups[].named[]`
 * payload entry uses.
 */
const NAMED_SUB_BLOCK_HEADER: { headerKey: string; batchKey: string; required: boolean }[] = [
  {headerKey: 'role_id', batchKey: 'history_event_named_role_id', required: true},
  {headerKey: 'role_status_id', batchKey: 'history_event_role_status_id', required: false},
];

/** The Place twin of `NAMED_SUB_BLOCK_HEADER` - the wire names the
 * `history_event_groups[].places[]` payload entry uses. */
const PLACE_SUB_BLOCK_HEADER: { headerKey: string; batchKey: string; required: boolean }[] = [
  {headerKey: 'role_id', batchKey: 'history_event_place_role_id', required: true},
  {headerKey: 'role_status_id', batchKey: 'history_event_place_role_status_id', required: false},
];

/**
 * The singular batch-field keys the Historikkhendelser group cards replace
 * once ≥1 group is added - hidden from genericBatchFields and rendered by
 * the card (the event type in the card header, role / status per Named-party
 * and per Place sub-block).
 */
const HISTORY_EVENT_BATCH_KEYS: readonly string[] = [
  HISTORY_EVENT_TYPE_BATCH_KEY, ...[...NAMED_SUB_BLOCK_HEADER, ...PLACE_SUB_BLOCK_HEADER].map(h => h.batchKey)];

const ADMIN_EVENT_SUB_TYPE_BATCH_KEY = 'admin_event_sub_type_id';
const ADMIN_EVENT_PURPOSE_TYPE_BATCH_KEY = 'admin_event_purpose_type_id';
/** The field title of each profile field that holds one database id. */
const PICK_TITLE_KEYS: Record<BlankedPickKey, string> = {
  collection_id: 'TRANS__FIELD__COLLECTION',
  reg_level_id: 'TRANS__FIELD__REGISTRATION_LEVEL_BASE__REGISTRATION_LEVEL',
  status_id: 'TRANS__FIELD__STATUS__STATUS_TYPE',
};
/** The title of each history-event sub-block. */
const SUB_BLOCK_TITLE_KEYS: Record<GroupSubBlockDef['kind'], string> = {
  named: 'TRANS__HISTORY_EVENT_NAMED__ITEM__NAMED',
  place: 'TRANS__FIELD__PLACE',
};
/** One shared instance, so a card bound to "no lookups" sees the same
 * array on every change-detection pass. */
const NO_LOOKUPS: LookupDefinition[] = [];
/** The shared answer for a card with no required member missing. */
const NO_MISSING_MEMBERS: readonly GroupMemberFieldDef[] = [];

/** A card's key and one of its member fields as one set key. */
function memberKey(cardKey: string | undefined, fieldId: string): string {
  return `${cardKey ?? ''}\n${fieldId}`;
}

/** The event kind the preflight names a card of a group kind by, for the
 * kinds whose values it names by the card's key. */
const EVENT_GROUP_KINDS: Partial<Record<GroupKind, EventGroupKind>> = {
  history_event: 'history', admin_event: 'admin', placement_event: 'admin',
};

/** Pause after the last value-review edit before the session stash is
 * written: the stash serialises the whole review, candidates included. */
export const REVIEW_STASH_DELAY_MS = 500;

/** Pause after the last change to what the mapping is built from before
 * the lookups are checked: each check reads the whole table. */
export const LOOKUP_CHECK_DELAY_MS = 600;

/** Pause after the last mapping change or row step before the live
 * preview asks for its row. */
export const LIVE_PREVIEW_DELAY_MS = 600;

/** What `POST /source/inspect` answers. */
type SourceInspectResult = Awaited<ReturnType<CmsApiService['importModuleInspectSource']>>;

/** The pauses before each new ask for a file the media server cannot
 * serve yet, the last one repeating. */
export const SOURCE_WAIT_DELAYS_MS = [2000, 3000, 5000, 8000, 10000];

/** How long after the first ask the tables are waited for before the file
 * counts as not readable. */
export const SOURCE_WAIT_LIMIT_MS = 180_000;

/** How long after its upload a stashed file the media server cannot serve
 * is taken as not ready yet rather than gone. */
export const SOURCE_NOT_READY_RESUME_WINDOW_MS = 15 * 60_000;

/** A rejected source request's answer said the media server cannot serve
 * the file yet: on the routes that read the source, 409 is only ever
 * that (`code: source_not_ready`). */
function isSourceNotReady(e: any): boolean {
  return e?.status === 409;
}

/** The value-review entry card's figures: the review's totals, and each
 * field with values the run would skip, in scan order. */
export interface ValueReviewSummary {
  totals: ReviewTotals;
  undecidedFields: { fieldId: string; undecided: number }[];
}

const NO_VALUE_REVIEW: ValueReviewSummary = {
  totals: reviewTotals([]),
  undecidedFields: [],
};

export type SourceColumn = ImportModuleSourceColumn;

/** A table's `/source/column_profile` as the steps read it. */
export interface SourceColumnProfile {
  rowsScanned: number;
  // Each profiled column by its name.
  columns: ReadonlyMap<string, ImportModuleColumnProfileColumn>;
  lookupSuggestions: ImportModuleLookupSuggestion[];
}

function sourceColumnProfile(res: ImportModuleColumnProfile): SourceColumnProfile {
  return {
    rowsScanned: res.rows_scanned,
    columns: new Map(res.columns.map(column => [column.name, column])),
    lookupSuggestions: res.lookup_suggestions,
  };
}

/** A column a lookup reads that its table does not have. */
export interface LookupColumnProblem {
  column: string;
  table: string;
}

/** What tells the column profile's suggested lookups apart. */
export function lookupSuggestionKey(suggestion: ImportModuleLookupSuggestion): string {
  return JSON.stringify([suggestion.code_column, suggestion.table, suggestion.key_column]);
}

/** A target field, or one of the targets the page adds itself (the
 * identification number, the created date and user), which carries no
 * form placement. */
export type TargetField =
  Omit<ImportModuleTargetField, keyof ImportModuleFormPlacement> & Partial<ImportModuleFormPlacement>;

/**
 * One mapping: a target field ← its value source (see `FlatValueSource`).
 * A template is a plain string with `{Column}` placeholders; a plain
 * literal (no placeholder) is a fixed value written to every row.
 * Composition happens per row in `POST /run`.
 */
export interface Assignment extends FlatValueSource {
  id: number;
  targetField: TargetField | null;
  // The field that places the row in the mapping list: its last picked
  // `targetField`, taken over while the row is not the opened line. Typing
  // in the opened row's field box, or picking another field there, leaves
  // the row where it is until it closes, so its focused input is kept.
  placedField: TargetField | null;
  targetFieldControl: FormControl<string | TargetField | null>;
  filteredTargetFields: TargetField[];
  // root-scope picker for a hierarchic concept target
  // (`concept_root_id`). `conceptRootOptions` is emptied on every
  // target-field change and filled (`loadConceptRoots`) while the row is
  // the opened line, or when a restore gives it a root pick. It stays empty - hiding the picker - for a
  // non-concept field or a concept list with no more than one root term
  // (nothing to scope by).
  conceptRootControl: FormControl<string | null>;
  conceptRootOptions: ConceptOption[];
}

/** One line of the mapping list: a flat row or a group card. */
export type MappingLine = Assignment | GroupCardModel;

/** One line of the "Gjenstanden får" list: a flat `row`, or a `card` with
 * its kind's `cfg` and its `index` among that kind's cards. */
export interface TargetListLine {
  row: Assignment | null;
  card: GroupCardModel | null;
  cfg: GroupKindConfig | null;
  index: number;
}

/** One pick of the add search: a group kind the object type has fields
 * for, with whether its cards can create the terms they miss, or a flat
 * target field, `media` when it is a field of the linked media. */
export type AddSearchOption =
  | { cfg: GroupKindConfig; createsMissing: boolean; field?: undefined; media?: undefined }
  | { field: TargetField; media: boolean; cfg?: undefined; createsMissing?: undefined };

/** What the add search offers, under the mapping list's section
 * headings: each group kind where its cards sit, each field where a row
 * onto it sits. */
export type AddSearchOptions = TargetSection<AddSearchOption>[];

/** A flat row's unknown placeholders, collapsed source and issue, each
 * filled in the first time the template asks for it. The
 * source and the issue are kept with the choice they were built for,
 * since a switch from Fast verdi to Kolonne moves no `mappingVersion`. */
interface RowPreview {
  unknownPlaceholders?: string[];
  source?: { kind: ValueSourceKind; pieces: SourcePiece[] };
  issue?: { kind: ValueSourceKind; issue: LineIssue | null };
}

/** What the template reads off the mapping, for one `mappingVersion`. */
interface MappingDerived {
  version: number;
  // `mappingReady`'s mapping half: a mapping entry exists, every required
  // batch field, group header and sub-block role is picked, and every
  // counted card sends a value onto each of its required members.
  mappingComplete?: boolean;
  genericBatchFields?: BatchField[];
  incompleteCards?: ReadonlySet<GroupCardModel>;
  missingMembers?: ReadonlyMap<GroupCardModel, readonly GroupMemberFieldDef[]>;
  memberIssueCards?: ReadonlySet<GroupCardModel>;
  livePreviewHeldBack?: readonly ImportModuleIncompleteCard[];
  addSearchOptions?: AddSearchOptions;
  usagesByColumn?: Map<string, ColumnUsage[]>;
  // `statusColumnsText`, with the status field id it was built for.
  statusColumns?: { fieldId: string; text: string };
  lookupUsages?: Map<string, string[]>;
  rowPreviews: Map<Assignment, RowPreview>;
}

/** A Forhåndskontroll result's errors split by whether they refuse the
 * run, and whether any does. */
interface PreflightRefusal {
  result: ImportModulePreflightResult;
  split: PreflightErrorSplit;
  refuses: boolean;
}

/** The session-resume stash: the uploaded file, and the configuration
 * built on it once there is one. */
interface SessionStash {
  dmsIdentifier: string;
  fileName: string;
  fileSize?: number;
  uploadedAt?: number;
  session?: SessionSnapshot;
}

/** Whatever carries compose-mode lines: a flat assignment or a group-card
 * member row. */
export type ComposeHost = Pick<ValueSourceControls, 'composeRows'>;

/** `baseline` with no `locked` in its configuration. A baseline stashed
 * from a save body that carried `locked` would otherwise read as changed
 * against every save body the page builds, none of which carries it. */
function withoutLocked(baseline: ProfileComparison): ProfileComparison {
  const configuration = JSON.parse(baseline.configuration);
  if (!('locked' in configuration)) {
    return baseline;
  }
  delete configuration.locked;
  return {...baseline, configuration: JSON.stringify(configuration)};
}

/**
 * Prime fields to keep as batch defaults — the ones that are genuinely
 * the same for every imported row. The rest of the prime fields (count,
 * designations, brief description, …) are per-object data that comes from
 * mapped columns, not a batch default.
 *
 * `object_status_type_id` is an exception to that split, not a relaxation
 * of it: object status usually does vary per row, but some sources assign
 * the identical status to every object in the whole import, with no source
 * column carrying it at all — for that shape a batch default is the only
 * place it can come from.
 *
 * Names are the leaf sub-field the prime-fields builder flattens to
 * (static_overview_fields._set_matched_field_props): `reg_level` →
 * `registration_level_id`, `collection` → `collection_id`, `object_status`
 * → `object_status_type_id`.
 */
const BATCH_DEFAULT_STATUS_FIELD = 'object_status_type_id';
const BATCH_DEFAULT_FIELDS = ['registration_level_id', 'collection_id', BATCH_DEFAULT_STATUS_FIELD];

/**
 * The Import module page's state, provided on the page and shared by its
 * step components: the source file, table and object type, the mapping and
 * everything it is built from, the value review, the profiles, the session
 * stash, and the preflight and run.
 *
 * Every input the mapping is built from bumps `mappingVersion`, and the
 * values read off the mapping (`canRun`, `genericBatchFields`, the row
 * previews) are built once per version. An
 * async request that resets page state and then awaits an answer checks
 * `applyToken` afterwards, and `/batch_fields`, the preflight, the lookup
 * check and the live preview carry their own request sequences, so a late
 * answer never lands over a newer one.
 */
@Injectable()
export class ImportSessionStore implements OnDestroy {
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly notificationService: NotificationService = inject(NotificationService);
  private readonly objectEditService: ObjectEditService = inject(ObjectEditService);
  private readonly formGroupService: PrimusFormGroupService = inject(PrimusFormGroupService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly jobStatusSubscriber: JobStatusSubscriberService = inject(JobStatusSubscriberService);
  private readonly isActiveService: IsActiveService = inject(IsActiveService);
  private readonly pickNames: PickNamesService = inject(PickNamesService);

  // --- source file ---
  uploading = false;
  uploadError = '';
  dmsIdentifier = '';
  fileName = '';
  // The uploaded file's size in bytes; `null` when the session was resumed
  // from a stash written without it.
  fileSize: number | null = null;
  // When the file's upload finished, in epoch ms; `null` when the session
  // was resumed from a stash written without it.
  private uploadedAt: number | null = null;
  // The stashed file of the session before could not be read on init, so
  // the stash was dropped; until a new file is uploaded the page says so.
  previousFileGone = false;
  // A file has been uploaded on this page: the stash is that file's.
  private fileUploaded = false;

  // --- object type + native batch-defaults view ---
  objectTypes: ImportModuleObjectType[] = [];
  // The flat group-card kinds' specs (`group_kinds`), served alongside
  // `objectTypes` since both are static for the whole session. Empty until
  // `objectTypesLoaded` resolves - every reader (`flatGroupKindConfig`'s
  // closures) tolerates that the same way it tolerates `objectTypes` being
  // briefly empty.
  groupKindSpecs: ImportModuleGroupKindSpec[] = [];
  // The admin-event kinds (`admin_event_kinds`), served in the same
  // response: each kind's own `/target_fields` type and required member.
  private adminEventKinds: ImportModuleAdminEventKind[] = [];
  // Image's type (`image_superobject_type_id`), served in the same
  // response: it scopes the `/target_fields` call for the fields a row can
  // write onto the images it links.
  private imageSuperobjectTypeId: string | null = null;
  // Resolves once `objectTypes` above is populated - `applyProfile` awaits
  // this before resolving a stored profile's object_type against it, since
  // `init` fires the two loads (this one, loadProfiles) independently and
  // a profile can otherwise be picked before this one lands.
  private objectTypesLoaded: Promise<void> = Promise.resolve();
  objectTypeControl = new FormControl<string>('');
  buildingModel = false;
  // Numbers every batch-defaults model build; only the latest one applies.
  private modelBuildRequestSeq = 0;
  batchDefaults: SectionsContainer;
  // The batch defaults a run requires (collection, registration level) are
  // picked. Read from the form when it changes, not on every check.
  batchDefaultsComplete = false;
  private batchDefaultsSubscription?: Subscription;
  // The batch defaults' collection, registration level and status as last
  // read, joined; `null` without a batch-defaults model.
  private batchDefaultPicks: string | null = null;

  // --- discovered tables ---
  // Read once the file is uploaded or resumed, whatever the object type.
  inspecting = false;
  // The last read of the file's tables failed; the table card offers to
  // read them again.
  inspectFailed = false;
  // The media server cannot serve the file yet; the read asks again after
  // a pause, `inspecting` staying set meanwhile.
  waitingForFile = false;
  // Numbers every read of the file's tables; only the answer to the latest
  // one applies. A new or cleared file advances it too.
  private inspectRequestSeq = 0;
  // Ends the pause of a read waiting for the file before its time.
  private endSourceWait: (() => void) | null = null;
  tables: string[] = [];
  selectedTable = '';
  // Each table's row and column count by name, for the file
  // `tableStatsFile` names. Asked for once per file, after inspect, so the
  // table list shows the names before the counts; kept over a new object
  // type. An upload or a cleared file empties both, so an answer for the
  // file before never applies.
  tableStats: ReadonlyMap<string, ImportModuleTableStats> = new Map();
  private tableStatsFile: string | null = null;
  // The selected table's first rows, from its columns' answer.
  firstRows: ImportModuleSourceRow[] = [];
  // Any failure reading the uploaded file from the media server (inspect,
  // columns, batch fields, concept scan). Shows inline; often transient
  // right after upload, so a retry usually clears it.
  sourceError: string | null = null;

  // --- column profile ---
  // The selected table's column profile; `null` until it arrives. Asked
  // for once per file and table and kept for the session, never stashed.
  columnProfile: SourceColumnProfile | null = null;
  columnProfileLoading = false;
  columnProfileFailed = false;
  // The profiles asked for, by file and table, as promises, so a table
  // picked again shares the request already sent. A failed one is
  // dropped, so the next ask retries.
  private columnProfiles = new Map<string, Promise<SourceColumnProfile>>();
  // Numbers every ask for the selected table's profile; only the latest
  // one applies its answer.
  private columnProfileSeq = 0;

  // --- column → field mapping ---
  // The selected table's columns and first rows are loading. They need
  // only the file, and an object-type change keeps them.
  loadingColumns = false;
  // Numbers every columns request; only the answer to the latest one
  // applies. A discovery reset advances it too, so no answer lands after
  // the file changed.
  private columnsRequestSeq = 0;
  // The selected table's columns have loaded.
  private columnsReady = false;
  // The selected table's columns failed to load; the table card offers to
  // load them again, and an object-type change keeps the message.
  columnsFailed = false;
  // The object type's target fields are loading.
  loadingTargetFields = false;
  // The object type's target fields failed to load; the table card offers
  // to load them again.
  targetFieldsFailed = false;
  // Numbers every target-fields request; only the answer to the latest one
  // applies. An object-type change and a discovery reset advance it too.
  private targetFieldsRequestSeq = 0;
  // The selected object type's target fields have loaded. A pending
  // restore waits for these and `columnsReady` both
  // (`applyPendingWhenReady`).
  private targetFieldsReady = false;
  // The target-fields load of an object type, loaded or on its way, which
  // a table pick shares; dropped when it fails.
  private targetFieldsLoad: {objectType: string; loaded: Promise<boolean>} | null = null;
  columns: SourceColumn[] = [];
  // Columns of the *other* tables in the source file, fetched on demand
  // for a lookup's key column and a lookup row's value column. Keyed by
  // table name.
  lookupColumns: { [table: string]: SourceColumn[] } = {};
  readonly lookupColumnsLoading = new Set<string>();
  // The tables whose columns failed to load, with the error it gave.
  readonly lookupColumnsFailed = new Map<string, string>();
  private lookupTablesMemo: { tables: string[]; selectedTable: string; available: string[] } | null = null;
  assignments: Assignment[] = [];
  // The rows onto a field of the linked media (`mediaTargetFields`), held
  // apart from `assignments`: they go out as `media_mapping`, and only
  // while the media join has a column.
  mediaAssignments: Assignment[] = [];
  private columnNames = new Set<string>();
  // Every field the object type accepts, from GET /target_fields. The flat
  // list picker (`targetOptions`) is this minus the group-owned fields;
  // the group card descriptors keep the group-owned ones.
  private allTargetFields: TargetField[] = [];
  // GenericEvent's own target fields (its Name), from a SEPARATE
  // /target_fields call scoped to the kind's `superobject_type_id` - never
  // merged into allTargetFields: the shared `field_id` they carry (the Name
  // `meta_field` every artifact type uses) leaves no marker to tell the two
  // occurrences apart other than which call produced them.
  private adminEventTargetFields: TargetField[] = [];
  // PlacementEvent's own target fields (Location, date), from a separate
  // /target_fields call scoped to the kind's `superobject_type_id`.
  private placementEventTargetFields: TargetField[] = [];
  // Image's own target fields, from a separate /target_fields call scoped
  // to `imageSuperobjectTypeId`: the fields a `mediaAssignments` row
  // can pick.
  private mediaTargetFields: TargetField[] = [];
  targetOptions: TargetField[] = [];
  /** Whether the user may map the created date / user from the source (the
   * `__created_at__` / `__created_by__` targets), as the latest
   * `/target_fields` answered. */
  canOverrideCreatedRevision = false;
  // Where each card kind's cards sit, by its `card_kinds` object type,
  // where each client-side target sits, by its target id, and where a line
  // in no section of the form sits.
  private cardKindPlacements = new Map<string, ImportModuleCardKind>();
  private specialTargetPlacements: { [target: string]: ImportModuleFormPlacement } = {};
  private otherFieldsPlacement: ImportModuleFormPlacement = otherFieldsPlacement([]);
  private nextAssignmentId = 0;
  // Compose-field line keys ('0', '1', … - the structured line
  // repeater), never reused, so a `track`ed row stays stable across a
  // reorder or a sibling line's removal.
  private nextComposeRowId = 0;
  private rowSubscriptions = new Subscription();
  // The lookups' control subscriptions: they live as long as the lookups,
  // which outlast a mapping reset when the columns or target fields land.
  private lookupSubscriptions = new Subscription();
  readonly mappingChanged = new Subject<void>();
  // Moves on every change to what the mapping is built from - a
  // `mappingChanged` emit, a source, discovery or batch-field reset, a
  // descriptor rebuild - so `mappingDerived` is rebuilt only then, not on
  // every change detection. A change that bypasses all of these leaves
  // `canRun`, `genericBatchFields` and the row previews stale.
  private mappingVersion = 0;
  /** `mappingVersion`, for a component that memoises what it reads off
   * the mapping. */
  get currentMappingVersion(): number {
    return this.mappingVersion;
  }
  private mappingDerived: MappingDerived = {version: -1, rowPreviews: new Map()};
  // Generation guard: every async entry point that resets
  // page state and then awaits a response bumps this and re-checks it
  // after the await, so a stale response (a superseded selectTable/profile
  // apply/object-type change) can never clobber a newer one that already
  // landed. Not resource cleanup — no subscription/timer to cancel, just a
  // staleness check.
  private applyToken = 0;
  // `/concept_roots` answers by object type and field id, as promises so
  // rows asking for the same field at once share one request. Emptied by
  // `resetTargetDiscovery`; a failed request is dropped so the next ask
  // retries.
  private conceptRootsCache = new Map<string, Promise<ConceptOption[]>>();
  // `targetSections` as built for a `mappingVersion` and a
  // `placementVersion`, which moves when a row's `placedField` does.
  private targetSectionsMemo: {
    mappingVersion: number; placementVersion: number; sections: TargetSection<TargetListLine>[];
  } | null = null;
  private placementVersion = 0;
  // The one mapping line shown with its controls; every other row and
  // card shows its one-line summary. Page state only: no request body
  // reads it, so opening or closing a line emits no `mappingChanged`.
  openedLine: MappingLine | null = null;

  // --- batch settings ---
  batchFields: BatchField[] = [];
  batchControls: { [key: string]: FormControl<string | null> } = {};
  // Each batch control's `valueChanges` subscription, by key, so a key
  // `/batch_fields` stops returning drops its own subscription.
  private batchControlSubscriptions = new Map<string, Subscription>();
  // The `batchFields` list the group card descriptors were last built from.
  private descriptorBatchFields: readonly BatchField[] = [];
  // Numbers every `/batch_fields` request; only the answer to the latest
  // one is applied.
  private batchFieldsRequestSeq = 0;
  // The body of the latest `/batch_fields` request (as JSON) and the
  // `applyToken` it was sent under. Its answer is applied or on its way,
  // so the same body under the same token is not sent again.
  private batchFieldsSent: { body: string; token: number } | null = null;
  loadingBatchFields = false;
  overwriteExistingControl = new FormControl<boolean>(false, {nonNullable: true});
  /** Publish every created object to DigitaltMuseum (one PublishingEvent
   * per row). Off by default - publishing is an explicit choice the
   * operator makes for a run, never a side effect of importing. */
  publishControl = new FormControl<boolean>(false, {nonNullable: true});
  /** An optional stretch of the file to run (1-based, inclusive, either
   * alone) - a trial run on the first rows, or a large file split into
   * several runs. Blank runs the whole file. A choice for one run, so it
   * is neither saved in a profile nor stashed in the session. */
  runFirstRowControl = new FormControl<number | null>(null);
  runLastRowControl = new FormControl<number | null>(null);
  // A loaded profile's batch values: a batch key's control starts at its
  // value here when `/batch_fields` first returns the key.
  // `/batch_fields` returns an incrementally growing set as group cards /
  // mapped fields pull in more keys, so this is NOT cleared after first
  // use — only when discovery resets (a new object type / table / file).
  profileBatchValues: { [key: string]: string } = {};

  // --- group cards ---
  // One card per group the mapping needs, of every kind in
  // `groupKindConfigs`. The page owns each model (so its controls'
  // subscriptions and payload composition live here); `<app-group-card>`
  // renders it. Group-owned member fields never appear in the flat
  // assignment list.
  //
  // `groupKindConfigs` drives every per-kind code path below. A flat kind
  // is one `flatGroupKindConfig(...)` call, built from `groupKindSpecs`
  // (see `ImportModuleGroupKindSpec`); a kind on the `group_id` wire key
  // is also listed in `GROUP_ID_KINDS` (see `import-payload.ts`). History
  // event, admin event and placement have hand-written descriptors.
  readonly groupKindConfigs: GroupKindConfig[] = [
    {
      kind: 'history_event',
      sectionTitleKey: 'TRANS__IMPORT__HISTORY_EVENT_SECTION',
      hintKey: 'TRANS__IMPORT__HISTORY_EVENT_HINT',
      cardObjectType: 'SuperobjectHistoryEventItem',
      buildDescriptor: () => this.buildHistoryEventDescriptor(),
      hiddenBatchKeys: () => HISTORY_EVENT_BATCH_KEYS,
      requiresMappedMember: false,
    },
    this.flatGroupKindConfig('reference', 'ReferenceItem', 'TRANS__SECTION__REFERENCES__TITLE'),
    this.flatGroupKindConfig('alternative_no', 'AlternativeNoItem', 'TRANS__IMPORT__ALTERNATIVE_NO_SECTION'),
    this.flatGroupKindConfig('measurement', 'MeasurementItem', 'TRANS__IMPORT__MEASUREMENT_SECTION'),
    this.flatGroupKindConfig('material', 'MaterialItem', 'TRANS__IMPORT__MATERIAL_SECTION'),
    this.flatGroupKindConfig('title', 'SuperobjectTitleItem', 'TRANS__IMPORT__TITLE_SECTION'),
    {
      kind: 'admin_event',
      sectionTitleKey: 'TRANS__IMPORT__ADMIN_EVENT_SECTION',
      cardObjectType: ObjectTypes.GENERIC_EVENT,
      buildDescriptor: () => this.buildAdminEventDescriptor(),
      hiddenBatchKeys: () => [ADMIN_EVENT_SUB_TYPE_BATCH_KEY, ADMIN_EVENT_PURPOSE_TYPE_BATCH_KEY],
      requiresMappedMember: false,
    },
    {
      kind: 'placement_event',
      sectionTitleKey: 'TRANS__SUPEROBJECT_TYPE_ADM_EVENT_PLACEMENT',
      hintKey: 'TRANS__IMPORT__PLACEMENT_EVENT_HINT',
      cardObjectType: 'PlacementEvent',
      buildDescriptor: () => this.buildPlacementEventDescriptor(),
      hiddenBatchKeys: () => [],
      requiresMappedMember: false,
    },
  ];
  private readonly groupKinds: readonly GroupKind[] = this.groupKindConfigs.map(cfg => cfg.kind);
  // The "place new terms under" picker's options per kind, and the
  // requests for them by kind and object type - a kind's list is asked for
  // once per object type, the first time one of its cards needs it, and a
  // failed request is forgotten. Declared ahead of `groupDescriptors`,
  // whose initialiser already reads the options.
  private createMissingParentOptions: Partial<Record<GroupKind, ConceptOption[]>> = {};
  private readonly createMissingParentRequests = new Map<string, Promise<ConceptOption[]>>();
  groupModels: Record<GroupKind, GroupCardModel[]> = Object.fromEntries(
    this.groupKindConfigs.map(cfg => [cfg.kind, []]),
  ) as Record<GroupKind, GroupCardModel[]>;
  groupDescriptors: Record<GroupKind, GroupCardDescriptor> = Object.fromEntries(
    this.groupKindConfigs.map(cfg => [cfg.kind, cfg.buildDescriptor()]),
  ) as Record<GroupKind, GroupCardDescriptor>;

  // 'g0', 'g1', … stable across every kind (history entries carry
  // `history_event_group`, admin-event and placement entries
  // `admin_event_group`, the `GROUP_ID_KINDS` entries `group_id` - separate
  // wire namespaces, but one counter keeps every key globally distinct).
  private nextGroupKey = 0;
  // Named-party and Place sub-block keys ('g0.n0', 'g0.n1', …); one counter,
  // never reused, so a sub-key stays stable.
  private nextSubBlockKey = 0;

  // --- media link ---
  // Opt-in: does this import attach each row to an already-uploaded media
  // object? Off for a plain tabular import - the media section stays hidden.
  linkMediaControl = new FormControl<boolean>(false, {nonNullable: true});
  // The source column whose cell each row is matched to media files by,
  // by the match rule below; a matched file is linked to the artifact the
  // row creates. The media need not be in the same collection.
  mediaJoinColumnControl = new FormControl<string | null>(null);
  // A restored media section whose join column the source lacks: a save
  // keeps it as it was until a join column is picked or media linking is
  // turned off.
  private keptMedia: MediaSetting | null = null;
  // Optional scope: only match media in this folder. Prevents matching a
  // same-named image elsewhere in the instance; a "Media og vedlegg"
  // upload batch lands in one folder. Empty = search the whole instance.
  mediaFolders: { id: string; name: string }[] = [];
  mediaFolderControl = new FormControl<string | null>(null);
  // What a run does with a row whose filename matches no single media
  // object. `block` (default) refuses the whole run; the operator switches
  // this after seeing the Forhåndskontroll.
  mediaOnMissingControl =
    new FormControl<MediaOnMissing>('block', {nonNullable: true});
  // How a key is made from the join cell and from a file's name: the whole
  // name, or what a pattern finds in it; and whether a key's numbers lose
  // their leading zeros. The pattern is a Python regular expression, whose
  // syntax is not checked here. It is the one sent: a preset other than
  // `custom` writes its own pattern into it, and `custom` lets the
  // operator type it.
  mediaMatchModeControl =
    new FormControl<ImportModuleMediaMatch['mode']>('file_name', {nonNullable: true});
  mediaMatchPatternControl = new FormControl<string>('', {nonNullable: true});
  // The named rule the pattern rule shows; not sent.
  readonly mediaMatchPresets = MEDIA_MATCH_PRESETS;
  mediaMatchPresetControl =
    new FormControl<MediaMatchPresetId>(DEFAULT_MEDIA_MATCH_PRESET, {nonNullable: true});
  mediaIgnoreLeadingZerosControl = new FormControl<boolean>(false, {nonNullable: true});
  // What a row with several matching files does.
  mediaJoinSeveralControl =
    new FormControl<ImportModuleMediaJoinSeveral>('skip', {nonNullable: true});

  // --- values read as blank ---
  // Placeholders such as "Ikke angitt" that stand for an empty cell, per
  // column.
  blankValues: ImportModuleBlankValues = {};
  // Values the operator said are real, per column: the column profile's
  // suggestion to read one of them as blank is not offered again.
  blankValuesRejected: ImportModuleBlankValues = {};
  // `blankValueSuggestions` for one set of usages, values read as blank,
  // column profile, value review scan and set of rejected values.
  private suggestionsMemo: {
    usages: ReadonlyMap<string, ColumnUsage[]>;
    blankValues: ImportModuleBlankValues;
    profile: SourceColumnProfile | null;
    reviewed: ImportModuleConceptField[];
    rejected: ImportModuleBlankValues;
    suggestions: BlankValueSuggestion[];
  } | null = null;

  // --- lookups ---
  // Each set up once on the Kodeoppslag tab and named by key from any
  // mapping row in lookup mode. Sent as `lookups` with every scan,
  // preflight, run and profile save.
  lookupDefinitions: LookupDefinition[] = [];
  private nextLookupKey = 0;
  // Each checked lookup's last `/lookup_check` answer, by lookup key. One
  // request checks every complete lookup, `LOOKUP_CHECK_DELAY_MS` after
  // the last change to what the mapping is built from, unless its body is
  // the one last sent.
  lookupChecks: ReadonlyMap<string, ImportModuleLookupCheck> = new Map();
  lookupCheckError: string | null = null;
  // Only the latest check's answer applies; a new file or table
  // supersedes the one on its way. A failed body is sent again only on
  // `retryLookupCheck`.
  private readonly lookupCheckRequests = new DebouncedRequest(
    LOOKUP_CHECK_DELAY_MS, () => void this.checkLookups());
  // The lookups whose table lacks a column they read, for one mapping
  // version and set of loaded table columns.
  private lookupColumnProblemDerived: {
    version: number; tableColumns: ImportSessionStore['lookupColumns'];
    problems: Map<string, LookupColumnProblem>;
  } | null = null;
  // The column profile's suggested lookups the operator said do not apply,
  // by `lookupSuggestionKey`. For the session only.
  private dismissedLookupSuggestions = new Set<string>();
  // Numbers every load of a lookup table's columns; the number of each
  // table's latest load. A load's answer applies only while it is its
  // table's latest and the table is still marked loading - a reset of
  // the source clears that mark.
  private lookupColumnsSeq = 0;
  private readonly lookupColumnsLoads = new Map<string, number>();

  // --- Forhåndskontroll (pre-import check) ---
  preflightResult: ImportModulePreflightResult | null = null;
  // The latest result's `PreflightRefusal`, kept for that result.
  private preflightRefusal: PreflightRefusal | null = null;
  preflightError: string | null = null;
  runningPreflight = false;
  // Advanced by every clear of `preflightResult`; a preflight answer (or
  // error) is applied only if no clear happened while it was in flight,
  // so it cannot show a check of a mapping that is no longer on the page.
  private preflightRequestSeq = 0;
  // Numbers every Forhåndskontroll started; only the latest one's end
  // clears `runningPreflight`.
  private preflightRunSeq = 0;
  preflightSampleSizeControl = new FormControl<number>(20, {nonNullable: true});
  /** Check every row of the run range; offered while it holds at most
   * `PREFLIGHT_MAX_SAMPLE_SIZE` rows (`preflightRangeFits`). */
  preflightWholeFileControl = new FormControl<boolean>(false, {nonNullable: true});
  // The row range the shown `preflightResult` was run over.
  private preflightResultRange: RowRange | null = null;
  // The rows a run range of a table holds, as the last preflight that
  // could tell counted them. A mapping change leaves the count as it is;
  // an exact count in `tableStats` goes first.
  private rangeRowCount: {dmsIdentifier: string; table: string; range: RowRange; rows: number} | null = null;
  // The last `preflightResultRangeCount`, handed out again while the count
  // stays the same, so a binding to it sees no change.
  private preflightResultRangeCountShown: RowCount | null = null;
  static readonly PREFLIGHT_MAX_SAMPLE_SIZE = 500;

  // --- live preview ---
  // One row of the table as the object it becomes, from a preflight over
  // that row alone. Asked for `LIVE_PREVIEW_DELAY_MS` after the last
  // mapping change or row step while the Feltkoblinger tab is shown,
  // unless its body is the one last sent. Its answer has a slot of its
  // own: a clear of `preflightResult` leaves it, and it leaves
  // `preflightResult`.
  // The row asked for next, from 1.
  livePreviewRow = 1;
  // The last answer and the row it was asked for; `null` before one lands,
  // and again when there is nothing to preview or its request failed.
  livePreviewAnswer: {row: number; result: ImportModulePreflightResult} | null = null;
  livePreviewError: string | null = null;
  // Only the latest request's answer applies; a new file or table, or
  // nothing left to preview, supersedes the one on its way. A failed body
  // is sent again only on `retryLivePreview`.
  private readonly livePreviewRequests = new DebouncedRequest(
    LIVE_PREVIEW_DELAY_MS, () => void this.refreshLivePreview());
  // Asked for again while a request was on its way: one request at a time
  // is sent, and the latest body once it is back.
  private livePreviewQueued = false;
  // The name of each fixed Egennavn or Sted pick looked up so far, by the
  // picked record's id: the answer carries the id alone.
  private livePreviewPickNames: ReadonlyMap<string, string> = new Map();
  // The fixed picks' ids a lookup answered without a name, and the mapping
  // version they were answered at: not asked for again until it moves.
  private pickMisses: {version: number; ids: ReadonlySet<string>} = {version: -1, ids: new Set()};
  // The fixed picks' ids whose names are on their way.
  private readonly pickNamesInFlight = new Set<string>();
  private livePreviewMemo: {
    answer: ImportSessionStore['livePreviewAnswer'];
    sections: TargetSection<TargetListLine>[];
    version: number;
    pickNames: ReadonlyMap<string, string>;
    view: LivePreviewView | null;
  } | null = null;
  private nextWarningMemo: {result: ImportModulePreflightResult | null; row: number; next: number | null} | null = null;

  // --- reusable profiles ---
  // A profile restores EVERYTHING it stored, but every restored value
  // lands in an ordinary control the operator can still change - a
  // profile is a starting point, never a lock. Applied progressively:
  // object type / collection / media settings as soon as picked, the
  // mapping + group cards once a table's columns and the object type's
  // target fields have both loaded (see `applyProfile` /
  // `applyPendingWhenReady`).
  profiles: ImportModuleProfileSummary[] = [];
  selectedProfileId: string | null = null;
  applyingProfile = false;
  profileApplyError: string | null = null;
  // What a restored profile referenced that no longer resolves (a renamed
  // column, a retired batch key, a deleted concept) - reported, not
  // silently dropped. Cleared on every fresh apply.
  profileRestoreWarnings: string[] = [];
  // What the last restore reported, from which `profileRestoreWarnings`
  // is built again once `/batch_fields` names the batch fields and card
  // headers: the restore's own warnings, translated, and the ids blanked
  // from a profile imported from a file. `null` when no restore reported.
  private restoreReport: {warnings: string[]; carried?: Pick<CarriedProfile, 'blanked' | 'droppedDecisions'>} | null
    = null;
  // How many mapping entries and lookups a profile apply put on the page,
  // once its mapping lands.
  readonly profileApplied = new Subject<{ mappings: number; lookups: number }>();
  deletingProfileId: string | null = null;
  // The name of the profile last imported from a file while no saved
  // profile has been picked or saved since: Lagre som ny offers it.
  carriedProfileName: string | null = null;
  // Numbers the profile loads and imports. A new load or import, «Ingen»
  // and an object type picked each move it on (`cancelProfileLoad`); a
  // load or import checks it after every await, and stops once it has
  // moved on. `applyingProfile` belongs to the load it numbers.
  private profileLoadSeq = 0;
  savingProfile = false;
  saveProfileStatus: string | null = null;
  // The profile as last applied or saved, to tell whether a save would
  // change it; `null` while there is none. The session stash carries it.
  private profileBaseline: ProfileComparison | null = null;
  // Set while a restore waits for the first `/batch_fields` answer to a
  // request numbered above `afterSeq`: the restored batch values reach the
  // page only with that answer. A profile apply then takes its baseline
  // from the page; a resumed session keeps the stashed one and is compared
  // from then on.
  private pendingBaseline: {afterSeq: number; restored: boolean} | null = null;
  // The restore `applyPendingProfile` runs next comes from a saved
  // profile, not a resumed session.
  private pendingRestoreIsProfile = false;
  // The configuration differed from `profileBaseline` when last compared.
  private profileDiffers = false;
  // The late-apply payload for the currently-loading profile - consumed by
  // `applyPendingWhenReady` once the table's columns and the object type's
  // target fields exist to reconcile it against, whichever lands last.
  // Cleared once applied, or when the operator changes the object type by
  // hand (a genuine new choice, not a reset).
  private pendingProfileApply: PendingRestore | null = null;
  // Reentrancy guard for `resumeSession`'s async chain - bumped on entry,
  // re-checked after every await (mirrors `applyToken`'s pattern) so a
  // second resume/dismiss while one is still in flight can't have its
  // state clobbered by the superseded one landing late.
  private resumingToken = 0;

  // --- session recovery (independent of profiles) ---
  // An uploaded-but-not-yet-profiled source file surviving an accidental
  // reload — deliberately NOT a saved profile (source identity is what a
  // profile excludes on purpose). `session` carries everything else the
  // operator had configured, restored automatically on resume.
  resumeCandidate: SessionStash | null = null;
  // The resume check's `/source/inspect` answer, taken by the next
  // `inspectSource` of that same file instead of inspecting it again.
  private resumeInspect: {dmsIdentifier: string; fileName: string; result: SourceInspectResult} | null = null;
  resumingSession = false;

  // --- value review ---
  // The whole-file scan's fields; `<app-value-review-page>` edits their
  // rows' decisions in place.
  conceptFields: ImportModuleConceptField[] = [];
  loadingConceptValues = false;
  conceptRowsScanned = 0;
  // The JSON of the `buildConceptScanRequest()` that `conceptFields` answers,
  // `null` before any scan.
  private conceptScanRequestKey: string | null = null;
  // The mapping changed since the scan, so its values may be out of date.
  conceptScanStale = false;
  // The loaded profile's decisions, or the last save's. The review rows
  // exist only after a scan, so a save sends these while no scan holds
  // rows of its own (`decisionsToSave`).
  private profileDecisions: ImportModuleDecisionRow[] = [];
  // The decisions of a profile imported from a file, as the import kept
  // them, while it is neither saved nor replaced: no stored profile holds
  // them, so a scan is sent them in place of a profile id for the backend
  // to seed its rows from. `null` otherwise.
  private carriedDecisions: ImportModuleStoredDecision[] | null = null;
  valueReviewSummary: ValueReviewSummary = NO_VALUE_REVIEW;

  // --- steps ---
  // The step view shown; the session stash carries it.
  private shownView: ImportView = 'kilde';
  // A step view the page is asked to show, as a resumed session asks for
  // the one it was stashed on.
  readonly viewRequested = new Subject<ImportView>();

  // --- run ---
  readonly runJob = new RunJob({
    cms: this.cms,
    jobStatusSubscriber: this.jobStatusSubscriber,
    isActiveService: this.isActiveService,
    logger: this.logger,
    errorText: e => this.errorText(e, 'TRANS__IMPORT__ERROR__RUN'),
    // The job now owns this file's import — nothing left worth resuming
    // from an accidental reload.
    onQueued: () => this.clearSessionStash(),
  });
  // Set by `ngOnDestroy`: a lookup table's columns arriving later schedule
  // no check.
  private destroyed = false;

  /** Loads the object types and the profiles, offers a stashed session,
   * and follows the controls every request body reads. */
  init(): void {
    this.objectTypesLoaded = this.cms.importModuleObjectTypes().then(
      res => {
        this.objectTypes = res.object_types;
        this.groupKindSpecs = res.group_kinds;
        this.adminEventKinds = res.admin_event_kinds ?? [];
        this.imageSuperobjectTypeId = res.image_superobject_type_id ?? null;
        // The flat kinds' descriptors and hidden batch keys come from these
        // specs; a descriptor built before they arrived has neither.
        this.rebuildGroupDescriptors();
      },
      err => this.logger.error(`raw import: could not load object types: ${err}`)
    );
    this.loadProfiles();
    this.checkForResumableSession();
    this.objectTypeControl.valueChanges.subscribe(objectType => {
      // A genuine operator pick, not a profile/resumed session applying its
      // own object type (that path uses {emitEvent: false}) - any pending
      // late-apply from an earlier profile or resume no longer applies.
      this.pendingProfileApply = null;
      this.pendingRestoreIsProfile = false;
      this.cancelProfileLoad();
      this.selectedProfileId = null;
      this.profileDecisions = [];
      this.carriedDecisions = null;
      this.onObjectTypeChange(objectType);
    });
    this.mappingChanged.pipe(debounceTime(400)).subscribe(() => {
      this.loadBatchFields();
      this.refreshConceptScanStale();
      this.stashSourceToSession();
    });
    // Any mapping / media change makes a shown Forhåndskontroll stale.
    this.mappingChanged.subscribe(() => {
      this.mappingVersion++;
      this.clearPreflightResult();
    });
    // The lookups, the blank values and the label columns the rows pick
    // are all read into the check's body.
    this.mappingChanged.subscribe(() => this.scheduleLookupCheck());
    this.mappingChanged.subscribe(() => this.scheduleLivePreview());
    // The join column decides whether the rows onto the linked media's
    // fields are sent, so with such rows it moves the mapping; without
    // any, it changes only what the add search offers.
    this.mediaJoinColumnControl.valueChanges.subscribe(column => {
      if (column) {
        this.keptMedia = null;
      }
      if (this.mediaAssignments.length) {
        this.mappingChanged.next();
      } else {
        this.mappingVersion++;
        this.clearPreflightResult();
      }
      this.stashSourceToSession();
    });
    this.mediaFolderControl.valueChanges.subscribe(() => this.clearPreflightResult());
    this.mediaMatchPresetControl.valueChanges.subscribe(preset => {
      const pattern = mediaMatchPresetPattern(preset);
      if (pattern !== null) {
        this.mediaMatchPatternControl.setValue(pattern);
      }
    });
    // The pattern rule starts from the default preset while it has no
    // pattern.
    this.mediaMatchModeControl.valueChanges.subscribe(mode => {
      if (mode === 'pattern' && !this.mediaMatchPatternControl.value.trim()) {
        this.mediaMatchPresetControl.setValue(DEFAULT_MEDIA_MATCH_PRESET);
      }
    });
    for (const control of this.mediaJoinOptionControls) {
      control.valueChanges.subscribe(() => {
        this.clearPreflightResult();
        this.stashSourceToSession();
      });
    }
    this.mediaOnMissingControl.valueChanges.subscribe(() => this.stashSourceToSession());
    this.overwriteExistingControl.valueChanges.subscribe(() => this.stashSourceToSession());
    this.publishControl.valueChanges.subscribe(() => {
      this.clearPreflightResult();
      this.stashSourceToSession();
    });
    this.linkMediaControl.valueChanges.subscribe(on => {
      this.clearPreflightResult();
      if (on) {
        this.loadMediaFolders();
      } else {
        this.keptMedia = null;
        this.mediaJoinColumnControl.reset();
        this.mediaFolderControl.reset();
        this.mediaFolders = [];  // re-fetch next time (a new upload batch may have a new folder)
        this.mediaOnMissingControl.setValue('block');
        this.resetMediaJoinOptions(true);
      }
      this.stashSourceToSession();
    });
  }

  private async loadMediaFolders(): Promise<void> {
    if (this.mediaFolders.length) {
      return;
    }
    try {
      this.mediaFolders = (await this.cms.importModuleMediaFolders()).media_folders;
      // Default to the newest folder (folders are newest-first) — a
      // just-uploaded batch is almost always what you're importing. The
      // operator can switch to "whole database" or another folder.
      if (!this.mediaFolderControl.value && this.mediaFolders.length) {
        this.mediaFolderControl.setValue(this.mediaFolders[0].id);
      }
    } catch (e) {
      this.logger.error(`raw import: could not load media folders: ${e}`);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.flushReviewStash();
    this.batchDefaultsSubscription?.unsubscribe();
    this.rowSubscriptions.unsubscribe();
    this.lookupSubscriptions.unsubscribe();
    this.clearBatchFields();
    this.mappingChanged.complete();
    this.viewRequested.complete();
    this.runJob.dispose();
    this.lookupCheckRequests.cancel();
    this.livePreviewRequests.cancel();
    this.supersedeInspect();
  }

  /** The step view shown. Showing the Feltkoblinger tab brings the live
   * preview up to date. */
  get view(): ImportView {
    return this.shownView;
  }

  set view(view: ImportView) {
    this.shownView = view;
    if (view === 'feltkoblinger') {
      this.scheduleLivePreview();
    }
  }

  get selectedObjectType(): ImportModuleObjectType | undefined {
    return this.objectTypes.find(t => t.object_type === this.objectTypeControl.value);
  }

  /** The mapping list, as the page would send it now (see `buildMapping`). */
  get mappings(): MappingEntry[] {
    return buildMapping(this.mappingState);
  }

  /** Whether the mapping list has an entry. Stops at the first one. */
  get hasMappings(): boolean {
    return hasMappingEntry(this.mappingState);
  }

  /** What the mapping list is built from. */
  private get mappingState(): MappingState {
    return {
      assignments: this.assignments,
      mediaAssignments: this.mediaAssignments,
      groups: {kinds: this.groupKinds, models: this.groupModels, descriptors: this.groupDescriptors},
      columnNames: this.columnNames,
      lookupDefinitions: this.lookupDefinitions,
    };
  }

  private get sourceFile(): SourceFile {
    return {dmsIdentifier: this.dmsIdentifier, fileName: this.fileName, table: this.selectedTable};
  }

  /** The page state every body that carries the configuration reads. */
  private configurationState(objectType: ImportModuleObjectType): ConfigurationState {
    return {
      ...this.mappingState,
      source: this.sourceFile,
      objectType: objectType.superobject_type_id,
      batch: this.batchValues,
      blankValues: this.blankValues,
      publish: this.publishControl.value,
      media: this.mediaJoin,
    };
  }

  /** The batch defaults' collection, registration level and object status,
   * synced from the form first; `null` while the batch-defaults model does
   * not exist. */
  private readBatchDefaults(): BatchDefaultValues | null {
    if (!this.batchDefaults) {
      return null;
    }
    this.formGroupService.setObjectValuesFromForm(
      this.batchDefaults.rootObject, this.batchDefaults.formGroup);
    const root = this.batchDefaults.rootObject as any;
    return {
      collectionId: root?.collection?.collection_id,
      collectionLabel: root?.collection?.collection_id_value,
      regLevelId: root?.reg_level?.registration_level_id,
      regLevelLabel: root?.reg_level?.registration_level_id_value,
      statusId: root?.object_status?.object_status_type_id,
      statusLabel: root?.object_status?.object_status_type_id_value,
    };
  }

  /** The operator's full current configuration, in the same shape a saved
   * profile uses, for the session-resume stash - `null` before the
   * batch-defaults model exists (no object type picked yet, or its model
   * is still building), since there is nothing stable to capture yet. */
  buildSessionSnapshot(): SessionSnapshot | null {
    const objectType = this.selectedObjectType;
    if (!objectType || !this.batchDefaults) {
      return null;
    }
    return sessionSnapshot({
      ...this.configurationState(objectType),
      keptMedia: this.keptMedia,
      blankValuesRejected: this.blankValuesRejected,
      batchDefaults: this.readBatchDefaults(),
      overwriteExisting: this.overwriteExistingControl.value,
      valueReview: this.conceptScanRequestKey === null ? undefined : {
        fields: this.conceptFields, rowsScanned: this.conceptRowsScanned,
      },
      profileId: this.selectedProfileId,
      profileDecisions: this.profileDecisions,
      carriedDecisions: this.carriedDecisions,
      view: this.view,
      profileBaseline: this.profileBaseline,
    });
  }

  /** The tables a lookup can read — every discovered table but the data
   * one. The same array until `tables` or `selectedTable` changes: a
   * component takes it as an input, which needs a stable reference. */
  get availableLookupTables(): string[] {
    const memo = this.lookupTablesMemo;
    if (memo?.tables !== this.tables || memo.selectedTable !== this.selectedTable) {
      this.lookupTablesMemo = {
        tables: this.tables,
        selectedTable: this.selectedTable,
        available: this.tables.filter(table => table !== this.selectedTable),
      };
    }
    return this.lookupTablesMemo!.available;
  }

  /** The lookups a `kind` card's member rows are offered. None for an
   * admin event or a placement: retrying a failed admin-event write
   * recomposes its values from the saved row alone, without the lookup
   * tables, so a looked-up value would come back blank there. */
  groupLookups(kind: GroupKind): LookupDefinition[] {
    return EVENT_GROUP_KINDS[kind] === 'admin' ? NO_LOOKUPS : this.lookupDefinitions;
  }

  /** Whether Run is enabled: the mapping is ready (`mappingReady`), the
   * shown Forhåndskontroll found nothing the run refuses
   * (`preflightAllowsRun`), and neither the media join's matches
   * (`mediaBlocksRun`) nor its options (`mediaJoinProblem`) block the run. */
  get canRun(): boolean {
    return this.mappingReady && this.preflightAllowsRun && !this.mediaBlocksRun && !this.mediaJoinProblem;
  }

  /** Whether the mapping is complete and nothing it depends on loads or
   * runs; not while a new file uploads to replace the selected table's
   * file. The loading flags are read on every call; the mapping half is
   * built once per `mappingVersion`. */
  get mappingReady(): boolean {
    return !!this.selectedTable && !this.runBusy && this.mappingIsComplete;
  }

  /** Whether a Forhåndskontroll result is shown and holds no error the run
   * refuses (`preflightRefusesRun`). */
  get preflightAllowsRun(): boolean {
    return !!this.preflightResult && !this.preflightRefusesRun(this.preflightResult);
  }

  /** Why Run is disabled, as a `TRANS__` key: the mapping is not complete,
   * the media join's options cannot be checked (`mediaJoinProblem`), no
   * Forhåndskontroll is shown, or it found an error the run refuses.
   * `null` while Run is enabled, while something loads, runs or is
   * checked, and when only the media join's matches block the run, which
   * the Forhåndskontroll result explains. */
  get runBlockedReason(): string | null {
    if (!this.selectedTable || this.runBusy) {
      return null;
    }
    if (!this.mappingIsComplete) {
      return 'TRANS__IMPORT__RUN_BLOCKED__MAPPING_INCOMPLETE';
    }
    const mediaJoinProblem = this.mediaJoinProblem;
    if (mediaJoinProblem) {
      return mediaJoinProblem;
    }
    if (!this.preflightResult) {
      return this.runningPreflight ? null : 'TRANS__IMPORT__RUN_BLOCKED__NEEDS_PREFLIGHT';
    }
    return this.preflightRefusesRun(this.preflightResult) ? 'TRANS__IMPORT__RUN_BLOCKED__PREFLIGHT_ERRORS' : null;
  }

  /** A file upload, a column or batch-field load, or a run in progress. */
  private get runBusy(): boolean {
    return this.uploading || this.loadingColumns || this.runJob.running || this.loadingBatchFields;
  }

  /** `mappingComplete`, once per `mappingVersion`. */
  private get mappingIsComplete(): boolean {
    const derived = this.derived();
    derived.mappingComplete ??= this.mappingComplete();
    return derived.mappingComplete;
  }

  /** Whether `result` holds a block-level error `/run` refuses as well. */
  private preflightRefusesRun(result: ImportModulePreflightResult): boolean {
    return this.preflightRefusalOf(result).refuses;
  }

  /** `result`'s block-level errors split by whether `/run` refuses them (an arrow, for a child component). */
  preflightErrorSplit = (result: ImportModulePreflightResult): PreflightErrorSplit =>
    this.preflightRefusalOf(result).split;

  /** `preflightRefusal` for `result`, built when it holds another. */
  private preflightRefusalOf(result: ImportModulePreflightResult): PreflightRefusal {
    if (this.preflightRefusal?.result !== result) {
      const split = this.splitPreflightErrors(result.errors);
      const refuses = Object.values(split.refusing).some(errors => Array.isArray(errors) ? errors.length : errors);
      this.preflightRefusal = {result, split, refuses};
    }
    return this.preflightRefusal;
  }

  /**
   * `errors` split by whether `/run` refuses them as well. It refuses any
   * `groups`, `incomplete_cards`, `media`, `media_pattern`, `value_decisions` or `rights` error, and a `target_fields`
   * error other than a field id the mapping or the media mapping sends,
   * which is a field the run leaves out rather than refuses. It refuses no `lookups` error:
   * most are a lookup table that cannot be read or a failed batched
   * Place, Location, KulturNav or identification-number lookup, which may
   * be transient and which `/run` does not refuse; the one `/run` does
   * refuse, a malformed lookup definition, the page never sends, as it
   * sends only complete lookups.
   */
  private splitPreflightErrors(errors: ImportModulePreflightResult['errors']): PreflightErrorSplit {
    const sentFields = errors.target_fields.length
      ? new Set([...this.mappings, ...buildMediaMapping(this.mappingState, this.mediaJoin)]
        .map(entry => entry.target_field))
      : new Set<string>();
    return {
      refusing: {
        target_fields: errors.target_fields.filter(field => !sentFields.has(field)),
        lookups: [], groups: errors.groups, incomplete_cards: errors.incomplete_cards ?? [], media: errors.media,
        media_pattern: errors.media_pattern ?? null, value_decisions: errors.value_decisions ?? [], rights: errors.rights ?? [],
      } satisfies Required<ImportModulePreflightResult['errors']>,
      notRefusing: {
        target_fields: errors.target_fields.filter(field => sentFields.has(field)),
        lookups: errors.lookups, groups: [], incomplete_cards: [], media: [], media_pattern: null, value_decisions: [], rights: [],
      } satisfies Required<ImportModulePreflightResult['errors']>,
    };
  }

  /** A mapping entry exists, every required batch field is picked, and
   * no group card is incomplete. Builds the mapping list once. */
  private mappingComplete(): boolean {
    const mapping = this.mappings;
    if (!mapping.length) {
      return false;
    }
    return !this.requiredBatchPickMissing && this.incompleteCards(mapping).size === 0;
  }

  /** The group cards that hold the run back: one missing a required header
   * pick (`groupHeaderComplete`) or sending no value onto a required
   * member (`missingMembersByCard`), and a history-event card with a
   * mapped sub-block missing its role or type (`subBlockComplete`). Built
   * once per `mappingVersion`, from `mapping` when the caller already
   * built it. */
  private incompleteCards(mapping?: readonly MappingEntry[]): ReadonlySet<GroupCardModel> {
    const derived = this.derived();
    if (!derived.incompleteCards) {
      const entries = mapping ?? this.mappings;
      const usedIds = usedGroupIds(entries);
      const usedSubBlocks = usedSubBlockKeys(entries);
      const missingMembers = this.missingMembersByCard(entries);
      const incomplete = new Set<GroupCardModel>();
      for (const cfg of this.groupKindConfigs) {
        for (const model of this.groupModels[cfg.kind]) {
          if (!this.groupHeaderComplete(cfg, model, usedIds) || missingMembers.has(model)
              || !model.subBlocks.every(sub => this.subBlockComplete(sub, usedSubBlocks))) {
            incomplete.add(model);
          }
        }
      }
      derived.incompleteCards = incomplete;
    }
    return derived.incompleteCards;
  }

  /** The required members `card` sends no value onto, while that holds
   * the run back. */
  missingRequiredMembers(card: GroupCardModel): readonly GroupMemberFieldDef[] {
    return this.missingMembersByCard().get(card) ?? NO_MISSING_MEMBERS;
  }

  /** Each card with a required member (`GroupMemberFieldDef.required`)
   * that no entry of `mapping` sends a value onto, with those members.
   * With `requiresMappedMember`, only a group in `usedIds` counts, as for
   * its header picks. Built once per `mappingVersion`, from `mapping`
   * when the caller already built it. */
  private missingMembersByCard(
    mapping?: readonly MappingEntry[],
  ): ReadonlyMap<GroupCardModel, readonly GroupMemberFieldDef[]> {
    const derived = this.derived();
    if (!derived.missingMembers) {
      const entries = mapping ?? this.mappings;
      const usedIds = usedGroupIds(entries);
      const sent = new Set(entries.map(
        entry => memberKey(entry.group_id ?? entry.admin_event_group, entry.target_field)));
      const missing = new Map<GroupCardModel, readonly GroupMemberFieldDef[]>();
      for (const cfg of this.groupKindConfigs) {
        const required = this.groupDescriptors[cfg.kind].members.filter(def => def.required);
        for (const model of required.length ? this.groupModels[cfg.kind] : []) {
          if (cfg.requiresMappedMember && !usedIds.has(model.key)) {
            continue;
          }
          const lacking = required.filter(def => !sent.has(memberKey(model.key, def.fieldId)));
          if (lacking.length) {
            missing.set(model, lacking);
          }
        }
      }
      derived.missingMembers = missing;
    }
    return derived.missingMembers;
  }

  /** Whether `line` shows the incomplete marker: a group card the run is
   * held back by (`incompleteCards`) or with a member row that has an
   * issue (`memberIssueCards`), or a flat row with an issue
   * (`lineIssue`). */
  isLineIncomplete(line: MappingLine): boolean {
    return 'targetFieldControl' in line
      ? this.lineIssue(line) !== null
      : this.incompleteCards().has(line) || this.memberIssueCards().has(line);
  }

  /** The group cards with a member row on a Kodeoppslag or a Sammensatt
   * that sends nothing (`valueSourceIssue`, over the lookups the card is
   * offered). They do not hold the run back. Built once per
   * `mappingVersion`. */
  private memberIssueCards(): ReadonlySet<GroupCardModel> {
    const derived = this.derived();
    if (!derived.memberIssueCards) {
      const cards = new Set<GroupCardModel>();
      for (const cfg of this.groupKindConfigs) {
        const lookups = this.groupLookups(cfg.kind);
        for (const model of this.groupModels[cfg.kind]) {
          if (groupModelMemberRows(model).some(row => valueSourceIssue(row, memberValueSourceKind(row), lookups))) {
            cards.add(model);
          }
        }
      }
      derived.memberIssueCards = cards;
    }
    return derived.memberIssueCards;
  }

  /** `mappingDerived`, emptied first when the mapping moved on since it
   * was filled. */
  private derived(): MappingDerived {
    if (this.mappingDerived.version !== this.mappingVersion) {
      this.mappingDerived = {version: this.mappingVersion, rowPreviews: new Map()};
    }
    return this.mappingDerived;
  }

  /** Batch fields rendered by the generic batch-settings card - each kind's
   * `hiddenBatchKeys` (its own card header fields) are hidden once ≥1 of
   * its groups exists (rendered per-group in the card header instead).
   * The same array until `mappingVersion` moves. */
  get genericBatchFields(): BatchField[] {
    const derived = this.derived();
    if (!derived.genericBatchFields) {
      let fields = this.batchFields;
      for (const cfg of this.groupKindConfigs) {
        if (this.groupModels[cfg.kind].length) {
          fields = fields.filter(f => !cfg.hiddenBatchKeys().includes(f.key));
        }
      }
      derived.genericBatchFields = fields;
    }
    return derived.genericBatchFields;
  }

  /** Whether `kind`'s descriptor has a member or a sub-block to map. */
  hasGroupTargets(kind: GroupKind): boolean {
    const descriptor = this.groupDescriptors[kind];
    return descriptor.members.length > 0 || descriptor.subBlocks.length > 0;
  }

  /** The flat rows and group cards under the object form's section
   * headings: a card where `/target_fields` places its kind, a row where
   * it places the row's target field, and a line it places nowhere under
   * the other fields. A row is placed by its `placedField`. The rows onto
   * a field of the linked media come last, under their own heading, while
   * the media join has a column. Built once per `mappingVersion` and
   * `placementVersion`. */
  get targetSections(): TargetSection<TargetListLine>[] {
    const memo = this.targetSectionsMemo;
    if (memo?.mappingVersion === this.mappingVersion && memo.placementVersion === this.placementVersion) {
      return memo.sections;
    }
    const placed: PlacedLine<TargetListLine>[] = [];
    for (const cfg of this.groupKindConfigs) {
      const placement = this.cardKindPlacements.get(cfg.cardObjectType) ?? {};
      this.groupModels[cfg.kind].forEach((card, index) =>
        placed.push({line: {row: null, card, cfg, index}, placement}));
    }
    for (const row of this.assignments) {
      placed.push({
        line: {row, card: null, cfg: null, index: 0},
        placement: row.placedField && fieldPlacement(row.placedField, this.specialTargetPlacements),
      });
    }
    for (const row of this.mediaJoinColumn ? this.mediaAssignments : []) {
      placed.push({line: {row, card: null, cfg: null, index: 0}, placement: MEDIA_FIELDS_PLACEMENT});
    }
    const sections = groupBySection(placed, this.otherFieldsPlacement);
    this.targetSectionsMemo = {
      mappingVersion: this.mappingVersion, placementVersion: this.placementVersion, sections,
    };
    return sections;
  }

  /** Whether a required batch pick under "Gjelder alle radene" is still
   * empty - one of the picks `canRun` waits for. */
  get requiredBatchPickMissing(): boolean {
    return this.genericBatchFields.some(f => f.required && !this.batchControls[f.key]?.value);
  }

  /** How many rows and cards the mapping list holds. */
  get lineCount(): number {
    return this.targetSections.reduce((count, section) => count + section.lines.length, 0);
  }

  /** The add search's offer, placed as `targetSections` places the cards
   * and rows it adds: the fields of the linked media only while the media
   * join has a column. Built once per `mappingVersion`. */
  get addSearchOptions(): AddSearchOptions {
    const derived = this.derived();
    if (!derived.addSearchOptions) {
      const placed: PlacedLine<AddSearchOption>[] = [];
      for (const cfg of this.groupKindConfigs) {
        if (this.hasGroupTargets(cfg.kind)) {
          placed.push({
            line: {cfg, createsMissing: !!this.groupDescriptors[cfg.kind].supportsCreateMissing},
            placement: this.cardKindPlacements.get(cfg.cardObjectType) ?? {},
          });
        }
      }
      for (const field of this.targetOptions) {
        placed.push({line: {field, media: false}, placement: fieldPlacement(field, this.specialTargetPlacements)});
      }
      for (const field of this.mediaJoinColumn ? this.mediaTargetFields : []) {
        placed.push({line: {field, media: true}, placement: MEDIA_FIELDS_PLACEMENT});
      }
      derived.addSearchOptions = groupBySection(placed, this.otherFieldsPlacement);
    }
    return derived.addSearchOptions;
  }

  /** Whether `model` has every required header field of its descriptor
   * picked, or does not count. With `requiresMappedMember`, only a group
   * in `usedIds` - one a mapping entry routes a value to - counts;
   * otherwise every group does, since a header-only group is a complete
   * configuration there. */
  private groupHeaderComplete(
    cfg: GroupKindConfig, model: GroupCardModel, usedIds: ReadonlySet<string>,
  ): boolean {
    if (cfg.requiresMappedMember && !usedIds.has(model.key)) {
      return true;
    }
    return this.groupDescriptors[cfg.kind].header
      .filter(h => h.required)
      .every(h => !!model.headerControls[h.key]?.value);
  }

  /** Whether a Named/Place sub-block has a role picked, and a Named one of
   * a NamedObject its type (`subBlockPicked`), or does not count: only
   * one in `usedSubBlocks` - one a mapping entry routes a value to -
   * does. `role_id` is `is_required=True` on both
   * `SuperobjectHistoryEventItemNamed` and
   * `SuperobjectHistoryEventItemPlace`, so a role-less entry would be one
   * the rest of Primus treats as incomplete. */
  private subBlockComplete(sub: GroupSubBlockRow, usedSubBlocks: ReadonlySet<string>): boolean {
    return !usedSubBlocks.has(sub.key) || subBlockPicked(sub);
  }

  private configFor(kind: GroupKind): GroupKindConfig {
    return this.groupKindConfigs.find(cfg => cfg.kind === kind)!;
  }

  addGroupOfKind(kind: GroupKind): void {
    const descriptor = this.groupDescriptors[kind];
    const model = this.addGroup(
      this.groupModels[kind], descriptor.header, !!descriptor.withOnlyWhenMappedToggle,
      !!descriptor.supportsCreateMissing);
    this.openLine(model);
    if (descriptor.supportsCreateMissing) {
      void this.ensureCreateMissingParents(kind);
    }
  }

  /** Shows `kind`'s "place new terms under" options - every term of the
   * concept list its concept member resolves against - for the current
   * object type, asked of the backend once per kind and object type. */
  private async ensureCreateMissingParents(kind: GroupKind): Promise<void> {
    const objectType = this.selectedObjectType;
    const groupParent = this.configFor(kind).cardObjectType;
    const conceptField = this.allTargetFields.find(
      f => f.group_parent === groupParent && f.group_concept);
    if (!objectType || !conceptField) {
      return;
    }
    const key = JSON.stringify([kind, objectType.superobject_type_id]);
    const request = sharedRequest(this.createMissingParentRequests, key, () => this.cms.importModuleConceptTerms({
      field_id: conceptField.field_id, object_type: objectType.superobject_type_id,
    }).then(res => res.terms).catch(e => {
      this.logger.error(`raw import: could not load the ${kind} terms: ${e}`);
      throw e;
    }));
    let terms: ConceptOption[];
    try {
      terms = await request;
    } catch {
      return;  // logged once for the request, above
    }
    if (this.selectedObjectType !== objectType || this.createMissingParentOptions[kind] === terms) {
      return;  // another object type picked while this was in flight, or already shown
    }
    this.createMissingParentOptions[kind] = terms;
    this.rebuildGroupDescriptors();
  }

  removeGroupOfKind(kind: GroupKind, model: GroupCardModel): void {
    this.groupModels[kind] = this.groupModels[kind].filter(m => m !== model);
    this.closeLine(model);
    this.mappingChanged.next();
  }

  private addGroup(
    models: GroupCardModel[], header: GroupHeaderFieldDef[], withOnlyWhenMappedToggle = false,
    withCreateMissing = false,
  ): GroupCardModel {
    const model = createGroupCardModel(
      `g${this.nextGroupKey++}`, header, withOnlyWhenMappedToggle, withCreateMissing);
    this.registerGroupModel(model);
    models.push(model);
    this.mappingChanged.next();
    return model;
  }

  /** Add a member field row to a group card and wire its controls. */
  addGroupMember(model: GroupCardModel, fieldId: string): void {
    const row = createGroupMemberRow(fieldId);
    this.registerGroupControls(memberRowControls(row));
    this.watchLookupRef(row);
    model.members.push(row);
    this.mappingChanged.next();
  }

  removeGroupMember(model: GroupCardModel, row: GroupMemberRow): void {
    model.members = model.members.filter(r => r !== row);
    this.mappingChanged.next();
  }

  /** Add a repeatable sub-block (a Named party or Place, each 1:M on the
   * event) to a history-event card and wire its controls. The sub-block key
   * (`'<group>.<n>'`) stays stable - `nextSubBlockKey` only increments. */
  addGroupSubBlock(model: GroupCardModel, def: GroupSubBlockDef): void {
    const row = createGroupSubBlockRow(`${model.key}.n${this.nextSubBlockKey++}`, def);
    this.registerGroupControls(subBlockRowControls(row));
    this.watchLookupRef(row.member);
    model.subBlocks.push(row);
    this.mappingChanged.next();
  }

  removeGroupSubBlock(model: GroupCardModel, row: GroupSubBlockRow): void {
    model.subBlocks = model.subBlocks.filter(r => r !== row);
    this.mappingChanged.next();
  }

  private registerGroupControls(controls: FormControl[]): void {
    for (const control of controls) {
      this.rowSubscriptions.add(control.valueChanges.subscribe(() => this.mappingChanged.next()));
    }
  }

  /** Wire every control of a group model, its member rows' included. */
  private registerGroupModel(model: GroupCardModel): void {
    this.registerGroupControls(groupModelControls(model));
    for (const row of groupModelMemberRows(model)) {
      this.watchLookupRef(row);
    }
  }

  /** Reset a lookup row's value column when it picks another lookup - the
   * column belongs to the previous lookup's table. */
  private watchLookupRef(controls: LookupControls): void {
    this.rowSubscriptions.add(controls.lookupRefControl.valueChanges.subscribe(() => {
      controls.lookupLabelControl.setValue(null);
    }));
  }

  /** Every mapping row that can be in lookup mode - flat rows, the rows
   * onto the linked media and every group card's member rows, sub-block
   * members included. */
  private get lookupRows(): LookupControls[] {
    return [
      ...this.assignments, ...this.mediaAssignments,
      ...Object.values(this.groupModels).flat().flatMap(groupModelMemberRows),
    ];
  }

  /** Add an empty lookup to the Kodeoppslag tab. */
  addLookup(): LookupDefinition {
    const lookup = createLookupDefinition(`l${this.nextLookupKey++}`);
    this.lookupSubscriptions.add(lookup.tableControl.valueChanges.subscribe(table => {
      // The key column and every row's value column belong to the
      // previous table.
      lookup.keyColumnControl.setValue(null);
      for (const row of this.lookupRows) {
        if (row.lookupRefControl.value === lookup.key) {
          row.lookupLabelControl.setValue(null);
        }
      }
      if (table) {
        this.loadLookupColumns(table);
      }
    }));
    for (const control of lookupDefinitionControls(lookup)) {
      this.lookupSubscriptions.add(control.valueChanges.subscribe(() => this.mappingChanged.next()));
    }
    this.lookupDefinitions = [...this.lookupDefinitions, lookup];
    this.mappingChanged.next();
    return lookup;
  }

  /** Drop a lookup. A row that took its value through it keeps lookup
   * mode with nothing picked, so the gap shows rather than a silently
   * different value. */
  removeLookup(lookup: LookupDefinition): void {
    this.lookupDefinitions = this.lookupDefinitions.filter(other => other !== lookup);
    for (const row of this.lookupRows) {
      if (row.lookupRefControl.value === lookup.key) {
        row.lookupRefControl.setValue(null);
      }
    }
    this.mappingChanged.next();
  }

  /** Whether a lookup check is waiting to be sent or on its way: the
   * answers shown may be out of date. */
  get lookupCheckPending(): boolean {
    return this.lookupCheckRequests.pending;
  }

  /** Whether a lookup check is on its way. */
  get lookupChecking(): boolean {
    return this.lookupCheckRequests.inFlight;
  }

  /** How many codes the lookups' last checks found no table row for. */
  get lookupMissedCodes(): number {
    return this.lookupDefinitions.reduce(
      (sum, lookup) => sum + (this.lookupChecks.get(lookup.key)?.missed_code_count ?? 0), 0);
  }

  /** (Re)starts the wait before the lookups are checked, unless none is
   * running and the check would send the body it sent last - as after a
   * change to a lookup's name alone, which the body leaves out. */
  private scheduleLookupCheck(): void {
    this.lookupCheckRequests.schedule(() => {
      const request = this.nextLookupCheckRequest();
      return request ? JSON.stringify(request) : null;
    });
  }

  /** Checks every complete lookup over the whole table in one
   * `POST /lookup_check`, each with the label columns the mapping and the
   * media mapping read through it, unless the body is the one last sent.
   * Clears the answers while there is nothing to check.
   *
   * One lookup reading a column its table lacks fails the whole request,
   * so such a lookup is left out (see `lookupColumnProblems`), and
   * nothing is sent while a lookup's table columns are still loading:
   * their arrival checks again. */
  async checkLookups(): Promise<void> {
    const request = this.nextLookupCheckRequest();
    if (request === undefined) {
      return;
    }
    const seq = this.lookupCheckRequests.begin(request && JSON.stringify(request));
    if (seq === null) {
      return;
    }
    this.lookupCheckError = null;
    if (!request) {
      this.lookupChecks = new Map();
      return;
    }
    try {
      const answer = await this.cms.importModuleLookupCheck(request);
      if (!this.lookupCheckRequests.isLatest(seq)) {
        return;  // a newer check, or a new file or table
      }
      this.lookupChecks = new Map(Object.entries(answer));
    } catch (e: any) {
      if (!this.lookupCheckRequests.isLatest(seq)) {
        return;
      }
      this.logger.error(`raw import: lookup check failed: ${e}`);
      this.lookupCheckError = this.sourceErrorText(e, 'TRANS__IMPORT__ERROR__LOOKUP_CHECK');
    } finally {
      this.lookupCheckRequests.finish(seq);
    }
  }

  /** The `/lookup_check` body for the lookups as they are; `null` while
   * there is nothing to check, `undefined` while a complete lookup's table
   * columns are still loading. */
  private nextLookupCheckRequest(): ReturnType<typeof lookupCheckRequest> | undefined {
    const loading = this.lookupDefinitions.some(lookup =>
      isLookupComplete(lookup) && this.lookupColumnsLoading.has(lookup.tableControl.value!));
    if (loading) {
      return undefined;
    }
    const problems = this.lookupColumnProblems;
    return lookupCheckRequest(
      this.sourceFile, this.lookupDefinitions.filter(lookup => !problems.has(lookup.key)), this.columnNames,
      this.blankValues, this.lookupLabelColumns);
  }

  /** The columns of each lookup's table the mapping and the media mapping
   * read through it (see `lookupLabelColumns`). */
  private get lookupLabelColumns(): Map<string, string[]> {
    return lookupLabelColumns([...this.mappings, ...buildMediaMapping(this.mappingState, this.mediaJoin)]);
  }

  /** Each complete lookup whose loaded table columns lack its key column
   * or a label column the mapping or the media mapping reads through it,
   * by lookup key: the first such column. A lookup whose table columns
   * have not loaded is not judged. */
  get lookupColumnProblems(): ReadonlyMap<string, LookupColumnProblem> {
    const cached = this.lookupColumnProblemDerived;
    if (cached?.version === this.mappingVersion && cached.tableColumns === this.lookupColumns) {
      return cached.problems;
    }
    const problems = new Map<string, LookupColumnProblem>();
    const labelColumns = this.lookupLabelColumns;
    for (const lookup of this.lookupDefinitions.filter(isLookupComplete)) {
      const table = lookup.tableControl.value!;
      const columns = this.lookupColumns[table];
      if (!columns) {
        continue;
      }
      const names = new Set(columns.map(column => column.name));
      const missing = [lookup.keyColumnControl.value!, ...(labelColumns.get(lookup.key) ?? [])]
        .find(column => !names.has(column));
      if (missing !== undefined) {
        problems.set(lookup.key, {column: missing, table});
      }
    }
    this.lookupColumnProblemDerived = {version: this.mappingVersion, tableColumns: this.lookupColumns, problems};
    return problems;
  }

  /** Sends the check again, its body unchanged since it failed. */
  retryLookupCheck(): Promise<void> {
    this.lookupCheckRequests.cancel();
    this.lookupCheckRequests.forget();
    return this.checkLookups();
  }

  /** Drops the lookups' answers and any check waiting or on its way. */
  private resetLookupChecks(): void {
    this.lookupCheckRequests.cancel();
    this.lookupCheckRequests.reset();
    this.lookupChecks = new Map();
    this.lookupCheckError = null;
  }

  /** Loads a lookup table's columns again after they failed to load.
   * Once they arrive, the page's error that failure set is cleared, unless
   * another error replaced it or another table's load still fails with
   * it. */
  async retryLookupColumns(table: string): Promise<void> {
    const failure = this.lookupColumnsFailed.get(table);
    await this.loadLookupColumns(table);
    if (failure !== undefined && this.lookupColumns[table] && this.sourceError === failure
        && ![...this.lookupColumnsFailed.values()].includes(failure)) {
      this.sourceError = null;
    }
  }

  /** Reads `code` as blank in the lookup's code column, and drops the
   * alias the lookup had for it. */
  lookupCodeMeansBlank(lookup: LookupDefinition, code: string): void {
    const column = lookup.codeColumnControl.value;
    if (!column) {
      return;
    }
    removeAlias(lookup, code);
    this.markBlankValue(column, code);
  }

  /** Where the mapping picks each lookup, by lookup key: the field, and
   * the card and sub-block it is on. */
  get lookupUsages(): ReadonlyMap<string, string[]> {
    const derived = this.derived();
    if (derived.lookupUsages) {
      return derived.lookupUsages;
    }
    const allKeys = new Set(this.lookupDefinitions.map(lookup => lookup.key));
    const usages = new Map<string, string[]>();
    for (const source of this.usageSources(allKeys)) {
      for (const part of source.parts.filter(p => p.kind === 'lookup')) {
        const labels = usages.get(part.value) ?? [];
        if (!labels.includes(source.label)) {
          usages.set(part.value, [...labels, source.label]);
        }
      }
    }
    derived.lookupUsages = usages;
    return usages;
  }

  /** The column profile's suggested lookups for a column no lookup reads
   * its code from, but those dismissed this session. */
  get lookupSuggestions(): ImportModuleLookupSuggestion[] {
    const suggestions = this.columnProfile?.lookupSuggestions ?? [];
    if (!suggestions.length) {
      return suggestions;
    }
    const read = new Set([
      ...this.lookupDefinitions.map(lookup => lookup.codeColumnControl.value),
      ...[...this.lookupCodeColumns().values()].flat(),
    ]);
    return suggestions.filter(suggestion => !read.has(suggestion.code_column)
      && !this.dismissedLookupSuggestions.has(lookupSuggestionKey(suggestion)));
  }

  /** A new lookup set up as `suggestion` says. */
  acceptLookupSuggestion(suggestion: ImportModuleLookupSuggestion): LookupDefinition {
    const lookup = this.addLookup();
    lookup.tableControl.setValue(suggestion.table);
    lookup.keyColumnControl.setValue(suggestion.key_column);
    lookup.codeColumnControl.setValue(suggestion.code_column);
    return lookup;
  }

  /** Stops offering `suggestion` for the rest of the session. */
  dismissLookupSuggestion(suggestion: ImportModuleLookupSuggestion): void {
    this.dismissedLookupSuggestions.add(lookupSuggestionKey(suggestion));
  }

  private batchHeaderDef(
      batchKey: string, headerKey: string, required: boolean, fallbackLabel: string = batchKey,
  ): GroupHeaderFieldDef {
    const field = this.batchFields.find(f => f.key === batchKey);
    return {key: headerKey, label: field?.label ?? fallbackLabel, required, options: field?.options ?? []};
  }

  /** The Historikkhendelser card descriptor: header = the event type;
   * members = every history-event field that isn't a nested named party
   * or place; a repeatable Named sub-block (its own role / status + the
   * named_id member) and a repeatable Place sub-block (role / status +
   * the place_id member). Option lists fill in once a mapping makes the
   * batch fields appear. */
  private buildHistoryEventDescriptor(): GroupCardDescriptor {
    const namedField = this.allTargetFields.find(f => f.named_party);
    const placeField = this.allTargetFields.find(f => f.place_party);
    const subBlocks: GroupSubBlockDef[] = [];
    if (namedField) {
      subBlocks.push({
        kind: 'named',
        titleKey: 'TRANS__HISTORY_EVENT_NAMED__ITEM__NAMED',
        addLabelKey: 'TRANS__IMPORT__GROUP_CARD__ADD_NAMED_PARTY',
        header: NAMED_SUB_BLOCK_HEADER.map(
          h => this.batchHeaderDef(h.batchKey, h.headerKey, h.required)),
        member: {fieldId: namedField.field_id, label: namedField.label, concept: true},
      });
    }
    if (placeField) {
      subBlocks.push({
        kind: 'place',
        titleKey: 'TRANS__FIELD__PLACE',
        addLabelKey: 'TRANS__IMPORT__GROUP_CARD__ADD_PLACE',
        header: PLACE_SUB_BLOCK_HEADER.map(
          h => this.batchHeaderDef(h.batchKey, h.headerKey, h.required)),
        member: {fieldId: placeField.field_id, label: placeField.label, concept: true},
      });
    }
    return {
      kind: 'history_event',
      titleKey: 'TRANS__IMPORT__HISTORY_EVENT_GROUP',
      header: [this.batchHeaderDef(HISTORY_EVENT_TYPE_BATCH_KEY, 'event_type_id', true)],
      members: this.allTargetFields
        .filter(f => f.history_event && !f.named_party && !f.place_party)
        .map(f => ({fieldId: f.field_id, label: f.label, concept: false, composable: this.isFreeTextField(f)})),
      subBlocks,
    };
  }

  /**
   * Builds the `groupKindConfigs` entry for one of the "flat" group kinds
   * the backend's `GROUP_KINDS` registry fully describes. `objectType` must
   * match a `groupKindSpecs` entry's `object_type`, the same string a
   * target field's `group_parent` and a `card_kinds` entry carry - another
   * kind added to that registry needs only one more call here, no new
   * `buildXDescriptor` method or `*_BATCH_KEY` constant.
   */
  private flatGroupKindConfig(kind: GroupKind, objectType: string, sectionTitleKey: string): GroupKindConfig {
    return {
      kind, sectionTitleKey,
      cardObjectType: objectType,
      requiresMappedMember: true,
      buildDescriptor: () => this.buildFlatGroupDescriptor(kind, objectType),
      hiddenBatchKeys: () =>
        this.groupKindSpecs.find(s => s.object_type === objectType)?.header.map(h => h.batch_key) ?? [],
    };
  }

  /** The card descriptor for one `flatGroupKindConfig` kind: header, title
   * and each header field's required-ness come straight from the matching
   * `groupKindSpecs` entry (empty/`''` before `GET /object_types` resolves,
   * same as every other descriptor tolerates `batchFields`/`allTargetFields`
   * being briefly empty); members are every target field whose `group_parent`
   * equals `objectType` - exact and mutually exclusive per kind, so no
   * kind needs to exclude another kind's fields by hand - each required
   * when the spec's `required_members` names it. */
  private buildFlatGroupDescriptor(kind: GroupKind, objectType: string): GroupCardDescriptor {
    const spec = this.groupKindSpecs.find(s => s.object_type === objectType);
    const requiredMembers = new Set(spec?.required_members ?? []);
    return {
      kind,
      titleKey: spec?.title_key ?? '',
      header: (spec?.header ?? [])
        .map(h => this.batchHeaderDef(h.batch_key, h.batch_key, h.required)),
      members: this.allTargetFields
        .filter(f => f.group_parent === objectType)
        .map(f => ({
          fieldId: f.field_id, fieldName: f.group_field_name ?? undefined, label: f.label, concept: false,
          composable: this.isFreeTextField(f),
          required: !!f.group_field_name && requiredMembers.has(f.group_field_name),
        })),
      subBlocks: [],
      supportsCreateMissing: !!spec?.options?.includes(GROUP_OPTION_CREATE_MISSING),
      createMissingParentOptions: this.createMissingParentOptions[kind] ?? [],
    };
  }

  /** The admin-event (GenericEvent) card descriptor (a second, context-linked artifact):
   * header = the sub-type AND purpose-type selects, both required (mirrors
   * Measurement's two-required-header shape). Members come
   * from `adminEventTargetFields` - a SEPARATE discovery call scoped to
   * GenericEvent's own superobject_type_id, never `allTargetFields` (see
   * `adminEventTargetFields`) - so unlike
   * every other descriptor here, this one does not depend on the row's own
   * object type at all. No sub-blocks; a member here writes onto a whole
   * SECOND artifact rather than an inline array on the row's own. */
  private buildAdminEventDescriptor(): GroupCardDescriptor {
    return {
      kind: 'admin_event',
      titleKey: 'TRANS__SUPEROBJECT_TYPE_ADM_EVENT_GENERIC',
      header: [
        this.batchHeaderDef(
          ADMIN_EVENT_SUB_TYPE_BATCH_KEY, ADMIN_EVENT_SUB_TYPE_BATCH_KEY, true,
          'TRANS__FIELD__GENERIC_SUB_TYPE__SUB_TYPE'),
        this.batchHeaderDef(
          ADMIN_EVENT_PURPOSE_TYPE_BATCH_KEY, ADMIN_EVENT_PURPOSE_TYPE_BATCH_KEY, true,
          'TRANS__FIELD__PURPOSE_GENERIC__PURPOSE_TYPE'),
      ],
      members: this.adminEventTargetFields
        .map(f => ({
          fieldId: f.field_id, label: f.label, concept: false, repeatable: f.repeatable,
          composable: this.isFreeTextField(f),
        })),
      subBlocks: [],
      withOnlyWhenMappedToggle: true,
    };
  }

  /** The Fast plassering card descriptor: a PlacementEvent per row,
   * context-linked like the admin-event card's. No header - the backend
   * creates permanent placements only. Members come from
   * `placementEventTargetFields`; the Location member takes the Location's
   * full code path (`LOFT.1`), and a row whose path matches no Location
   * gets no placement. The Location member is required. */
  private buildPlacementEventDescriptor(): GroupCardDescriptor {
    return {
      kind: 'placement_event',
      titleKey: 'TRANS__SUPEROBJECT_TYPE_ADM_EVENT_PLACEMENT',
      header: [],
      members: this.placementEventTargetFields
        .map(f => ({
          fieldId: f.field_id, label: f.label, concept: false, repeatable: f.repeatable,
          composable: this.isFreeTextField(f),
          required: f.field_id === this.placementRequiredFieldId,
        })),
      subBlocks: [],
    };
  }

  /** The `field_id` of the placement member a placement cannot be written
   * without, `null` while the kinds are not loaded or when it has none. */
  get placementRequiredFieldId(): string | null {
    return this.adminEventKinds.find(kind => kind.object_type === PLACEMENT_EVENT_KIND)
      ?.required_member_field_id ?? null;
  }

  /**
   * The run is blocked (not just discouraged) when a media join is set,
   * the Forhåndskontroll saw rows with no single media match, and the
   * operator hasn't chosen to skip or create those rows. The backend
   * enforces the same rule — this just explains it before the request.
   */
  get mediaBlocksRun(): boolean {
    if (!this.linkMediaControl.value || !this.mediaJoinColumnControl.value
        || this.mediaOnMissingControl.value !== 'block') {
      return false;
    }
    const s = this.preflightResult?.summary;
    return !!s && (s.media_unmatched > 0 || s.media_ambiguous > 0);
  }

  /** The match rule and several-files policy controls. */
  private get mediaJoinOptionControls(): FormControl[] {
    return [
      this.mediaMatchModeControl, this.mediaMatchPresetControl, this.mediaMatchPatternControl,
      this.mediaIgnoreLeadingZerosControl, this.mediaJoinSeveralControl,
    ];
  }

  /** Puts the match rule and several-files policy back to the defaults,
   * emitting their changes when `emitEvent` is set. */
  private resetMediaJoinOptions(emitEvent: boolean): void {
    this.setMediaJoinOptions(DEFAULT_MEDIA_MATCH, 'skip', emitEvent);
  }

  /** Sets the match rule and several-files policy. The preset shown is
   * the one `match.pattern` names, set without emitting so that it does
   * not write its own pattern over a blank one. */
  private setMediaJoinOptions(
    match: ImportModuleMediaMatch, several: ImportModuleMediaJoinSeveral, emitEvent: boolean,
  ): void {
    this.mediaMatchPatternControl.setValue(match.pattern, {emitEvent});
    this.mediaMatchPresetControl.setValue(mediaMatchPresetFor(match.pattern), {emitEvent: false});
    this.mediaMatchModeControl.setValue(match.mode, {emitEvent});
    this.mediaIgnoreLeadingZerosControl.setValue(match.ignore_leading_zeros, {emitEvent});
    this.mediaJoinSeveralControl.setValue(several, {emitEvent});
  }

  /**
   * Why the media join cannot be checked or run, as a `TRANS__` key;
   * `null` when it can or no join column is picked. The pattern rule
   * needs a pattern, and the pattern rule, ignored leading zeros and
   * linking every file need a media folder: only a folder's files are
   * keyed by the rule.
   */
  get mediaJoinProblem(): string | null {
    const media = this.mediaJoin;
    if (!media.joinColumn) {
      return null;
    }
    if (this.mediaPatternProblem) {
      return this.mediaPatternProblem;
    }
    const needsFolder = media.match.mode === 'pattern' || media.match.ignore_leading_zeros
      || media.several === 'link_all';
    return needsFolder && !media.folderId ? 'TRANS__IMPORT__MEDIA_NEEDS_FOLDER' : null;
  }

  /**
   * `TRANS__IMPORT__MEDIA_MATCH_PATTERN_REQUIRED` while a join column is
   * picked, the rule is the pattern, the operator's own, and it is blank;
   * `null` otherwise. A profile keeps the rule but not the folder, so this
   * is the only part of `mediaJoinProblem` that blocks a profile save.
   */
  get mediaPatternProblem(): string | null {
    const media = this.mediaJoin;
    return media.joinColumn && media.match.mode === 'pattern' && this.mediaMatchPresetControl.value === 'custom'
      && !media.match.pattern.trim()
      ? 'TRANS__IMPORT__MEDIA_MATCH_PATTERN_REQUIRED' : null;
  }

  /** The media join to send: no join column while media linking is off,
   * and no folder while none is picked. */
  private get mediaJoin(): MediaJoin {
    const joinColumn = this.linkMediaControl.value && this.mediaJoinColumnControl.value
      ? this.mediaJoinColumnControl.value : undefined;
    return {
      joinColumn,
      folderId: joinColumn && this.mediaFolderControl.value ? this.mediaFolderControl.value : undefined,
      onMissing: this.mediaOnMissingControl.value,
      match: {
        mode: this.mediaMatchModeControl.value,
        pattern: this.mediaMatchPatternControl.value,
        ignore_leading_zeros: this.mediaIgnoreLeadingZerosControl.value,
      },
      several: this.mediaJoinSeveralControl.value,
    };
  }

  /** Reads `value` as blank in `column`, unless it is already, and takes
   * it off the column's rejected values. */
  markBlankValue(column: string, value: string): void {
    const text = value.trim();
    if (!text) {
      return;
    }
    const rejected = withoutBlankValue(this.blankValuesRejected, column, text);
    const rejectedChanged = rejected !== this.blankValuesRejected;
    this.blankValuesRejected = rejected;
    const blankValues = withBlankValue(this.blankValues, column, text);
    if (blankValues !== this.blankValues) {
      this.blankValues = blankValues;
      this.mappingChanged.next();
    } else if (rejectedChanged) {
      this.stashSourceToSession();
    }
  }

  /** Stops reading `value` as blank in `column`. */
  unmarkBlankValue(column: string, value: string): void {
    const blankValues = withoutBlankValue(this.blankValues, column, value);
    if (blankValues !== this.blankValues) {
      this.blankValues = blankValues;
      this.mappingChanged.next();
    }
  }

  isReadAsBlank(column: string, value: string): boolean {
    return hasBlankValue(this.blankValues[column], value);
  }

  /** The values read as blank by column, in the order each column was
   * first given one. */
  get blankValueColumns(): {column: string; values: string[]}[] {
    return Object.entries(this.blankValues).map(([column, values]) => ({column, values}));
  }

  /** How many cells of `column` its blank values stand in, `null` unless
   * the column profile counts each (see `blankCellCount`). */
  blankCellCount(column: string, values: readonly string[]): number | null {
    return blankCellCount(this.columnProfile?.columns.get(column), values);
  }

  /** Where the mapping reads each column: every flat row and card member
   * taking its cells, directly or through a complete lookup. */
  get usagesByColumn(): ReadonlyMap<string, ColumnUsage[]> {
    const derived = this.derived();
    return derived.usagesByColumn ??= columnUsages(this.usageSources(), this.lookupCodeColumns());
  }

  /** The columns the mapping reads into the object's status, joined with
   * `, `; `''` while none does, and before the batch defaults exist.
   * Relies on a prime field's `meta_field_id` being its target field's
   * `field_id`. */
  get statusColumnsText(): string {
    const fieldId: string | undefined = this.batchDefaults?.primeFields
      ?.find(field => field.name === BATCH_DEFAULT_STATUS_FIELD)?.meta_field_id;
    if (!fieldId) {
      return '';
    }
    const derived = this.derived();
    if (derived.statusColumns?.fieldId !== fieldId) {
      derived.statusColumns = {fieldId, text: this.resolveConceptColumns(fieldId).join(', ')};
    }
    return derived.statusColumns.text;
  }

  /** The column profile's suggestions still open for the columns the
   * mapping reads. None is applied until the operator picks it. */
  get blankValueSuggestions(): BlankValueSuggestion[] {
    const usages = this.usagesByColumn;
    const profile = this.columnProfile;
    const memo = this.suggestionsMemo;
    if (memo?.usages === usages && memo.blankValues === this.blankValues && memo.profile === profile
        && memo.reviewed === this.conceptFields && memo.rejected === this.blankValuesRejected) {
      return memo.suggestions;
    }
    this.suggestionsMemo = {
      usages,
      blankValues: this.blankValues,
      profile,
      reviewed: this.conceptFields,
      rejected: this.blankValuesRejected,
      suggestions: profile ? blankValueSuggestions({
        columns: this.columns.map(column => column.name),
        profile: profile.columns,
        usages,
        blankValues: this.blankValues,
        rejected: this.blankValuesRejected,
        lookupName: column => this.lookupNameOf(column),
        reviewed: this.conceptFields,
      }) : [],
    };
    return this.suggestionsMemo.suggestions;
  }

  // `usedColumns` for one set of usages, media join column and table.
  private usedColumnDerived: {
    usages: ReadonlyMap<string, ColumnUsage[]>;
    joinColumn: string | null;
    columns: SourceColumn[];
    used: ReadonlySet<string>;
  } | null = null;

  /** The media join column, while media linking is on. */
  get mediaJoinColumn(): string | null {
    return this.mediaJoin.joinColumn ?? null;
  }

  /** The table's columns the run reads: those a row, card member or
   * lookup reads (see `usagesByColumn`), and the media join column. */
  get usedColumns(): ReadonlySet<string> {
    const usages = this.usagesByColumn;
    const joinColumn = this.mediaJoinColumn;
    const cached = this.usedColumnDerived;
    if (cached?.usages === usages && cached.joinColumn === joinColumn && cached.columns === this.columns) {
      return cached.used;
    }
    const used = new Set(this.columns.map(column => column.name)
      .filter(column => usages.has(column) || column === joinColumn));
    this.usedColumnDerived = {usages, joinColumn, columns: this.columns, used};
    return used;
  }

  /** Every flat row and card member that takes its value from columns,
   * and while the media join has a column every row onto a field of the
   * linked media, under «Bilde». A row reads a lookup among `lookupKeys`
   * only. */
  private usageSources(
    lookupKeys: ReadonlySet<string> = completeLookupKeys(this.lookupDefinitions),
  ): UsageSource[] {
    const partsOf = (row: ValueSourceControls) => valueSourceParts(row, this.columnNames, lookupKeys);
    const sources: UsageSource[] = [];
    for (const assignment of this.assignments) {
      const field = assignment.targetField;
      if (field) {
        sources.push({
          label: field.label, field: field.label, fieldId: field.field_id,
          kind: fieldUsageKind(field, false), parts: partsOf(assignment),
        });
      }
    }
    const media = this.translate.instant(MEDIA_VALUES_TITLE_KEY);
    for (const assignment of this.mediaJoinColumn ? this.mediaAssignments : []) {
      const field = assignment.targetField;
      if (field) {
        sources.push({
          label: `${media} · ${field.label}`, field: field.label, fieldId: field.field_id,
          kind: fieldUsageKind(field, false), parts: partsOf(assignment),
        });
      }
    }
    for (const kind of this.groupKinds) {
      const descriptor = this.groupDescriptors[kind];
      const memberFields = this.memberFieldsOf(kind);
      this.groupModels[kind].forEach((model, index) => {
        const card = `${this.translate.instant(descriptor.titleKey)} ${index + 1}`;
        for (const row of model.members.filter(member => member.sourceControl.value === 'column')) {
          const field = descriptor.members.find(member => member.fieldId === row.fieldId)?.label ?? row.fieldId;
          sources.push({
            label: `${card} · ${field}`, field, fieldId: row.fieldId,
            kind: memberUsageKind(
              kind, memberFields.find(f => f.field_id === row.fieldId),
              row.valueTypeControl.value === 'kulturnav_uuid'),
            parts: partsOf(row),
          });
        }
        for (const def of descriptor.subBlocks) {
          model.subBlocks.filter(sub => sub.defKind === def.kind).forEach((sub, subIndex) => {
            if (sub.member.sourceControl.value !== 'column') {
              return;
            }
            const title = `${this.translate.instant(def.titleKey)} ${subIndex + 1}`;
            sources.push({
              label: `${card} · ${title}`, field: title, fieldId: sub.member.fieldId,
              kind: subBlockUsageKind(sub), parts: partsOf(sub.member),
            });
          });
        }
      });
    }
    return sources;
  }

  /** The field list a `kind` card's members come from: the admin-event
   * and Fast plassering cards write their own artifact, every other card
   * the row's. */
  private memberFieldsOf(kind: GroupKind): readonly TargetField[] {
    switch (kind) {
      case 'admin_event':
        return this.adminEventTargetFields;
      case 'placement_event':
        return this.placementEventTargetFields;
      default:
        return this.allTargetFields;
    }
  }

  /** The source columns each complete lookup reads its code from, by
   * lookup key: its key template's columns, else its code column. */
  private lookupCodeColumns(): Map<string, string[]> {
    const columns = new Map<string, string[]>();
    for (const lookup of this.lookupDefinitions) {
      const payload = lookupPayload(lookup, this.columnNames);
      if (payload) {
        const keyColumns = (payload.key_parts ?? []).filter(part => part.kind === 'column').map(part => part.value);
        columns.set(payload.key, keyColumns.length ? keyColumns : [payload.code_column]);
      }
    }
    return columns;
  }

  /** The lookup reading its codes from `column`, by the name it shows,
   * else the table the column profile suggests looking its codes up in. */
  private lookupNameOf(column: string): string | null {
    const lookup = this.lookupDefinitions.find(l => isLookupComplete(l) && l.codeColumnControl.value === column);
    return (lookup && lookupDisplayName(lookup))
      || this.columnProfile?.lookupSuggestions.find(s => s.code_column === column)?.table
      || null;
  }

  /** Keeps `value` in `column` from being suggested as blank again. A
   * change to the profile only: no scan, preflight or run reads it. */
  rejectBlankValue(column: string, value: string): void {
    const rejected = withBlankValue(this.blankValuesRejected, column, value);
    if (rejected !== this.blankValuesRejected) {
      this.blankValuesRejected = rejected;
      this.stashSourceToSession();
    }
  }

  /** Undoes `rejectBlankValue`: `value` in `column` may be suggested
   * again. */
  unrejectBlankValue(column: string, value: string): void {
    const rejected = withoutBlankValue(this.blankValuesRejected, column, value);
    if (rejected !== this.blankValuesRejected) {
      this.blankValuesRejected = rejected;
      this.stashSourceToSession();
    }
  }

  /** `{key: id}` for every batch field the operator has given a value. */
  get batchValues(): { [key: string]: string } {
    const values: { [key: string]: string } = {};
    for (const field of this.batchFields) {
      const value = this.batchControls[field.key]?.value;
      if (value) {
        values[field.key] = value;
      }
    }
    return values;
  }

  /** A target field's display label, from the picker options, the group
   * cards' members or the fields of the linked media (the server refers to
   * fields by id). */
  labelFor(fieldId: string): string {
    return this.targetOptions.find(option => option.field_id === fieldId)?.label
      ?? this.groupMemberLabel(fieldId)
      ?? this.mediaTargetFields.find(field => field.field_id === fieldId)?.label
      ?? fieldId;
  }

  /** The label a group card shows for `fieldId` as one of its members or
   * a sub-block's member, if any card kind maps it. */
  private groupMemberLabel(fieldId: string): string | undefined {
    for (const descriptor of Object.values(this.groupDescriptors)) {
      const member = [...descriptor.members, ...descriptor.subBlocks.map(block => block.member)]
        .find(m => m.fieldId === fieldId);
      if (member) {
        return member.label;
      }
    }
    return undefined;
  }

  /** Every value-review row across all fields, for `POST /run`. The
   * `<app-value-review-page>` child mutates these row objects in place. */
  get conceptReviewRows(): ImportModuleConceptRow[] {
    return this.conceptFields.flatMap(field => field.review_rows);
  }

  /** The decisions a profile save sends: the scan's rows while a scan
   * holds them, otherwise `profileDecisions`. */
  private get decisionsToSave(): ImportModuleConceptRow[] | ImportModuleDecisionRow[] {
    return this.conceptScanRequestKey === null ? this.profileDecisions : this.conceptReviewRows;
  }

  /** `labelFor`, bound (arrow) to keep `this` the store, for a child
   * component that labels a field the backend names only by id. */
  resolveConceptLabel = (fieldId: string): string => this.labelFor(fieldId);

  /** The source columns mapped onto a target field, for the value review.
   * Reads only the rows mapped onto that field. */
  resolveConceptColumns = (fieldId: string): string[] => [...new Set(
    buildMapping(this.mappingState, fieldId)
      .flatMap(m => m.parts)
      .map(part => this.sourceColumnOf(part))
      .filter((column): column is string => !!column),
  )];

  /** The source column a template part reads - for a lookup part, its
   * lookup's code column; `null` for literal text. */
  private sourceColumnOf(part: TemplatePart): string | null {
    if (part.kind === 'text') {
      return null;
    }
    if (part.kind === 'lookup') {
      return this.lookupDefinitions.find(lookup => lookup.key === part.value)?.codeColumnControl.value ?? null;
    }
    return part.value;
  }

  /** A value-review scan has run for the current table. */
  get hasConceptScan(): boolean {
    return this.conceptScanRequestKey !== null;
  }

  /** The loaded profile's name, `null` when none is loaded. */
  get selectedProfileName(): string | null {
    return this.profiles.find(p => p.profile_id === this.selectedProfileId)?.profile_name ?? null;
  }

  /** An edited value-review decision makes a shown Forhåndskontroll stale
   * (the preflight request carries the decisions), changes the entry
   * card's counts, and goes into the session stash. */
  conceptDecisionsChanged(): void {
    this.clearPreflightResult();
    this.refreshValueReviewSummary();
    this.stashReviewSoon();
  }

  /**
   * Forhåndskontroll: post the current mapping and have the backend dry-run
   * the first N rows (compose values, validate, classify concept values,
   * resolve the media join), and read the media folder against the join
   * column of the whole table, without writing anything. Not while a new
   * file uploads to replace the one the mapping reads, nor while the media
   * join's options cannot be checked (`mediaJoinProblem`).
   */
  async runPreflight(): Promise<void> {
    const objectType = this.selectedObjectType;
    if (!objectType || this.runningPreflight || this.uploading || !this.hasMappings || this.mediaJoinProblem) {
      return;
    }
    this.runningPreflight = true;
    this.preflightError = null;
    const seq = this.preflightRequestSeq;
    const run = ++this.preflightRunSeq;
    try {
      const rowRange = this.effectiveRowRange;
      const source = {dmsIdentifier: this.dmsIdentifier, table: this.selectedTable};
      const result = await this.cms.importModulePreflight(preflightRequest({
        ...this.configurationState(objectType),
        valueDecisions: this.conceptReviewRows,
        sampleSize: this.preflightSampleSize,
        batchDefaults: this.readBatchDefaults(),
        rowRange,
        mediaOverview: true,
      }));
      if (seq === this.preflightRequestSeq) {
        this.preflightResult = result;
        this.preflightResultRange = rowRange;
        const rows = result.more_rows ? result.id_numbering?.rows ?? null : result.rows_previewed;
        if (rows !== null) {
          this.rangeRowCount = {...source, range: rowRange, rows};
        }
      }
    } catch (e: any) {
      this.logger.error(`raw import: preflight failed: ${e}`);
      if (seq === this.preflightRequestSeq) {
        this.preflightError = this.sourceErrorText(e, 'TRANS__IMPORT__ERROR__PREFLIGHT');
      }
    } finally {
      if (run === this.preflightRunSeq) {
        this.runningPreflight = false;
      }
    }
  }

  /** `clearPreflightResult`, and ends the wait for one in flight: the
   * mapping it checks is gone, so a new one can start. */
  private abandonPreflight(): void {
    this.clearPreflightResult();
    this.preflightRunSeq++;
    this.runningPreflight = false;
  }

  /** Drops a shown Forhåndskontroll and supersedes one in flight. */
  private clearPreflightResult(): void {
    this.preflightResult = null;
    this.preflightResultRange = null;
    this.preflightRequestSeq++;
  }

  /** The rows of the selected table, as the stepper counts them; `null`
   * while not known. */
  get livePreviewRowCount(): RowCount | null {
    const table = this.tableStats.get(this.selectedTable);
    return table ? {rows: table.rows, exact: table.rows_exact} : null;
  }

  /** Asks for row `row` next, kept within the table's rows when their
   * exact number is known: an approximate count can fall short. */
  setLivePreviewRow(row: number): void {
    const count = this.livePreviewRowCount;
    const last = count?.exact ? count.rows : undefined;
    const bounded = Math.max(1, Math.floor(last ? Math.min(row, last) : row) || 1);
    if (bounded !== this.livePreviewRow) {
      this.livePreviewRow = bounded;
      this.scheduleLivePreview();
    }
  }

  /** Whether a preview request is waiting to be sent or on its way: the
   * preview shown may be out of date. */
  get livePreviewPending(): boolean {
    return this.livePreviewRequests.pending;
  }

  /** Whether a preview request is on its way. */
  get livePreviewLoading(): boolean {
    return this.livePreviewRequests.inFlight;
  }

  /** The last answer as the preview and the mapping lines show it, built
   * once per answer, mapping version, list order and set of fixed-pick
   * names. */
  get livePreview(): LivePreviewView | null {
    const answer = this.livePreviewAnswer;
    const sections = this.targetSections;
    const pickNames = this.livePreviewPickNames;
    const memo = this.livePreviewMemo;
    if (memo?.answer === answer && memo.sections === sections && memo.version === this.mappingVersion
      && memo.pickNames === pickNames) {
      return memo.view;
    }
    const view = answer ? buildLivePreviewView(answer.row, answer.result, this.livePreviewLines(sections)) : null;
    this.livePreviewMemo = {answer, sections, version: this.mappingVersion, pickNames, view};
    return view;
  }

  /** What `line` gets in the previewed row; `undefined` while there is no
   * preview or the line sends it nothing. */
  livePreviewLineValues(line: MappingLine): RowValues | undefined {
    return this.livePreview?.lines.get(line);
  }

  /** What `card` and its member rows get in the previewed row; `null`
   * while there is no preview or the card sends it nothing. */
  livePreviewCard(card: GroupCardModel): CardRowValues | null {
    return this.livePreview?.cards.get(card) ?? null;
  }

  /** The cards the preview leaves entries of out (`heldBackCards`), each
   * as the `incomplete_cards` entry the preview names it by
   * (`incompleteCardText`): its key, the object type its kind writes, and
   * the picks it lacks, one without a title key as a field with no
   * title. Built once per `mappingVersion`. */
  get livePreviewHeldBack(): readonly ImportModuleIncompleteCard[] {
    const derived = this.derived();
    derived.livePreviewHeldBack ??= heldBackCards(this.mappingState).map(held => ({
      card: held.card.key,
      kind: this.configFor(held.kind).cardObjectType,
      missing: held.lacking.map(title => ({label_key: title || null})),
    }));
    return derived.livePreviewHeldBack;
  }

  /** The first row past the one asked for that the shown Forhåndskontroll
   * found a warning on; `null` when it found none. */
  get livePreviewNextWarningRow(): number | null {
    const result = this.preflightResult;
    const row = this.livePreviewRow;
    const memo = this.nextWarningMemo;
    if (memo?.result !== result || memo.row !== row) {
      this.nextWarningMemo = {result, row, next: nextWarningRow(result, row)};
    }
    return this.nextWarningMemo!.next;
  }

  /** The mapping lines that send the preview an entry
   * (`livePreviewState`), in list order under their headings, as the
   * preview reads them; a row onto a field of the linked media under
   * «Bilde». An event card carries the title the list names it
   * by (`cardListTitle`), and every card the parts the answer does not
   * (`previewCardParts`). */
  private livePreviewLines(sections: readonly TargetSection<TargetListLine>[]): PreviewLine[] {
    const sent = entryRows(livePreviewState(this.mappingState));
    const sentMedia = mediaEntryRows(this.mappingState);
    const lines: PreviewLine[] = [];
    for (const {key, titleKey, lines: sectionLines} of sections) {
      const section = {key, titleKey};
      for (const {row, card, cfg, index} of sectionLines) {
        const onMedia = !!row && sentMedia.flat.has(row);
        const sentFlat = onMedia ? sentMedia : sent;
        if (row?.targetField && sentFlat.flat.has(row)) {
          lines.push({
            kind: 'flat', line: row, fieldId: row.targetField.field_id, label: row.targetField.label,
            order: (onMedia ? this.mediaAssignments : this.assignments).indexOf(row),
            readsLookup: sentFlat.lookupReaders.has(row), section: onMedia ? MEDIA_VALUES_SECTION : section, onMedia,
          });
        }
        const rows = card && sent.cards.get(card);
        if (card && cfg && rows) {
          const eventKind = EVENT_GROUP_KINDS[cfg.kind];
          const descriptor = this.groupDescriptors[cfg.kind];
          lines.push({
            kind: 'card', line: card, groupKind: cfg.kind, descriptor,
            index: index + 1, rows, lookupRows: new Set(rows.filter(r => sent.lookupReaders.has(r))),
            title: eventKind ? this.cardListTitle(card.key, eventKind) ?? undefined : undefined, section,
            ...previewCardParts(card, descriptor, !!eventKind, this.livePreviewPickNames),
          });
        }
      }
    }
    return lines;
  }

  /** Looks up the names of the group cards' fixed Egennavn and Sted picks
   * that have none yet (`PickNamesService`), for the preview to show them
   * by. A pick whose name is on its way is not asked for again, nor one a
   * lookup found no record for at this mapping version; one whose lookup
   * failed is asked for on the next answer. */
  private async loadLivePreviewPickNames(): Promise<void> {
    const misses = this.pickMisses.version === this.mappingVersion ? this.pickMisses.ids : new Set<string>();
    const missing = this.fixedPicks().filter(pick => !this.livePreviewPickNames.has(pick.id)
      && !misses.has(pick.id) && !this.pickNamesInFlight.has(pick.id));
    if (!missing.length) {
      return;
    }
    const ids = new Set(missing.map(pick => pick.id));
    ids.forEach(id => this.pickNamesInFlight.add(id));
    try {
      const {names, looked} = await this.pickNames.names(missing);
      if (names.size) {
        this.livePreviewPickNames = new Map([...this.livePreviewPickNames, ...names]);
      }
      const unfound = [...looked].filter(id => !names.has(id));
      if (unfound.length) {
        const version = this.mappingVersion;
        const kept = this.pickMisses.version === version ? this.pickMisses.ids : [];
        this.pickMisses = {version, ids: new Set([...kept, ...unfound])};
      }
    } finally {
      ids.forEach(id => this.pickNamesInFlight.delete(id));
    }
  }

  /** Every fixed pick of a Named or Place sub-block on the group cards. */
  private fixedPicks(): FixedPick[] {
    return this.groupKindConfigs
      .flatMap(cfg => this.groupModels[cfg.kind])
      .flatMap(model => model.subBlocks)
      .filter(sub => sub.member.sourceControl.value === 'constant' && memberFixedIsPick(sub.member, true)
        && !!sub.member.conceptControl.value)
      .map(sub => ({id: sub.member.conceptControl.value!, kind: sub.defKind}));
  }

  /** (Re)starts the wait before the live preview asks for its row, unless
   * none is running and the preview would send the body it sent last - as
   * after a change to a lookup's name alone, which the body leaves out. */
  private scheduleLivePreview(): void {
    this.livePreviewRequests.schedule(() => {
      const request = this.livePreviewRequest();
      return request && JSON.stringify(request);
    });
  }

  /** Asks for `livePreviewRow` in a one-row `POST /preflight`
   * (`livePreviewRequest`), with `first_row = last_row` = the row, unless
   * it is the body last sent. Sends nothing while another tab is
   * shown, and drops the preview while it has nothing to ask for. While a
   * request is on its way, on this tab or another, waits for it and then
   * sends the latest body. */
  async refreshLivePreview(): Promise<void> {
    if (this.livePreviewLoading) {
      this.livePreviewQueued = true;
      return;
    }
    if (this.view !== 'feltkoblinger') {
      return;
    }
    const request = this.livePreviewRequest();
    if (!request) {
      this.dropLivePreview();
      return;
    }
    await this.sendLivePreview(request, JSON.stringify(request));
  }

  /** The one-row preview body for `livePreviewRow`, built from what the
   * preview reads (`livePreviewState`); `null` while that holds nothing
   * to preview. It carries no value decisions: they decide only the
   * value-review warnings, which the preview does not show. Nor does it
   * carry the lookups' names, so renaming one does not ask for the row
   * again. */
  private livePreviewRequest(): PreflightRequest | null {
    const objectType = this.selectedObjectType;
    if (!objectType || !this.selectedTable) {
      return null;
    }
    const state = livePreviewState(this.configurationState(objectType));
    if (!hasMappingEntry(state)) {
      return null;
    }
    const row = this.livePreviewRow;
    const request = preflightRequest({
      ...state,
      sampleSize: 1,
      batchDefaults: this.readBatchDefaults(),
      rowRange: {first: row, last: row},
    });
    const lookups = request.lookups?.map(({name, ...lookup}) => lookup);
    return lookups ? {...request, lookups} : request;
  }

  /** Sends `request` and shows its answer, unless `body` is the one last
   * sent, or a new file or table, or nothing left to preview, came first.
   * When the preview was asked for again meanwhile and the body has
   * changed, the answer is dropped, and the new body sent on this tab or
   * when the tab is shown again. */
  private async sendLivePreview(request: PreflightRequest, body: string): Promise<void> {
    const seq = this.livePreviewRequests.begin(body);
    if (seq === null) {
      return;
    }
    this.livePreviewError = null;
    this.livePreviewQueued = false;
    let result: ImportModulePreflightResult | null = null;
    let error: unknown = null;
    try {
      result = await this.cms.importModulePreflight(request);
    } catch (e: any) {
      error = e;
    }
    if (!this.livePreviewRequests.isLatest(seq)) {
      return;  // a new file or table, or nothing left to preview
    }
    this.livePreviewRequests.finish(seq);
    if (this.livePreviewQueued) {
      this.livePreviewQueued = false;
      const next = this.livePreviewRequest();
      if (!next) {
        this.dropLivePreview();
        return;
      }
      const nextBody = JSON.stringify(next);
      if (nextBody !== body) {
        if (this.view === 'feltkoblinger') {
          await this.sendLivePreview(next, nextBody);
        } else {
          this.livePreviewRequests.forget();
        }
        return;
      }
    }
    if (error) {
      this.logger.error(`raw import: live preview failed: ${error}`);
      this.livePreviewAnswer = null;
      this.livePreviewError = this.sourceErrorText(error, 'TRANS__IMPORT__ERROR__LIVE_PREVIEW');
    } else {
      this.livePreviewAnswer = {row: request.first_row!, result: result!};
      void this.loadLivePreviewPickNames();
    }
  }

  /** Drops the preview and supersedes a request on its way: there is
   * nothing to preview. */
  private dropLivePreview(): void {
    this.livePreviewRequests.reset();
    this.livePreviewAnswer = null;
    this.livePreviewQueued = false;
    this.livePreviewError = null;
  }

  /** Sends the preview again, its body unchanged since it failed. */
  retryLivePreview(): Promise<void> {
    this.livePreviewRequests.cancel();
    this.livePreviewRequests.forget();
    return this.refreshLivePreview();
  }

  /** Drops the preview and any request waiting or on its way, and starts
   * again from row 1. */
  private resetLivePreview(): void {
    this.livePreviewRequests.cancel();
    this.dropLivePreview();
    this.livePreviewRow = 1;
  }

  /** The sample size the next preflight sends: every row of the run range
   * while **Hele filen** is on and the range fits, else the one typed. */
  get preflightSampleSize(): number {
    return this.preflightWholeFile ? this.preflightRangeBound! : this.preflightSampleSizeControl.value;
  }

  /** **Hele filen** is on and the run range fits in one preflight. */
  get preflightWholeFile(): boolean {
    return this.preflightWholeFileControl.value && this.preflightRangeFits;
  }

  /** The run range holds at least one and at most
   * `PREFLIGHT_MAX_SAMPLE_SIZE` rows, so one preflight can check all of
   * them. */
  get preflightRangeFits(): boolean {
    const rows = this.preflightRangeBound;
    return rows !== null && rows > 0 && rows <= ImportSessionStore.PREFLIGHT_MAX_SAMPLE_SIZE;
  }

  /** The rows in the run range, or the most it can hold when only its
   * bounds are known (a **Til rad** past the table's end); `null` when
   * neither is known. */
  private get preflightRangeBound(): number | null {
    const count = this.runRangeCount;
    if (count !== null) {
      return count.rows;
    }
    const {first, last} = this.effectiveRowRange;
    return last === null ? null : Math.max(last - (first ?? 1) + 1, 0);
  }

  /** The rows the current run range holds; `null` while not known. */
  get runRangeCount(): RowCount | null {
    return this.rangeCount(this.effectiveRowRange);
  }

  /** The rows the range the shown preflight ran over holds; `null` while
   * not known or no preflight is shown. The same object while the count
   * stays the same. */
  get preflightResultRangeCount(): RowCount | null {
    const count = this.preflightResultRange ? this.rangeCount(this.preflightResultRange) : null;
    const shown = this.preflightResultRangeCountShown;
    if (count && shown && count.rows === shown.rows && count.exact === shown.exact) {
      return shown;
    }
    this.preflightResultRangeCountShown = count;
    return count;
  }

  /** The rows `range` of the selected table holds: from the table's exact
   * count, else from a preflight over the same table and range (every row
   * it checked when it reached the range's end, else the numbering
   * check's count), else from the table's approximate count. `null` when
   * none of them is known. */
  private rangeCount(range: RowRange): RowCount | null {
    const table = this.tableStats.get(this.selectedTable);
    if (table?.rows_exact) {
      return {rows: ImportSessionStore.rowsInRange(range, table.rows), exact: true};
    }
    const counted = this.rangeRowCount;
    if (counted && counted.dmsIdentifier === this.dmsIdentifier && counted.table === this.selectedTable
        && ImportSessionStore.sameRowRange(counted.range, range)) {
      return {rows: counted.rows, exact: true};
    }
    return table ? {rows: ImportSessionStore.rowsInRange(range, table.rows), exact: false} : null;
  }

  /** The rows of a `tableRows`-row table that `range` takes in. */
  private static rowsInRange(range: RowRange, tableRows: number): number {
    const end = range.last !== null ? Math.min(range.last, tableRows) : tableRows;
    return Math.max(end - (range.first ?? 1) + 1, 0);
  }

  /** The shown preflight checked every row of the current run range, so
   * its counts are the run's. */
  get preflightCoversRunRange(): boolean {
    const result = this.preflightResult;
    return !!result && !result.more_rows && !!this.preflightResultRange
      && ImportSessionStore.sameRowRange(this.preflightResultRange, this.effectiveRowRange);
  }

  /** A **Fra rad** or **Til rad** is picked. */
  get runRangeChosen(): boolean {
    const {first, last} = this.effectiveRowRange;
    return first !== null || last !== null;
  }

  private static sameRowRange(a: RowRange, b: RowRange): boolean {
    return a.first === b.first && a.last === b.last;
  }

  /** `rowRange` as the request reads it: a blank or zero bound is none. */
  private get effectiveRowRange(): RowRange {
    const {first, last} = this.rowRange;
    return {first: first || null, last: last || null};
  }

  /** An event-group key is a Fast plassering card's. */
  isPlacementGroupKey = (key: string): boolean =>
    this.groupModels.placement_event.some(m => m.key === key);

  /** The title of the card a preflight key belongs to, as the mapping list
   * titles it (`groupCardListTitle`): "Historikkhendelse 1 · Funn",
   * "Materiale 2", found by the card's position in `groupModels` since the
   * key alone carries no label. `kind` is the kind `event_groups` gives an event card's
   * key, `null` for a key it does not list: a flat group card's
   * (`GROUP_ID_KINDS`), which a member's field warning names. `null` for a
   * key that no longer matches any card (the mapping changed after the
   * preflight result was fetched, e.g. a card was deleted). An arrow, so a
   * child component can call it unbound. */
  cardListTitle = (key: string, kind: EventGroupKind | null): string | null => {
    const groupKinds: readonly GroupKind[] = kind === null ? GROUP_ID_KINDS
      : this.groupKinds.filter(k => EVENT_GROUP_KINDS[k] === kind);
    for (const groupKind of groupKinds) {
      const models = this.groupModels[groupKind];
      const index = models.findIndex(m => m.key === key);
      if (index !== -1) {
        return groupCardListTitle(
          this.groupDescriptors[groupKind], models[index], index + 1, titleKey => this.translate.instant(titleKey)).text;
      }
    }
    return null;
  };

  /** The name the history-event card `cardKey` gives its sub-block
   * `subBlock` ("Egennavn 2"): the block's title and its number among the
   * card's blocks of its kind, as the card shows it. `null` where the card
   * has one block of that kind, and for a key no card or block has. An
   * arrow, so a child component can call it unbound. */
  subBlockListTitle = (cardKey: string, subBlock: string): string | null => {
    const model = this.groupModels.history_event.find(m => m.key === cardKey);
    const sub = model?.subBlocks.find(s => s.key === subBlock);
    if (!model || !sub) {
      return null;
    }
    const sameKind = model.subBlocks.filter(s => s.defKind === sub.defKind);
    const def = this.groupDescriptors.history_event.subBlocks.find(d => d.kind === sub.defKind);
    return def && sameKind.length > 1 ? `${this.translate.instant(def.titleKey)} ${sameKind.indexOf(sub) + 1}` : null;
  };

  /** The list title of the card `key` belongs to (`cardListTitle`), for a
   * key that is a flat group card's or an admin-event card's: those keys
   * never coincide. `null` for a key neither kind has. An arrow, so a child
   * component can call it unbound. */
  flatOrAdminCardTitle = (key: string): string | null =>
    this.cardListTitle(key, null) ?? this.cardListTitle(key, 'admin');

  /**
   * The line an `incomplete_cards` entry shows: "Mål 1 kan ikke lagres:
   * mangler Måltype, Mål", the card by its list title (`cardListTitle`)
   * and each missing field by its translated title, a title two fields
   * share once. The card is a flat group card's or an admin-event card's
   * key, or a history-event card's in an entry of `livePreviewHeldBack`.
   * A card no longer on the page goes by its kind's title alone, and a
   * kind the page has no card for by the missing fields alone; a field
   * with no title is left out, and a card with none left says it lacks a
   * required choice. An arrow, so a child component can call it unbound.
   */
  incompleteCardText = (entry: ImportModuleIncompleteCard): string => {
    const t = (key: string, params?: object): string => this.translate.instant(key, params);
    const titles = entry.missing.flatMap(field => field.label_key ? [t(field.label_key)] : []);
    const fields = [...new Set(titles)].join(', ');
    const kindTitleKey = this.cardKindTitleKey(entry.kind);
    const title = this.flatOrAdminCardTitle(entry.card) ?? this.cardListTitle(entry.card, 'history')
      ?? (kindTitleKey ? t(kindTitleKey) : null);
    if (!fields) {
      return title ? `${title}: ${t('TRANS__IMPORT__LINE__INCOMPLETE')}` : t('TRANS__IMPORT__LINE__INCOMPLETE');
    }
    return title ? t('TRANS__IMPORT__PREFLIGHT_INCOMPLETE_CARD', {card: title, fields})
      : t('TRANS__IMPORT__GROUP_CARD__MISSING_MEMBERS', {fields});
  };

  /** `error`, the media match pattern's refusal, as the operator reads
   * it. An arrow, so a child component can call it unbound. */
  mediaPatternText = (error: ImportModuleMediaPatternError): string => mediaPatternErrorText(this.translate, error);

  /**
   * The line a `rights` entry shows: its key translated with its
   * parameters, each admin-event kind by its card title and each field by
   * its translated title, else the page's label for its id (`labelFor`).
   * A kind the page has no card for goes by its object type. An arrow, so
   * a child component can call it unbound.
   */
  lackingRightText = (entry: ImportModuleLackingRight): string => {
    const t = (key: string, params?: object): string => this.translate.instant(key, params);
    const params: Record<string, string> = {};
    if (entry.params.kinds) {
      params['kinds'] = entry.params.kinds.map(kind => {
        const titleKey = this.cardKindTitleKey(kind);
        return titleKey ? t(titleKey) : kind;
      }).join(', ');
    }
    if (entry.params.fields) {
      params['fields'] = entry.params.fields
        .map(field => field.label_key ? t(field.label_key) : this.labelFor(field.field_id)).join(', ');
    }
    return t(entry.key, params);
  };

  /** The title key of the card kind that writes `objectType`; '' for an
   * object type no card kind writes. */
  private cardKindTitleKey(objectType: string): string {
    const config = this.groupKindConfigs.find(cfg => cfg.cardObjectType === objectType);
    return config ? this.groupDescriptors[config.kind].titleKey : '';
  }

  /**
   * Run the import. Flushes the batch-defaults form into its model, reads
   * collection + registration level off it, assembles the `/run` spec,
   * and hands it to `runJob`.
   */
  async run(): Promise<void> {
    const spec = this.buildRunSpec();
    if (!spec) {
      return;
    }
    await this.runJob.submit(spec);
  }

  /** The batch defaults' collection by its name, for the run card; `null`
   * while the model holds no name for it. Read off the model the form was
   * last synced into. */
  get runCollectionLabel(): string | null {
    return (this.batchDefaults?.rootObject as any)?.collection?.collection_id_value || null;
  }

  /** The row range picked for the next preflight or run. */
  private get rowRange(): RowRange {
    return {first: this.runFirstRowControl.value, last: this.runLastRowControl.value};
  }

  private buildRunSpec(): ImportModuleRunSpec | null {
    const objectType = this.selectedObjectType;
    if (!this.canRun || !objectType || !this.batchDefaults) {
      return null;
    }
    const batchDefaults = this.readBatchDefaults();
    if (!batchDefaults.collectionId || !batchDefaults.regLevelId) {
      this.notificationService.addNotification(new AppNotification(
        ['TRANS__IMPORT__ERROR__BATCH_DEFAULTS_INCOMPLETE'], 'warning'));
      return null;
    }
    return runSpec({
      ...this.configurationState(objectType),
      valueDecisions: this.conceptReviewRows,
      batchDefaults,
      overwriteExisting: this.overwriteExistingControl.value,
      rowRange: this.rowRange,
    });
  }

  /** The rejected response's media pattern problem translated, else its
   * own message, else `fallbackKey` translated when its body carried
   * none. */
  private errorText(e: any, fallbackKey: string): string {
    const patternError = mediaPatternErrorOf(e);
    if (patternError) {
      return mediaPatternErrorText(this.translate, patternError);
    }
    const message = e?.errorMessage;
    return message && message !== UNKNOWN_ERROR_MESSAGE ? message : this.translate.instant(fallbackKey);
  }

  /** Uploads `file` to the media server as the source, unless an upload
   * is already running, and reads its tables. Until the upload succeeds
   * the page keeps the file before, with its tables and mapping, and
   * cannot run it; a profile picked meanwhile lands on that file when the
   * upload fails. Once it succeeds the page's discovery starts over; a
   * mapping built on the file before is carried over (`carryMappingOver`)
   * and lands once the new file's tables are listed. */
  async uploadSource(file: File): Promise<void> {
    if (this.uploading) {
      return;
    }
    this.uploading = true;
    this.uploadError = '';
    const pendingBefore = this.pendingProfileApply;
    let dmsIdentifier: string | null = null;
    try {
      const {dms_identifier, url} = await this.cms.getImportSourceUploadUrl(file.name);
      const response = await fetch(url, {
        method: 'PUT',
        headers: {'Content-Type': file.type || 'application/octet-stream'},
        body: file,
      });
      if (!response.ok) {
        throw new Error(`DMS upload responded ${response.status}`);
      }
      dmsIdentifier = dms_identifier;
    } catch (e) {
      this.logger.error(`raw import: source upload failed: ${e}`);
      this.uploadError = 'TRANS__IMPORT__UPLOAD_FAILED';
    } finally {
      this.uploading = false;
    }
    if (dmsIdentifier === null) {
      if (this.pendingProfileApply && this.pendingProfileApply !== pendingBefore) {
        // Picked during the upload, it waited for the new file's tables.
        await this.showPendingProfile();
      }
      return;
    }
    this.carryMappingOver();
    // Drops a read of the file before, too.
    this.resetDiscovery();
    this.clearTableStats();
    this.dmsIdentifier = dmsIdentifier;
    this.fileName = file.name;
    this.fileSize = file.size;
    this.uploadedAt = Date.now();
    this.fileUploaded = true;
    this.previousFileGone = false;
    // An offer made while this upload ran was for the file it replaces.
    this.resumeCandidate = null;
    this.resumeInspect = null;
    this.stashSourceToSession();
    await this.inspectSource();
  }

  /** Makes the page's mapping - rows and cards, lookups, blank values,
   * media join - the pending restore, with its table as the one to select,
   * when one is built and no other restore is waiting. A restore picked
   * later, a profile say, replaces it. */
  private carryMappingOver(): void {
    if (this.pendingProfileApply || !this.selectedTable) {
      return;
    }
    const built = this.hasMappings || this.lookupDefinitions.length > 0
      || Object.keys(this.blankValues).length > 0;
    const session = built ? this.buildSessionSnapshot() : null;
    if (!session) {
      return;
    }
    this.pendingProfileApply = {...pendingRestore({session}), sourceTable: session.selectedTable};
    this.pendingRestoreIsProfile = false;
  }

  clearFile(): void {
    this.dmsIdentifier = '';
    this.fileName = '';
    this.fileSize = null;
    this.uploadedAt = null;
    this.uploadError = '';
    this.resetDiscovery();
    this.clearTableStats();
    this.clearSessionStash();
  }

  /** Reads the file's tables, unless a read is already running, and
   * selects the only one, or the one a pending restore was saved from.
   * While the media server cannot serve the file yet, asks again after
   * each of `SOURCE_WAIT_DELAYS_MS` until `SOURCE_WAIT_LIMIT_MS` has
   * passed. An answer for a file that is no longer the source is dropped. */
  async inspectSource(): Promise<void> {
    if (!this.dmsIdentifier || this.inspecting) {
      return;
    }
    const seq = ++this.inspectRequestSeq;
    this.inspecting = true;
    this.inspectFailed = false;
    this.waitingForFile = false;
    this.sourceError = null;
    try {
      const res = await this.inspectWhenReadable(seq);
      if (seq !== this.inspectRequestSeq) {
        return;  // another file since
      }
      this.waitingForFile = false;
      if (!res) {
        this.sourceError = this.translate.instant('TRANS__IMPORT__ERROR__SOURCE_NOT_READY');
        this.inspectFailed = true;
        return;
      }
      this.tables = res.tables;
      void this.loadTableStats();
      if (this.tables.length === 1) {
        // Single-table file: inspect already carried this table's columns,
        // so selectTable skips a second /source/columns round trip.
        await this.selectTable(this.tables[0], res.column_preview);
      } else if (this.tables.includes(this.pendingProfileApply?.sourceTable ?? '')) {
        // Multi-table file holding the table the pending profile was saved
        // from: select it. Without a match the operator picks by hand.
        await this.selectTable(this.pendingProfileApply!.sourceTable!);
      }
    } catch (e: any) {
      if (seq !== this.inspectRequestSeq) {
        return;
      }
      this.logger.error(`raw import: source inspect failed: ${e}`);
      this.waitingForFile = false;
      this.sourceError = this.errorText(e, 'TRANS__IMPORT__ERROR__INSPECT_SOURCE');
      this.inspectFailed = true;
    } finally {
      if (seq === this.inspectRequestSeq) {
        this.inspecting = false;
      }
    }
  }

  /** The current file's `/source/inspect` answer, asked for again after
   * a pause while the media server cannot serve the file yet; `null` once
   * `SOURCE_WAIT_LIMIT_MS` has passed without it, or when read `seq` is
   * superseded meanwhile. Any other failure rejects. */
  private async inspectWhenReadable(seq: number): Promise<SourceInspectResult | null> {
    const started = Date.now();
    for (let attempt = 0; ; attempt++) {
      try {
        return await this.inspectCurrentSource();
      } catch (e) {
        if (seq !== this.inspectRequestSeq || !isSourceNotReady(e)) {
          throw e;
        }
        if (Date.now() - started >= SOURCE_WAIT_LIMIT_MS) {
          this.logger.error(`raw import: ${this.fileName} is still not readable from the media server`);
          return null;
        }
      }
      this.waitingForFile = true;
      await this.sourceWait(SOURCE_WAIT_DELAYS_MS[Math.min(attempt, SOURCE_WAIT_DELAYS_MS.length - 1)]);
      if (seq !== this.inspectRequestSeq) {
        return null;
      }
    }
  }

  /** Resolves after `ms`, or as soon as `supersedeInspect` ends the pause. */
  private sourceWait(ms: number): Promise<void> {
    return new Promise(resolve => {
      const timer = setTimeout(() => {
        this.endSourceWait = null;
        resolve();
      }, ms);
      this.endSourceWait = () => {
        clearTimeout(timer);
        this.endSourceWait = null;
        resolve();
      };
    });
  }

  /** Drops a read of the tables that is running or waiting for the file:
   * it asks nothing more and its answer no longer applies. */
  private supersedeInspect(): void {
    this.inspectRequestSeq++;
    this.inspecting = false;
    this.waitingForFile = false;
    this.endSourceWait?.();
  }

  /** `errorText` for a request that reads the source file, a file the
   * media server cannot serve yet said as the page's own words. */
  private sourceErrorText(e: any, fallbackKey: string): string {
    return isSourceNotReady(e)
      ? this.translate.instant('TRANS__IMPORT__ERROR__SOURCE_NOT_READY')
      : this.errorText(e, fallbackKey);
  }

  /** Asks for the current file's table counts, once per file. The answer
   * applies only while that file is still the source. */
  private async loadTableStats(): Promise<void> {
    const file = JSON.stringify([this.dmsIdentifier, this.fileName]);
    if (this.tableStatsFile === file) {
      return;
    }
    this.tableStatsFile = file;
    this.tableStats = new Map();
    try {
      const res = await this.cms.importModuleTableStats({
        dms_identifier: this.dmsIdentifier,
        file_name: this.fileName,
      });
      if (this.tableStatsFile === file) {
        this.tableStats = new Map(res.tables.map(table => [table.name, table]));
      }
    } catch (e) {
      if (this.tableStatsFile === file) {
        this.tableStatsFile = null;  // the next inspect asks again
      }
      this.logger.error(`raw import: could not count the source tables: ${e}`);
    }
  }

  private clearTableStats(): void {
    this.tableStatsFile = null;
    this.tableStats = new Map();
  }

  /** The current file's `/source/inspect` answer: the resume check's when
   * it was for this file, asked of the backend otherwise. */
  private inspectCurrentSource(): Promise<SourceInspectResult> {
    const checked = this.resumeInspect;
    this.resumeInspect = null;
    if (checked && checked.dmsIdentifier === this.dmsIdentifier && checked.fileName === this.fileName) {
      return Promise.resolve(checked.result);
    }
    return this.cms.importModuleInspectSource({
      dms_identifier: this.dmsIdentifier,
      file_name: this.fileName,
    });
  }

  /** Selects `table` and loads its columns and the object type's target
   * fields, the mapping starting over. `columnPreview` is the table's
   * columns when inspect already carried them. A pending restore lands
   * once both have loaded (`applyPendingWhenReady`). */
  async selectTable(
    table: string, columnPreview?: { columns: SourceColumn[]; first_rows?: ImportModuleSourceRow[] },
  ): Promise<void> {
    this.selectedTable = table;
    void this.loadColumnProfile();
    this.firstRows = [];
    // The dropped rows', group cards' and lookups' subscriptions close here,
    // not only once the columns load: a load that fails or never lands
    // would leave them emitting `mappingChanged`. The media reset is
    // silent: the stash is written once the table's columns load.
    this.startMappingOver(false);
    this.applyToken++;
    await Promise.all([this.loadColumns(columnPreview), this.loadTargetFields()]);
  }

  /** Add a mapping row, optionally pre-filled to `{column}`, as the
   * opened line. */
  addAssignment(column?: string): Assignment {
    const assignment = this.newAssignment(column);
    this.openLine(assignment);
    return assignment;
  }

  /** Add a mapping row onto `field`, with no source yet, as the opened
   * line. */
  addTargetField(field: TargetField): Assignment {
    const assignment = this.newAssignment();
    assignment.targetFieldControl.setValue(field);
    this.openLine(assignment);
    return assignment;
  }

  /** Add a row onto `field`, a field of the linked media, with no source
   * yet, as the opened line. */
  addMediaTargetField(field: TargetField): Assignment {
    const assignment = this.newAssignment(undefined, true);
    assignment.targetFieldControl.setValue(field);
    this.openLine(assignment);
    return assignment;
  }

  /** A new mapping row at the end of the list, optionally pre-filled to
   * `{column}`, its change tracking wired; with `onMedia`, a row of
   * `mediaAssignments`, which picks from the fields of the linked
   * media. */
  private newAssignment(column?: string, onMedia = false): Assignment {
    const picker = this.newTargetFieldPicker(onMedia ? this.mediaTargetFields : this.targetOptions, () => {
      this.resetConceptRoots(assignment);
      if (this.openedLine !== assignment) {
        this.placeRow(assignment);
      }
    });
    const valueSource = createValueSourceControls(column ? `{${column}}` : '');
    this.registerGroupControls([valueSource.templateControl, ...lookupControlList(valueSource)]);
    this.watchLookupRef(valueSource);
    const conceptRootControl = new FormControl<string | null>(null);
    const assignment = Object.assign(picker, {
      id: this.nextAssignmentId++,
      placedField: null,
      ...valueSource,
      fixedValue: false,
      conceptRootControl,
      conceptRootOptions: [] as ConceptOption[],
    }) as Assignment;
    this.rowSubscriptions.add(
      conceptRootControl.valueChanges.subscribe(() => this.mappingChanged.next()));
    this.rowSubscriptions.add(assignment.composeControl.valueChanges.subscribe(on => {
      // Carry a flat template forward into compose mode only when it's an
      // unambiguous 1:1 mapping (a single bare {column}, nothing else) -
      // anything with surrounding text or more than one column has no
      // lossless equivalent as a single compose-field line, so it's left
      // as-is on templateControl rather than guessed at.
      if (on && !assignment.composeRows.length) {
        const column = bareTemplateColumn(assignment.templateControl.value, this.columnNames);
        if (column) {
          this.addComposeLine(assignment);
          assignment.composeRows[assignment.composeRows.length - 1].columnControl.setValue(column);
        }
      }
      this.mappingChanged.next();
    }));
    if (onMedia) {
      this.mediaAssignments = [...this.mediaAssignments, assignment];
    } else {
      this.assignments = [...this.assignments, assignment];
    }
    this.mappingChanged.next();
    return assignment;
  }

  isFreeTextField(field: TargetField | null | undefined): boolean {
    return field?.input_type === 'input' || field?.input_type === 'text_area';
  }


  /** Append a line to a compose-mode assignment or group-card member row -
   * blank, or already on `column`. */
  addComposeLine(host: ComposeHost, column: string | null = null): void {
    const row = createComposeLineRow(this.nextComposeRowId++, column);
    this.registerGroupControls(composeLineRowControls(row));
    host.composeRows = [...host.composeRows, row];
    this.mappingChanged.next();
  }

  removeComposeLine(host: ComposeHost, row: ComposeLineRow): void {
    host.composeRows = host.composeRows.filter(r => r !== row);
    this.mappingChanged.next();
  }

  reorderComposeLines(host: ComposeHost, rows: ComposeLineRow[]): void {
    host.composeRows = rows;
    this.mappingChanged.next();
  }

  /** On a target-field change: clears `assignment`'s root pick and
   * options, so a row never shows a stale previous field's roots while the
   * new ones are in flight, and fetches the new field's roots while the
   * row is the opened line. A collapsed row shows no picker, and fetches
   * them once it is opened. */
  private resetConceptRoots(assignment: Assignment): void {
    assignment.conceptRootControl.setValue(null);
    assignment.conceptRootOptions = [];
    if (this.openedLine === assignment) {
      void this.loadConceptRoots(assignment);
    }
  }

  /** Fill `assignment`'s root-scope options for the `concept_root_id`
   * picker, leaving its pick as it is. Left empty (picker hidden) for a
   * concept list with one root or none, since there's nothing to scope by
   * there. A concept-list field is a `map_id` input, but so is any other
   * id reference (a named party, a place), so `map_id` only decides
   * whether asking is worthwhile; an empty answer is what says the field
   * has no roots to offer. A row onto a field of the linked media has
   * none: such a field cannot be a list field. */
  private async loadConceptRoots(assignment: Assignment): Promise<void> {
    const field = assignment.targetField;
    const objectType = this.selectedObjectType;
    if (!field || field.input_type !== 'map_id' || !objectType || this.mediaAssignments.includes(assignment)) {
      return;
    }
    const token = this.applyToken;
    try {
      const roots = await this.conceptRoots(objectType.superobject_type_id, field.field_id);
      if (token !== this.applyToken || assignment.targetField !== field) {
        return;  // superseded by a page reset, or another field picked meanwhile
      }
      assignment.conceptRootOptions = roots.length > 1 ? roots : [];
    } catch (e: any) {
      if (token !== this.applyToken || assignment.targetField !== field) {
        return;
      }
      this.logger.error(`raw import: could not load concept roots for ${field.field_id}: ${e}`);
    }
  }

  /** The root terms of `fieldId` on `objectType`, asked of the backend
   * once per pair until `resetTargetDiscovery`. */
  private conceptRoots(objectType: string, fieldId: string): Promise<ConceptOption[]> {
    const key = JSON.stringify([objectType, fieldId]);
    return sharedRequest(this.conceptRootsCache, key, () =>
      this.cms.importModuleConceptRoots({field_id: fieldId, object_type: objectType}).then(res => res.roots));
  }

  /** Load (and cache) the columns of a non-data table for its lookup selects. */
  private async loadLookupColumns(table: string): Promise<void> {
    if (this.lookupColumns[table] || this.lookupColumnsLoading.has(table)) {
      return;
    }
    const seq = ++this.lookupColumnsSeq;
    const isLatest = () => this.lookupColumnsLoading.has(table) && this.lookupColumnsLoads.get(table) === seq;
    this.lookupColumnsLoads.set(table, seq);
    this.lookupColumnsLoading.add(table);
    this.lookupColumnsFailed.delete(table);
    try {
      const res = await this.cms.importModuleSourceColumns({
        dms_identifier: this.dmsIdentifier,
        file_name: this.fileName,
        table,
      });
      if (isLatest()) {
        this.lookupColumns = {...this.lookupColumns, [table]: res.columns};
      }
    } catch (e: any) {
      if (isLatest()) {
        this.logger.error(`raw import: could not load lookup columns for ${table}: ${e}`);
        this.sourceError = this.sourceErrorText(e, 'TRANS__IMPORT__ERROR__SOURCE_COLUMNS');
        this.lookupColumnsFailed.set(table, this.sourceError);
      }
    } finally {
      if (isLatest()) {
        this.lookupColumnsLoading.delete(table);
      }
      // The check waits for a lookup's table columns.
      if (!this.destroyed) this.scheduleLookupCheck();
    }
  }

  removeAssignment(assignment: Assignment): void {
    this.assignments = this.assignments.filter(other => other !== assignment);
    this.mediaAssignments = this.mediaAssignments.filter(other => other !== assignment);
    this.closeLine(assignment);
    this.mappingChanged.next();
  }

  /** Shows `line` with its controls and collapses the line opened before
   * it, which moves to where its field places it. A flat row fetches its
   * target field's root terms here. */
  openLine(line: MappingLine): void {
    const previous = this.openedLine;
    this.openedLine = line;
    if (previous && previous !== line && 'targetFieldControl' in previous) {
      this.placeRow(previous);
    }
    if ('targetFieldControl' in line) {
      void this.loadConceptRoots(line);
    }
  }

  /** Collapses `line` when it is the opened one; a row moves to where its
   * field places it. */
  closeLine(line: MappingLine): void {
    if (this.openedLine === line) {
      this.openedLine = null;
      if ('targetFieldControl' in line) {
        this.placeRow(line);
      }
    }
  }

  /** Places `row` by its picked field, when it has one other than the
   * field it is placed by. Page state only: no request body reads it. */
  private placeRow(row: Assignment): void {
    if (row.targetField && row.targetField !== row.placedField) {
      row.placedField = row.targetField;
      this.placementVersion++;
    }
  }

  /** The unknown placeholders a row warns of, on Kolonne only: a fixed
   * value's braces are its text, and Kodeoppslag and Sammensatt pick
   * their columns from lists. */
  placeholderWarning(assignment: Assignment): string[] {
    return this.sourceKind(assignment) === 'column' ? this.unknownPlaceholders(assignment) : [];
  }

  /** What the row's collapsed line marks it incomplete for, `null` for
   * nothing: a Kolonne template naming a column the source lacks; a Fast
   * verdi template that still reads a column - one restored from literal
   * text whose `{…}` has become a column name; a Kodeoppslag without a
   * complete lookup and a value column, or a Sammensatt without a line
   * with a column, either of which sends nothing. Built once per
   * `mappingVersion` and choice. */
  lineIssue(assignment: Assignment): LineIssue | null {
    const row = this.rowPreview(assignment);
    const kind = this.sourceKind(assignment);
    if (row.issue?.kind !== kind) {
      row.issue = {kind, issue: this.findLineIssue(assignment, kind)};
    }
    return row.issue.issue;
  }

  private findLineIssue(assignment: Assignment, kind: ValueSourceKind): LineIssue | null {
    if (kind === 'column') {
      return this.unknownPlaceholders(assignment).length ? {key: 'TRANS__IMPORT__UNKNOWN_PLACEHOLDER'} : null;
    }
    if (kind === 'constant') {
      const columns = templateColumns(assignment.templateControl.value, this.columnNames);
      return columns.length
        ? {key: 'TRANS__IMPORT__VALUE_SOURCE__FIXED_READS_COLUMN', params: {columns: columns.join(', ')}}
        : null;
    }
    return valueSourceIssue(assignment, kind, this.lookupDefinitions);
  }

  /** `{Placeholder}` names in the template that don't match any column,
   * found once per `mappingVersion`. */
  unknownPlaceholders(assignment: Assignment): string[] {
    const row = this.rowPreview(assignment);
    row.unknownPlaceholders ??= unknownPlaceholderNames(assignment.templateControl.value, this.columnNames);
    return row.unknownPlaceholders;
  }

  /** The row's "Hentes fra" choice. */
  sourceKind(assignment: Assignment): ValueSourceKind {
    return flatValueSourceKind(assignment);
  }

  /** Switches the row to `kind` (`setFlatValueSourceKind`). A switch to
   * Sammensatt carries a bare `{column}` template over (see
   * `newAssignment`). */
  setSourceKind(assignment: Assignment, kind: ValueSourceKind): void {
    setFlatValueSourceKind(assignment, kind, this.columnNames);
  }

  /** What the collapsed row shows as its source (`valueSourceSummary`),
   * built once per `mappingVersion` and choice. */
  sourceSummary(assignment: Assignment): SourcePiece[] {
    const row = this.rowPreview(assignment);
    const kind = this.sourceKind(assignment);
    if (row.source?.kind !== kind) {
      row.source = {
        kind, pieces: valueSourceSummary(assignment, kind, this.columnNames, this.lookupDefinitions),
      };
    }
    return row.source.pieces;
  }

  private rowPreview(assignment: Assignment): RowPreview {
    const previews = this.derived().rowPreviews;
    let row = previews.get(assignment);
    if (!row) {
      row = {};
      previews.set(assignment, row);
    }
    return row;
  }

  /** Loads the selected table's column profile, from the session's
   * profiles when this file and table were asked for already. Call again
   * after a failure to retry. */
  async loadColumnProfile(): Promise<void> {
    const seq = ++this.columnProfileSeq;
    this.columnProfile = null;
    this.columnProfileFailed = false;
    this.columnProfileLoading = !!this.selectedTable;
    if (!this.selectedTable) {
      return;
    }
    const params = {dms_identifier: this.dmsIdentifier, file_name: this.fileName, table: this.selectedTable};
    const key = JSON.stringify([params.dms_identifier, params.file_name, params.table]);
    const request = sharedRequest(
      this.columnProfiles, key, () => this.cms.importModuleColumnProfile(params).then(sourceColumnProfile));
    try {
      const profile = await request;
      if (seq === this.columnProfileSeq) {
        this.columnProfile = profile;
      }
    } catch (e) {
      if (seq === this.columnProfileSeq) {
        this.columnProfileFailed = true;
        this.logger.error(`raw import: could not profile the columns of ${params.table}: ${e}`);
      }
    } finally {
      if (seq === this.columnProfileSeq) {
        this.columnProfileLoading = false;
      }
    }
  }

  /** Leaves no profile shown; an answer on its way no longer applies. */
  private clearColumnProfile(): void {
    this.columnProfileSeq++;
    this.columnProfile = null;
    this.columnProfileLoading = false;
    this.columnProfileFailed = false;
  }

  /** Loads the selected table's columns and first rows, from
   * `columnPreview` when inspect already carried them. `false` when another
   * table was picked, or discovery was reset, before they arrived, or when
   * the load failed: nothing was applied then. A failed load leaves no
   * columns shown. */
  private async loadColumns(
    columnPreview?: { columns: SourceColumn[]; first_rows?: ImportModuleSourceRow[] },
  ): Promise<boolean> {
    const seq = ++this.columnsRequestSeq;
    this.columnsReady = false;
    this.columnsFailed = false;
    if (!this.selectedTable) {
      this.loadingColumns = false;
      return false;
    }
    this.loadingColumns = true;
    this.sourceError = null;
    try {
      const columnsRes = await (columnPreview ?? this.cms.importModuleSourceColumns({
        dms_identifier: this.dmsIdentifier,
        file_name: this.fileName,
        table: this.selectedTable,
      }));
      if (seq !== this.columnsRequestSeq) {
        return false;  // another table or file since
      }
      this.firstRows = columnsRes.first_rows ?? [];
      this.setColumns(columnsRes.columns);
      this.columnsReady = true;
      this.applyPendingWhenReady();
      return true;
    } catch (e: any) {
      if (seq !== this.columnsRequestSeq) {
        return false;
      }
      this.logger.error(`raw import: could not load columns: ${e}`);
      this.sourceError = this.sourceErrorText(e, 'TRANS__IMPORT__ERROR__SOURCE_COLUMNS');
      this.columnsFailed = true;
      this.columns = [];
      this.columnNames = new Set();
      this.mappingVersion++;
      return false;
    } finally {
      if (seq === this.columnsRequestSeq) {
        this.loadingColumns = false;
      }
    }
  }

  /** Loads the selected object type's target fields, with GenericEvent's,
   * PlacementEvent's and Image's own, once per object type: a load already done or
   * on its way is shared. `false` when no object type is selected, when
   * the object type changed before they arrived, or when the load failed. */
  private loadTargetFields(): Promise<boolean> {
    const objectType = this.selectedObjectType;
    const load = this.targetFieldsLoad;
    if (objectType && load?.objectType === objectType.superobject_type_id) {
      return load.loaded;
    }
    const seq = ++this.targetFieldsRequestSeq;
    this.targetFieldsReady = false;
    if (!objectType) {
      this.targetFieldsLoad = null;
      this.loadingTargetFields = false;
      return Promise.resolve(false);
    }
    const loaded = this.requestTargetFields(objectType, seq);
    this.targetFieldsLoad = {objectType: objectType.superobject_type_id, loaded};
    return loaded;
  }

  private async requestTargetFields(objectType: ImportModuleObjectType, seq: number): Promise<boolean> {
    this.loadingTargetFields = true;
    this.targetFieldsFailed = false;
    this.clearSourceError();
    try {
      const [
        targetFieldsRes, adminEventTargetFieldsRes, placementEventTargetFieldsRes, mediaTargetFieldsRes,
      ] = await Promise.all([
        this.cms.importModuleTargetFields({object_type: objectType.superobject_type_id}),
        // GenericEvent's own fields (its Name) - a SEPARATE call scoped to
        // its own superobject_type_id, independent of the row's object
        // type (see `adminEventTargetFields`).
        this.adminEventKindTargetFields(ObjectTypes.GENERIC_EVENT),
        this.adminEventKindTargetFields(PLACEMENT_EVENT_KIND),
        // Only the fields a linked media object takes: `for_media` leaves
        // out the list fields the backend refuses there.
        this.mediaTargetFieldsRequest(),
      ]);
      if (seq !== this.targetFieldsRequestSeq) {
        return false;  // another object type, table or file since
      }
      this.canOverrideCreatedRevision = targetFieldsRes.can_override_created_revision === true;
      this.setTargetFields(
        targetFieldsRes.target_fields, targetFieldsRes.card_kinds ?? [],
        targetFieldsRes.special_targets ?? {}, adminEventTargetFieldsRes.target_fields,
        placementEventTargetFieldsRes.target_fields, mediaTargetFieldsRes.target_fields);
      this.targetFieldsReady = true;
      this.applyPendingWhenReady();
      return true;
    } catch (e: any) {
      if (seq !== this.targetFieldsRequestSeq) {
        return false;
      }
      this.targetFieldsLoad = null;  // the next table pick asks again
      this.logger.error(`raw import: could not load target fields: ${e}`);
      this.sourceError = this.errorText(e, 'TRANS__IMPORT__ERROR__TARGET_FIELDS');
      this.targetFieldsFailed = true;
      return false;
    } finally {
      if (seq === this.targetFieldsRequestSeq) {
        this.loadingTargetFields = false;
      }
    }
  }

  /** Loads the selected table's columns and the target fields again,
   * after either failed. */
  retrySelectedTable(): Promise<void> {
    return this.selectTable(this.selectedTable);
  }

  /** Clears `sourceError`, unless it tells of a failed read of the file's
   * tables or the selected table's columns: that one stays until the read
   * is tried again. */
  private clearSourceError(): void {
    if (!this.inspectFailed && !this.columnsFailed) {
      this.sourceError = null;
    }
  }

  /** Applies a pending restore once the selected table's columns and the
   * object type's target fields have both loaded, whichever landed last,
   * and stashes the page. */
  private applyPendingWhenReady(): void {
    if (!this.columnsReady || !this.targetFieldsReady) {
      return;
    }
    this.applyPendingProfile();
    this.stashSourceToSession();
  }

  private setColumns(columns: SourceColumn[]): void {
    this.columns = columns;
    this.lookupColumns = {};
    this.lookupColumnsLoading.clear();
    this.lookupColumnsFailed.clear();
    // A lookup added while the columns were on their way keeps its table,
    // whose columns its key column is picked from.
    const lookupTables = new Set(this.lookupDefinitions
      .map(lookup => lookup.tableControl.value)
      .filter((table): table is string => !!table));
    for (const table of lookupTables) {
      void this.loadLookupColumns(table);
    }
    this.columnNames = new Set(columns.map(c => c.name));
    this.clearMappingLines();
  }

  /** The `/target_fields` answer for the members of the admin-event kind
   * `kindKey`; none while `GET /object_types` lists no such kind. */
  private adminEventKindTargetFields(kindKey: string): Promise<ImportModuleTargetFields> {
    const kind = this.adminEventKinds.find(k => k.object_type === kindKey);
    return kind
      ? this.cms.importModuleTargetFields({object_type: kind.superobject_type_id})
      : Promise.resolve({target_fields: []});
  }

  /** The `/target_fields` answer for the fields a linked image takes; none
   * while `GET /object_types` names no Image type. */
  private mediaTargetFieldsRequest(): Promise<ImportModuleTargetFields> {
    return this.imageSuperobjectTypeId
      ? this.cms.importModuleTargetFields({object_type: this.imageSuperobjectTypeId, for_media: true})
      : Promise.resolve({target_fields: []});
  }

  private setTargetFields(
    targetFields: TargetField[], cardKinds: ImportModuleCardKind[],
    specialTargets: { [target: string]: ImportModuleFormPlacement },
    adminEventTargetFields: TargetField[], placementEventTargetFields: TargetField[],
    mediaTargetFields: TargetField[],
  ): void {
    this.allTargetFields = targetFields;
    this.cardKindPlacements = new Map(cardKinds.map(cardKind => [cardKind.object_type, cardKind]));
    this.specialTargetPlacements = specialTargets;
    this.otherFieldsPlacement = otherFieldsPlacement(
      [...targetFields, ...cardKinds, ...Object.values(specialTargets)]);
    this.adminEventTargetFields = adminEventTargetFields;
    this.placementEventTargetFields = placementEventTargetFields;
    this.mediaTargetFields = mediaTargetFields;
    // Group-owned fields (history-event, groupable) live only in their
    // group card, never the flat picker.
    this.targetOptions = [
      {
        field_id: IDENTIFICATION_NUMBER_TARGET,
        label: this.translate.instant('TRANS__IMPORT__TARGET__IDENTIFICATION_NUMBER'),
        input_type: 'text',
      },
      ...(this.canOverrideCreatedRevision ? [
        {
          field_id: CREATED_AT_TARGET,
          label: this.translate.instant('TRANS__IMPORT__TARGET__CREATED_AT'),
          input_type: 'text',
        },
        {
          field_id: CREATED_BY_TARGET,
          label: this.translate.instant('TRANS__IMPORT__TARGET__CREATED_BY'),
          input_type: 'text',
        },
      ] : []),
      ...targetFields.filter(f => !f.history_event && !f.groupable),
    ];
    this.clearMappingLines();
  }

  /** Drops every mapping line and group card with their control
   * subscriptions: a mapping is built on one table's columns and one
   * object type's target fields. */
  private clearMappingLines(): void {
    this.rowSubscriptions.unsubscribe();
    this.rowSubscriptions = new Subscription();
    this.assignments = [];
    this.mediaAssignments = [];
    this.openedLine = null;
    this.resetGroupModels();
    this.rebuildGroupDescriptors();
  }

  private rebuildGroupDescriptors(): void {
    for (const cfg of this.groupKindConfigs) {
      this.groupDescriptors[cfg.kind] = cfg.buildDescriptor();
    }
    this.descriptorBatchFields = this.batchFields;
    this.mappingVersion++;
  }

  /** Clears every group-card kind's models - a table/source/discovery
   * reset invalidates every group (its descriptor's members come from the
   * just-reset target-field discovery). */
  private resetGroupModels(): void {
    for (const cfg of this.groupKindConfigs) {
      this.groupModels[cfg.kind] = [];
    }
  }

  /** A fresh target-field picker over `options`, wired so its control value
   * keeps `targetField` and the filtered option list in sync. Only a change
   * of `targetField` emits `mappingChanged` and runs `onFieldChange`: text
   * typed to filter by, or the field already picked picked again, just
   * refilters. Registered on the current subscription bag. */
  private newTargetFieldPicker(options: TargetField[], onFieldChange: () => void) {
    const targetFieldControl = new FormControl<string | TargetField | null>(null);
    const picker = {
      targetField: null as TargetField | null,
      targetFieldControl,
      filteredTargetFields: options,
    };
    this.rowSubscriptions.add(targetFieldControl.valueChanges.subscribe(value => {
      const field = value && typeof value === 'object' ? value : null;
      const query = (typeof value === 'string' ? value : '').toLowerCase().trim();
      picker.filteredTargetFields = query
        ? options.filter(option => option.label.toLowerCase().includes(query))
        : options;
      if (field === picker.targetField) {
        return;
      }
      picker.targetField = field;
      this.mappingChanged.next();
      onFieldChange();
    }));
    return picker;
  }

  /**
   * Ask the backend which batch-config concept pickers the current mapping
   * and media mapping need (a mapped title field → title status + language,
   * a history-event field → the event type, …) and build a control per field, keeping any
   * value the operator already picked. Debounced off `mappingChanged`.
   *
   * A body equal to the latest one sent under the same `applyToken` is not
   * sent again, and only the latest request's answer is applied.
   */
  async loadBatchFields(): Promise<void> {
    const objectType = this.selectedObjectType;
    const request = objectType ? batchFieldsRequest({
      ...this.mappingState,
      objectType: objectType.superobject_type_id,
      media: this.mediaJoin,
    }) : null;
    if (!request) {
      this.clearBatchFields();
      // No request goes out, so nothing more reaches the page for a
      // baseline an apply waits for.
      this.settleProfileBaseline(this.batchFieldsRequestSeq, true);
      return;
    }
    // Every edit with a request clears a shown source error, whether or
    // not its body changed.
    this.sourceError = null;
    const body = JSON.stringify(request);
    const token = this.applyToken;
    if (this.batchFieldsSent?.body === body && this.batchFieldsSent.token === token) {
      return;
    }
    const seq = ++this.batchFieldsRequestSeq;
    this.batchFieldsSent = {body, token};
    this.loadingBatchFields = true;
    try {
      const res = await this.cms.importModuleBatchFields(request);
      if (seq !== this.batchFieldsRequestSeq || token !== this.applyToken) {
        return;  // superseded by a newer request or a reset while in flight
      }
      this.setBatchFields(res.fields);
      this.settleProfileBaseline(seq, true);
    } catch (e: any) {
      if (seq !== this.batchFieldsRequestSeq || token !== this.applyToken) {
        return;
      }
      this.settleProfileBaseline(seq, false);
      // The same body is sent again on the next edit.
      this.batchFieldsSent = null;
      this.logger.error(`raw import: could not load batch fields: ${e}`);
      this.sourceError = this.errorText(e, 'TRANS__IMPORT__ERROR__BATCH_FIELDS');
    } finally {
      if (seq === this.batchFieldsRequestSeq) {
        this.loadingBatchFields = false;
      }
    }
  }

  /** Drops every batch field and its control, and supersedes a
   * `/batch_fields` request in flight. */
  private clearBatchFields(): void {
    this.batchFields = [];
    this.batchControls = {};
    for (const subscription of this.batchControlSubscriptions.values()) {
      subscription.unsubscribe();
    }
    this.batchControlSubscriptions.clear();
    this.batchFieldsSent = null;
    this.batchFieldsRequestSeq++;
    this.loadingBatchFields = false;
    this.mappingVersion++;
  }

  /**
   * Applies a `/batch_fields` answer. A key that stays keeps its control
   * and value; a new key's control starts at the loaded profile's value,
   * else the field's default; a dropped key's control goes. An answer
   * equal to the current fields keeps the current list, and the group card
   * descriptors are rebuilt only when the list differs from the one they
   * were built from.
   */
  setBatchFields(fields: BatchField[]): void {
    if (JSON.stringify(fields) !== JSON.stringify(this.batchFields)) {
      this.batchFields = fields;
    }
    const keys = new Set(this.batchFields.map(field => field.key));
    for (const key of Object.keys(this.batchControls)) {
      if (!keys.has(key)) {
        this.dropBatchControl(key);
      }
    }
    for (const field of this.batchFields) {
      if (!this.batchControls[field.key]) {
        this.addBatchControl(field.key, this.profileBatchValues[field.key] ?? field.default ?? null);
      }
    }
    // The group card header option lists come from these same fields,
    // and so do the names the restore warnings give batch values and card
    // headers.
    if (this.descriptorBatchFields !== this.batchFields) {
      this.rebuildGroupDescriptors();
      this.refreshRestoreWarnings();
    }
    this.mappingVersion++;
  }

  private addBatchControl(key: string, value: string | null): void {
    const control = new FormControl<string | null>(value);
    this.batchControlSubscriptions.set(
      key, control.valueChanges.subscribe(() => this.mappingChanged.next()));
    this.batchControls[key] = control;
  }

  private dropBatchControl(key: string): void {
    this.batchControlSubscriptions.get(key)?.unsubscribe();
    this.batchControlSubscriptions.delete(key);
    delete this.batchControls[key];
  }

  /**
   * Scans the whole source table for the distinct values headed to each
   * concept-list target and lists the ones that won't auto-resolve, for
   * the value review. Decisions already made carry over to the fresh
   * scan's rows (`setConceptFields`).
   */
  async scanConceptValues(): Promise<void> {
    const request = this.buildConceptScanRequest();
    const key = JSON.stringify(request);
    if (!request.table || request.mapping.length === 0) {
      this.clearConceptScan();
      this.conceptScanRequestKey = key;
      return;
    }
    this.loadingConceptValues = true;
    this.sourceError = null;
    const token = this.applyToken;
    try {
      const profileId = this.selectedProfileId || undefined;
      const res = await this.cms.importModuleConceptValues({
        ...request,
        // A loaded profile's stored decisions, or those of a profile
        // imported from a file, pre-fill any row they cover.
        profile_id: profileId,
        ...(!profileId && this.carriedDecisions ? {value_decisions: this.carriedDecisions} : {}),
      });
      if (token !== this.applyToken) {
        return;  // superseded by a reset while this request was in flight
      }
      this.setConceptFields(res.fields);
      this.conceptRowsScanned = res.rows_scanned;
      this.conceptScanRequestKey = key;
      // The mapping may have changed while the scan ran.
      this.refreshConceptScanStale();
      this.conceptDecisionsChanged();
    } catch (e: any) {
      if (token !== this.applyToken) {
        return;
      }
      this.logger.error(`raw import: could not scan concept values: ${e}`);
      this.sourceError = this.sourceErrorText(e, 'TRANS__IMPORT__ERROR__CONCEPT_VALUES');
    } finally {
      if (token === this.applyToken) {
        this.loadingConceptValues = false;
      }
    }
  }

  /** What `/concept_values` is asked, less the profile and the decisions
   * sent in its place (see `conceptScanRequest`). */
  buildConceptScanRequest(): ConceptScanRequest {
    return conceptScanRequest({
      ...this.mappingState,
      source: this.sourceFile,
      objectType: this.selectedObjectType?.superobject_type_id,
      blankValues: this.blankValues,
    });
  }

  private refreshConceptScanStale(): void {
    this.conceptScanStale = this.conceptScanRequestKey !== null
      && JSON.stringify(this.buildConceptScanRequest()) !== this.conceptScanRequestKey;
  }

  /** Scans when there is no scan yet or the mapping changed since the
   * last one, unless a scan is running. */
  scanConceptValuesIfOutOfDate(): void {
    this.refreshConceptScanStale();
    if (!this.loadingConceptValues && (this.conceptScanRequestKey === null || this.conceptScanStale)) {
      void this.scanConceptValues();
    }
  }

  /** Forgets the scan. A scan still in flight is superseded by the reset
   * that calls this (`applyToken`), so it is no longer loading either. */
  private clearConceptScan(): void {
    this.conceptFields = [];
    this.loadingConceptValues = false;
    this.conceptRowsScanned = 0;
    this.conceptScanRequestKey = null;
    this.conceptScanStale = false;
    this.valueReviewSummary = NO_VALUE_REVIEW;
  }

  /**
   * Takes a fresh scan's `fields`, keeping what the operator decided on
   * the previous scan's rows: the decision, the new term's name, code and
   * parent, the picked term, and any term found through the review's
   * search, which the scan's own candidates do not hold.
   */
  private setConceptFields(fields: ImportModuleConceptField[]): void {
    const previous = new Map<string, ImportModuleConceptRow>();
    for (const field of this.conceptFields) {
      for (const row of field.review_rows) {
        previous.set(`${field.target_field}::${row.source_value_key}`, row);
      }
    }
    this.conceptFields = fields.map(field => ({
      ...field,
      review_rows: field.review_rows.map(row => {
        const prev = previous.get(`${field.target_field}::${row.source_value_key}`);
        if (!prev) {
          return row;
        }
        const pickedId = prev.candidates.find(c => c.is_selected)?.candidate_artifact_id;
        const scanned = new Set(row.candidates.map(c => c.candidate_artifact_id));
        return {
          ...row,
          decision_kind: prev.decision_kind,
          new_concept_name: prev.new_concept_name,
          new_concept_code: prev.new_concept_code,
          new_concept_parent_id: prev.new_concept_parent_id ?? null,
          // Carried from `prev`, not the fresh scan's row: a profile-seeded
          // id, or one the review cleared, must survive a rescan unchanged.
          resolved_artifact_id: prev.resolved_artifact_id,
          candidates: [
            ...row.candidates,
            ...prev.candidates.filter(c => !scanned.has(c.candidate_artifact_id)),
          ].map(c => ({...c, is_selected: c.candidate_artifact_id === pickedId})),
        };
      }),
    }));
  }

  /** Recounts the entry card's figures from `conceptFields`. */
  private refreshValueReviewSummary(): void {
    const progress: [string, FieldProgress][] = this.conceptFields.map(
      field => [field.target_field, fieldProgress(field)]);
    this.valueReviewSummary = {
      totals: reviewTotals(progress.map(([, p]) => p)),
      undecidedFields: progress
        .filter(([, p]) => p.undecided)
        .map(([fieldId, p]) => ({fieldId, undecided: p.undecided})),
    };
  }

  /**
   * Builds the batch-defaults model for `objectType` and, with a table
   * selected, loads its target fields; the mapping starts over. The file,
   * its tables and the selected table's columns and preview stay.
   *
   * `seed`: initial field values for the freshly built prime-
   * fields model - how a profile restores collection / registration level,
   * which are nested (`collection.collection_id`, not a top-level field) so
   * `ObjectEditService.setMapValues` never resolves their `_value` display
   * label on its own; the profile carries the label itself.
   *
   * Applied AFTER the container is built, via `seedBatchDefaults` - NOT
   * threaded through `createSectionsContainer`'s own `object` (constructor
   * `data`) param. That path recurses into
   * `ModelFactoryService.createSubModelItem` for every inline sub-model
   * field (collection, reg_level, …) and throws for this shape (picking a
   * profile then fails with `TRANS__IMPORT__ERROR__OBJECT_TYPES`). Writing
   * directly to the built `rootObject` + its form controls is what
   * `ObjectEditService.createSectionsContainer` itself does for its
   * `identification_number` / `create_multiple` seeding.
   */
  private async onObjectTypeChange(objectType: string | null, seed?: any): Promise<void> {
    this.setBatchDefaults(null);
    this.resetTargetDiscovery();
    if (!objectType) {
      return;
    }
    this.applyToken++;
    const token = ++this.modelBuildRequestSeq;
    this.buildingModel = true;
    // The file, its tables and the selected table's columns stay; only the
    // target fields are asked for again.
    const targetFieldsLoaded = this.selectedTable ? this.loadTargetFields() : Promise.resolve(false);
    try {
      const built = await this.objectEditService.createSectionsContainer({
        useExistingObject: false,
        objectType,
        usePrimeFields: true,
        primeFieldNames: BATCH_DEFAULT_FIELDS,
      } as CreateSectionsContainerParams);
      if (token !== this.modelBuildRequestSeq) {
        return;  // superseded by a newer object-type change
      }
      if (seed) {
        this.seedBatchDefaults(built, seed);
      }
      this.setBatchDefaults(built);
      this.stashSourceToSession();
    } catch (e) {
      if (token !== this.modelBuildRequestSeq) {
        return;
      }
      this.logger.error(`raw import: could not build the ${objectType} model: ${e}`);
      this.notificationService.addNotification(new AppNotification(
        ['TRANS__IMPORT__ERROR__OBJECT_TYPES'], 'error'));
    } finally {
      if (token === this.modelBuildRequestSeq) {
        this.buildingModel = false;
      }
      await targetFieldsLoaded;
    }
  }

  /** Takes `container` as the batch-defaults view and follows its form,
   * whose collection and registration level gate the mapping step. */
  private setBatchDefaults(container: SectionsContainer | null): void {
    this.batchDefaultsSubscription?.unsubscribe();
    this.batchDefaults = container;
    this.batchDefaultsSubscription = container?.formGroup?.valueChanges?.subscribe(() => {
      if (this.refreshBatchDefaultsComplete()) {
        this.clearPreflightResult();
      }
      this.refreshProfileDiffers();
    });
    this.refreshBatchDefaultsComplete();
  }

  /** Reads the batch defaults into `batchDefaultsComplete`, and whether
   * their collection, registration level or status differ from the last
   * read. */
  private refreshBatchDefaultsComplete(): boolean {
    const defaults = this.readBatchDefaults();
    this.batchDefaultsComplete = !!defaults?.collectionId && !!defaults.regLevelId;
    const picks = defaults ? [defaults.collectionId, defaults.regLevelId, defaults.statusId].join('\n') : null;
    const changed = picks !== this.batchDefaultPicks;
    this.batchDefaultPicks = picks;
    return changed;
  }

  /** Writes `seed` (`{parentFieldName: {leafFieldName: value, ...}, ...}`)
   * directly onto a just-built batch-defaults container - both the
   * `rootObject` (what `run()` / `saveProfile()` read back out) and the
   * matching `parent->leaf`-keyed form control (what the rendered
   * `<app-edit-prime-fields>` picker actually shows), so the UI and the
   * underlying model agree. A `*_value` key (a display-label companion,
   * e.g. `collection_id_value`) is written to `rootObject` only - there is
   * no separate form control for it, only the id field is user-editable. */
  private seedBatchDefaults(container: SectionsContainer, seed: {[parent: string]: {[leaf: string]: any}}): void {
    const root = container.rootObject as any;
    for (const [parent, values] of Object.entries(seed)) {
      if (!root[parent]) {
        continue;  // this object type has no such prime field - nothing to seed
      }
      Object.assign(root[parent], values);
      for (const leaf of Object.keys(values)) {
        if (leaf.endsWith('_value')) {
          continue;
        }
        container.formGroup?.controls?.[`${parent}${System.keySep}${leaf}`]?.setValue(values[leaf]);
      }
    }
  }

  // === reusable profiles ======================================

  async loadProfiles(): Promise<void> {
    try {
      const res = await this.cms.importModuleProfiles();
      this.profiles = res.profiles;
    } catch (e) {
      this.logger.error(`raw import: could not load profiles: ${e}`);
    }
  }

  /** Fetch and apply the picked profile. Loads the full stored
   * configuration then hands it to `applyProfile`. */
  async selectProfile(profileId: string | null): Promise<void> {
    if (!profileId) {
      // "Ingen" - clears which profile is tracked (so delete hides, and the
      // same profile can be re-picked afterward to force a reload) without
      // touching anything already applied from it (object type, mapping, …).
      // A load or import still in flight is dropped.
      this.cancelProfileLoad();
      this.selectedProfileId = null;
      this.profileApplyError = null;
      this.stashSourceToSession();
      return;
    }
    this.selectedProfileId = profileId;
    const token = ++this.profileLoadSeq;
    // A different profile picked, or anything else that moves the sequence
    // on, replaces this load.
    const isCurrent = () => token === this.profileLoadSeq && this.selectedProfileId === profileId;
    this.carriedProfileName = null;
    this.applyingProfile = true;
    this.profileApplyError = null;
    try {
      const res = await this.cms.importModuleProfile(profileId);
      if (!isCurrent()) {
        return;
      }
      await this.applyProfile(res.profile, {isCurrent});
    } catch (e: any) {
      if (!isCurrent()) {
        return;
      }
      this.logger.error(`raw import: could not load profile ${profileId}: ${e}`);
      this.profileApplyError = this.errorText(e, 'TRANS__IMPORT__PROFILE__ERROR__LOAD');
    } finally {
      if (isCurrent()) {
        this.applyingProfile = false;
      }
    }
  }

  /** Drops the profile load or import in flight, if any: it stops at its
   * next check of `profileLoadSeq`, and the page is no longer busy with
   * it. */
  private cancelProfileLoad(): void {
    this.profileLoadSeq++;
    this.applyingProfile = false;
  }

  /**
   * The early half of applying a profile: everything that
   * does not need discovered columns / target fields - object type,
   * collection, registration level, overwrite, and media (join column
   * excepted - see below). The mapping / group cards / batch config are
   * stashed onto `pendingProfileApply`, applied once a table's columns and
   * the object type's target fields exist to reconcile them against
   * (`applyPendingWhenReady`).
   *
   * `mediaJoinColumnControl` / `mediaOnMissingControl` are, despite being
   * "media settings", also late: both `onObjectTypeChange` below and
   * `selectTable` unconditionally reset them, so setting them here would
   * just be wiped a moment later.
   *
   * `isCurrent` tells whether the load or import this apply belongs to is
   * still the current one (`profileLoadSeq`); it is checked after each
   * await, and a run that has lost bails rather than clobbering the newer
   * one's state. `carried` is set for a profile imported from a file.
   */
  private async applyProfile(
    profile: ImportModuleProfile,
    {isCurrent, carried}: {isCurrent: () => boolean; carried?: Pick<CarriedProfile, 'blanked' | 'droppedDecisions'>},
  ): Promise<void> {
    this.clearRestoreWarnings();
    this.dropPendingRestore();

    const proceeded = await this.applyEarlyConfig({
      objectType: profile.object_type,
      collectionId: profile.collection_id, collectionLabel: profile.collection_label,
      regLevelId: profile.reg_level_id, regLevelLabel: profile.reg_level_label,
      statusId: profile.status_id, statusLabel: profile.status_label,
      publish: profile.publish,
      overwriteExisting: profile.overwrite_existing,
      media: profile.media,
    }, isCurrent);
    if (!proceeded) {
      return;
    }

    this.pendingProfileApply = {...pendingRestore({profile}), carried};
    this.pendingRestoreIsProfile = true;
    this.profileDecisions = storedDecisionRows(profile.value_decisions);
    this.carriedDecisions = carried ? profile.value_decisions ?? [] : null;

    await this.showPendingProfile();
  }

  /** Brings the pending profile onto the file once it is uploaded: reads
   * its tables when none are listed (which selects the profile's own
   * table), selects the profile's table - or a single-table file's only
   * one - when it is not the one selected, and otherwise applies it to the
   * selected table once its columns and the target fields are there. A
   * multi-table file without the profile's table has its selection
   * cleared, and the profile waits for a table picked by hand. */
  private async showPendingProfile(): Promise<void> {
    if (!this.dmsIdentifier || this.uploading) {
      // The upload's own read of the tables takes the profile.
      return;
    }
    if (!this.tables.length) {
      // A read already running selects the profile's table when it lands.
      await this.inspectSource();
      return;
    }
    const table = this.tables.length === 1 ? this.tables[0] : this.pendingProfileApply?.sourceTable;
    if (!table || !this.tables.includes(table)) {
      this.clearSelectedTable();
    } else if (table !== this.selectedTable) {
      await this.selectTable(table);
    } else {
      this.applyPendingWhenReady();
    }
  }

  /** Forgets a restore still waiting for its table's columns or target
   * fields, so that none lands while a newer one's object type loads. */
  private dropPendingRestore(): void {
    this.pendingProfileApply = null;
    this.pendingRestoreIsProfile = false;
  }

  /**
   * The early half of restoring a full configuration - object type,
   * collection, registration level, overwrite, and media-linking-on -
   * shared by a saved profile (`applyProfile`) and a resumed session
   * (`resumeSession`), which differ only in where the config comes from
   * and what "still current" means for their own async chain. The
   * mapping / group cards / batch config are the caller's own concern:
   * stash them onto `pendingProfileApply` once this resolves.
   *
   * `config.objectType` is a superobject_type_id (what `/run` and a saved
   * profile use); `objectTypeControl` takes the model-name string
   * (`ImportModuleObjectType.object_type`) instead, so this resolves one back to
   * the other rather than feeding the id straight through.
   *
   * Returns whether the caller should proceed: `false` means either the
   * object type no longer resolves (a warning is pushed first) or
   * `isCurrent()` failed partway through - re-checked after every await
   * since a newer pick (a different profile, a second resume) may have
   * superseded this one while it was in flight.
   */
  private async applyEarlyConfig(
    config: Pick<SessionSnapshot, 'objectType' | 'collectionId' | 'collectionLabel' | 'regLevelId' | 'regLevelLabel'
      | 'statusId' | 'statusLabel' | 'overwriteExisting' | 'media'> & {publish?: boolean},
    isCurrent: () => boolean,
  ): Promise<boolean> {
    this.overwriteExistingControl.setValue(config.overwriteExisting);
    // A profile or session stash without `publish` reads as off, never as
    // "keep whatever is ticked".
    this.publishControl.setValue(config.publish ?? false);

    if (config.media) {
      this.linkMediaControl.setValue(true);
      // The media folder itself is deliberately never restored (even from a
      // save that carries a folder_id) - which folder holds this batch's
      // files changes from import to import, so the operator picks it fresh
      // every time rather than it silently pre-filling a stale one.
      await this.loadMediaFolders();
      if (!isCurrent()) {
        return false;
      }
    } else {
      this.linkMediaControl.setValue(false);
    }

    const seed: any = {};
    if (config.collectionId) {
      seed.collection = {collection_id: config.collectionId, collection_id_value: config.collectionLabel};
    }
    if (config.regLevelId) {
      seed.reg_level = {
        registration_level_id: config.regLevelId, registration_level_id_value: config.regLevelLabel,
      };
    }
    if (config.statusId) {
      seed.object_status = {
        object_status_type_id: config.statusId, object_status_type_id_value: config.statusLabel,
      };
    }

    await this.objectTypesLoaded;
    if (!isCurrent()) {
      return false;
    }
    const objectTypeChoice = this.objectTypes.find(t => t.superobject_type_id === config.objectType);
    if (!objectTypeChoice) {
      this.profileRestoreWarnings.push(
        this.translate.instant('TRANS__IMPORT__PROFILE__RESTORE__OBJECT_TYPE_MISSING'));
      return false;
    }
    this.objectTypeControl.setValue(objectTypeChoice.object_type, {emitEvent: false});
    await this.onObjectTypeChange(objectTypeChoice.object_type, Object.keys(seed).length ? seed : undefined);
    return isCurrent();
  }

  /**
   * The late half of applying a profile - runs from
   * `applyPendingWhenReady`, past every reset `selectTable` and
   * `onObjectTypeChange` perform. `restoreProfile` rebuilds the
   * flat rows, lookups and group cards from the stashed payload; this puts
   * its cards on the page, registers their controls and reports what no
   * longer resolves in `profileRestoreWarnings`.
   */
  private applyPendingProfile(): void {
    const pending = this.pendingProfileApply;
    this.pendingProfileApply = null;
    const fromProfile = this.pendingRestoreIsProfile;
    this.pendingRestoreIsProfile = false;
    if (!pending) {
      return;
    }
    this.clearRestoreWarnings();
    // Laid under whatever /batch_fields returns once mappingChanged fires
    // below - not drained here (see the field's own doc comment).
    this.profileBatchValues = {...pending.batch};
    this.blankValuesRejected = pending.blankValuesRejected;

    const result = restoreProfile(pending, {
      descriptors: this.groupDescriptors,
      groupKinds: this.groupKinds,
      columnNames: this.columnNames,
      flatTargets: this.targetOptions,
      lookupTables: this.availableLookupTables,
      addFlatRow: () => this.newAssignment(),
      addLookup: () => this.addLookup(),
      nextGroupKey: () => `g${this.nextGroupKey++}`,
      nextSubBlockKey: () => this.nextSubBlockKey++,
      nextComposeRowId: () => this.nextComposeRowId++,
    }, {
      flatTargets: this.mediaTargetFields,
      addFlatRow: () => this.newAssignment(undefined, true),
    });

    this.blankValues = result.blankValues;
    this.keptMedia = result.keptMedia;
    if (result.mediaJoin) {
      this.mediaJoinColumnControl.setValue(result.mediaJoin.column);
    }
    // A media join whose column the source lacks still sets its options,
    // so that a join column picked later keeps them.
    if (pending.mediaJoinColumn) {
      this.mediaOnMissingControl.setValue(pending.mediaOnMissing);
      this.setMediaJoinOptions(pending.mediaMatch, pending.mediaJoinSeveral, true);
    }
    this.registerGroupControls(result.composeLines.flatMap(composeLineRowControls));
    for (const kind of this.groupKinds) {
      for (const model of result.cards[kind]) {
        this.registerGroupModel(model);
        this.groupModels[kind].push(model);
        if (model.createMissingControl) {
          void this.ensureCreateMissingParents(kind);
        }
      }
    }
    this.restoreReport = {
      warnings: result.warnings.map(w => this.translate.instant(w.key, w.params)),
      carried: pending.carried,
    };
    this.refreshRestoreWarnings();
    // Every restored row and card starts collapsed.
    this.openedLine = null;
    // A collapsed row names the root it is limited to, so a row with a
    // pick has its roots now; the others fetch theirs once opened.
    for (const row of this.assignments) {
      if (row.conceptRootControl.value) {
        void this.loadConceptRoots(row);
      }
    }

    if (fromProfile) {
      this.profileApplied.next({mappings: this.mappings.length, lookups: this.lookupDefinitions.length});
      this.profileBaseline = null;
      // A profile carried from a file is no saved profile, so its page has
      // no baseline to take: it stays unsaved until saved.
      this.pendingBaseline = {afterSeq: this.batchFieldsRequestSeq, restored: !!pending.carried};
    } else if (this.profileBaseline) {
      this.pendingBaseline = {afterSeq: this.batchFieldsRequestSeq, restored: true};
    }
    this.mappingChanged.next();
  }

  /** Whether a save would change the profile last applied or saved: always
   * while there is none, never while an apply's baseline is on its way. */
  get profileUnsaved(): boolean {
    return this.profileDiffers && this.hasMappings;
  }

  /** Saves the current configuration as the profile `name` (insert, or
   * update by name); `true` when it was saved. A refused save - a name
   * held by a profile of another kind, say - leaves its message in
   * `saveProfileStatus`, as does a blank media match pattern, which is
   * refused without a request. */
  async saveProfileAs(name: string): Promise<boolean> {
    this.saveProfileStatus = null;
    const patternProblem = this.mediaPatternProblem;
    if (patternProblem) {
      this.saveProfileStatus = this.translate.instant(patternProblem);
      return false;
    }
    const body = name ? this.profileSaveBody(name) : null;
    if (!body) {
      return false;
    }
    const valueDecisions = this.decisionsToSave;

    this.savingProfile = true;
    try {
      const res = await this.cms.importModuleSaveProfile(body);
      this.selectedProfileId = res.profile_id;
      this.carriedProfileName = null;
      this.profileDecisions = decidedRows(valueDecisions);
      this.carriedDecisions = null;
      this.profileBaseline = profileComparison(body);
      this.pendingBaseline = null;
      this.saveProfileStatus =
        this.translate.instant('TRANS__IMPORT__PROFILE__SAVED', {name: res.profile_name});
      await this.loadProfiles();
      this.stashSourceToSession();
      return true;
    } catch (e: any) {
      this.logger.error(`raw import: could not save profile: ${e}`);
      this.saveProfileStatus = this.errorText(e, 'TRANS__IMPORT__PROFILE__ERROR__SAVE');
      return false;
    } finally {
      this.savingProfile = false;
    }
  }

  /** What a save under `name` sends, `null` before the object type and
   * its batch-defaults view exist. */
  private profileSaveBody(name: string): ProfileSaveRequest | null {
    const objectType = this.selectedObjectType;
    if (!objectType || !this.batchDefaults) {
      return null;
    }
    return profileSaveRequest({
      ...this.configurationState(objectType),
      keptMedia: this.keptMedia,
      blankValuesRejected: this.blankValuesRejected,
      profileName: name,
      valueDecisions: this.decisionsToSave,
      batchDefaults: this.readBatchDefaults()!,
      overwriteExisting: this.overwriteExistingControl.value,
    });
  }

  /** Ends the wait of `pendingBaseline` once a `/batch_fields` request
   * sent after the restore is answered, or none is needed. A profile apply
   * takes its baseline from the page then and stashes it, and a failed
   * request leaves it none; a resumed session keeps its stashed one. */
  private settleProfileBaseline(seq: number, answered: boolean): void {
    const pending = this.pendingBaseline;
    if (!pending || seq <= pending.afterSeq) {
      return;
    }
    this.pendingBaseline = null;
    if (pending.restored) {
      this.refreshProfileDiffers();
      return;
    }
    const body = answered ? this.profileSaveBody('') : null;
    this.profileBaseline = body && profileComparison(body);
    // The stash carries the baseline, and compares.
    this.stashSourceToSession();
  }

  /** Compares the configuration with the profile last applied or saved. */
  private refreshProfileDiffers(): void {
    if (this.pendingBaseline) {
      this.profileDiffers = false;
      return;
    }
    const body = this.profileSaveBody('');
    this.profileDiffers = !!body
      && (!this.profileBaseline || profileChanged(this.profileBaseline, profileComparison(body)));
  }

  /** Saves the selected saved profile, as stored, as a `<name>.json` file;
   * nothing while none is selected or the page holds changes the file
   * would not. */
  async exportProfile(): Promise<void> {
    const profileId = this.selectedProfileId;
    if (!profileId || this.profileUnsaved) {
      return;
    }
    this.profileApplyError = null;
    try {
      downloadProfileFile((await this.cms.importModuleProfile(profileId)).profile);
    } catch (e: any) {
      this.logger.error(`raw import: could not export profile ${profileId}: ${e}`);
      this.profileApplyError = this.errorText(e, 'TRANS__IMPORT__PROFILE__ERROR__LOAD');
    }
  }

  /** Reads `file` as a profile and puts it on the page as an unsaved
   * profile with none selected, asking first while the configuration has
   * changes no profile holds. A file that is not a profile is refused
   * with nothing changed. Nothing is saved. */
  async importProfileFile(file: File): Promise<void> {
    this.profileApplyError = null;
    let profile: ImportModuleProfile | null = null;
    try {
      profile = parseProfileFile(await file.text());
    } catch (e) {
      this.logger.error(`raw import: could not read the profile file: ${e}`);
    }
    if (!profile) {
      this.profileApplyError = this.translate.instant('TRANS__IMPORT__PROFILE__ERROR__IMPORT_FILE');
      return;
    }
    if (this.profileUnsaved) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'confirm-dialog',
        data: {
          modalTitle: 'TRANS__IMPORT__PROFILE__SWITCH',
          modalContent: this.translate.instant('TRANS__IMPORT__PROFILE__SWITCH__CONFIRM', {name: profile.profile_name}),
        } as ConfirmDialogData,
      });
      if (!await firstValueFrom(dialogRef.afterClosed())) {
        return;
      }
    }
    await this.importProfile(profile);
  }

  /** Applies `profile`, a profile read from a file, once the ids in it
   * that name no record here are blanked. A profile the unresolved-ids
   * route refuses with a 400 is reported as a file that is not a
   * profile. */
  private async importProfile(profile: ImportModuleProfile): Promise<void> {
    const token = ++this.profileLoadSeq;
    const isCurrent = () => token === this.profileLoadSeq;
    this.applyingProfile = true;
    try {
      let unresolved: ImportModuleUnresolvedId[];
      try {
        ({unresolved} = await this.cms.importModuleUnresolvedIds(profile));
      } catch (e: any) {
        if (e?.status !== 400) {
          throw e;
        }
        if (isCurrent()) {
          this.logger.error(`raw import: the profile file was refused: ${e?.errorMessage}`);
          this.profileApplyError = this.translate.instant('TRANS__IMPORT__PROFILE__ERROR__IMPORT_FILE');
        }
        return;
      }
      if (!isCurrent()) {
        return;
      }
      const carried = carryProfile(profile, unresolved);
      this.selectedProfileId = null;
      this.carriedProfileName = profile.profile_name;
      await this.applyProfile(carried.profile, {isCurrent, carried});
    } catch (e: any) {
      if (!isCurrent()) {
        return;
      }
      this.logger.error(`raw import: could not import the profile file: ${e}`);
      this.profileApplyError = this.errorText(e, 'TRANS__IMPORT__PROFILE__ERROR__LOAD');
    } finally {
      if (isCurrent()) {
        this.applyingProfile = false;
      }
    }
  }

  /** Clears the restore warnings and what they are built from. */
  private clearRestoreWarnings(): void {
    this.profileRestoreWarnings = [];
    this.restoreReport = null;
  }

  /** Builds `profileRestoreWarnings` from `restoreReport`, each text once. */
  private refreshRestoreWarnings(): void {
    if (!this.restoreReport) {
      return;
    }
    const {warnings, carried} = this.restoreReport;
    this.profileRestoreWarnings = [...new Set([...warnings, ...(carried ? this.carriedWarnings(carried) : [])])];
  }

  /** One warning for each id blanked from an imported profile, naming its
   * field or card, and one for the value decisions dropped. */
  private carriedWarnings({blanked, droppedDecisions}: Pick<CarriedProfile, 'blanked' | 'droppedDecisions'>): string[] {
    const warnings = blanked.map(({place}) => {
      const name = this.blankedPlaceName(place);
      return name === null
        ? this.translate.instant('TRANS__IMPORT__PROFILE__RESTORE__ID_UNRESOLVED_OTHER')
        : this.translate.instant('TRANS__IMPORT__PROFILE__RESTORE__ID_UNRESOLVED', {place: name});
    });
    if (droppedDecisions) {
      warnings.push(this.translate.instant(
        'TRANS__IMPORT__PROFILE__RESTORE__DECISIONS_DROPPED', {count: droppedDecisions}));
    }
    return warnings;
  }

  /** `place` in the page's words, `null` for a place the page has none
   * for. A batch value or a card header the batch fields do not name yet
   * is named as far as the page can. */
  private blankedPlaceName(place: BlankedPlace): string | null {
    const t = (key: string): string => this.translate.instant(key);
    switch (place.kind) {
      case 'pick': {
        const title = t(PICK_TITLE_KEYS[place.key]);
        return place.label ? `${title} (${place.label})` : title;
      }
      case 'mapping':
        return this.labelFor(place.targetField);
      case 'batch': {
        const label = this.batchFields.find(field => field.key === place.key)?.label;
        return [t('TRANS__IMPORT__MAPPING__APPLIES_TO_ALL_ROWS'), ...(label ? [t(label)] : [])].join(' › ');
      }
      case 'decision':
        return `${this.labelFor(place.field)} › ${place.value}`;
      case 'card':
        return this.blankedCardPickName(place);
      default:
        return null;
    }
  }

  /** A card pick's place: the card, the sub-block it sits on, and the pick's
   * label, each card and sub-block with its position when it has one. */
  private blankedCardPickName(place: Extract<BlankedPlace, {kind: 'card'}>): string {
    const t = (key: string): string => this.translate.instant(key);
    const numbered = (title: string, position: number | null) => position === null ? title : `${title} ${position}`;
    const descriptor = this.groupDescriptors[place.card];
    const cardTitleKey = descriptor.titleKey
      || this.groupKindConfigs.find(cfg => cfg.kind === place.card)!.sectionTitleKey;
    const segments = [numbered(t(cardTitleKey), place.position)];
    let header = descriptor.header;
    if (place.subBlock) {
      segments.push(numbered(t(SUB_BLOCK_TITLE_KEYS[place.subBlock.kind]), place.subBlock.position));
      header = descriptor.subBlocks.find(block => block.kind === place.subBlock!.kind)?.header ?? [];
    }
    const labelKey = place.field === 'type_id'
      ? (place.objectType === ObjectTypes.NAMED_OBJECT
        ? NAMED_OBJECT_TYPE_LABEL_KEY : 'TRANS__IMPORT__GROUP_CARD__AGENT_TYPE_PICKER')
      : place.field === 'create_missing_parent_id'
        ? 'TRANS__IMPORT__GROUP_CARD__CREATE_MISSING_PARENT'
        : header.find(h => h.key === place.field)?.label;
    if (labelKey) {
      segments.push(t(labelKey));
    }
    return segments.join(' › ');
  }

  /** Loads the picked profile, asking first while the configuration has
   * changes no profile holds: a load replaces the whole configuration.
   * «Ingen» (`null`) replaces nothing and does not ask. */
  pickProfile(profileId: string | null): void {
    if (!profileId || !this.profileUnsaved) {
      void this.selectProfile(profileId);
      return;
    }
    const name = this.profiles.find(p => p.profile_id === profileId)?.profile_name ?? profileId;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'confirm-dialog',
      data: {
        modalTitle: 'TRANS__IMPORT__PROFILE__SWITCH',
        modalContent: this.translate.instant('TRANS__IMPORT__PROFILE__SWITCH__CONFIRM', {name}),
      } as ConfirmDialogData,
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        void this.selectProfile(profileId);
      }
    });
  }

  /** Confirms (house pattern: `ConfirmDialogComponent`, mirrors every
   * other destructive-action prompt in this app) before actually deleting
   * — a profile is deleted outright with no undo. */
  deleteProfile(profileId: string): void {
    const name = this.profiles.find(p => p.profile_id === profileId)?.profile_name ?? profileId;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'confirm-dialog',
      data: {
        modalTitle: 'TRANS__IMPORT__PROFILE__DELETE',
        modalContent: this.translate.instant('TRANS__IMPORT__PROFILE__DELETE__CONFIRM', {name}),
      } as ConfirmDialogData,
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.performDeleteProfile(profileId);
      }
    });
  }

  private async performDeleteProfile(profileId: string): Promise<void> {
    this.deletingProfileId = profileId;
    try {
      await this.cms.importModuleDeleteProfile(profileId);
      this.profiles = this.profiles.filter(p => p.profile_id !== profileId);
      if (this.selectedProfileId === profileId) {
        this.selectedProfileId = null;
        // Nothing saved holds the configuration any more.
        this.profileBaseline = null;
        this.pendingBaseline = null;
        this.refreshProfileDiffers();
      }
    } catch (e) {
      this.logger.error(`raw import: could not delete profile ${profileId}: ${e}`);
    } finally {
      this.deletingProfileId = null;
    }
  }

  // === session recovery (independent of profiles) =======================
  //
  // The uploaded-but-not-yet-profiled source file, plus everything else the
  // operator has configured for it. Deliberately NOT a saved profile (a
  // profile's whole point is replaying one mapping against a *different*
  // file later) - this is scoped to the one file/table it was stashed
  // against, and is silently overwritten on every change.

  private readonly RESUME_SESSION_STORAGE_KEY = 'primus.import-module.resume';
  private reviewStashTimer: ReturnType<typeof setTimeout> | null = null;

  /** Stashes once the value-review edits pause for `REVIEW_STASH_DELAY_MS`. */
  private stashReviewSoon(): void {
    this.cancelReviewStash();
    this.reviewStashTimer = setTimeout(() => this.stashSourceToSession(), REVIEW_STASH_DELAY_MS);
  }

  /** Writes a stash `stashReviewSoon` is still waiting on, when the page
   * goes away before the pause is over. */
  flushReviewStash(): void {
    if (this.reviewStashTimer !== null) {
      this.stashSourceToSession();
    }
  }

  private cancelReviewStash(): void {
    if (this.reviewStashTimer !== null) {
      clearTimeout(this.reviewStashTimer);
      this.reviewStashTimer = null;
    }
  }

  /** Writes the stash, and compares the configuration it holds with the
   * profile last applied or saved. A resume in progress keeps the stash it
   * resumes from; `restoreSessionReview` writes the resumed one. */
  stashSourceToSession(): void {
    if (!this.resumingSession) {
      this.writeSessionStash();
    }
    this.refreshProfileDiffers();
  }

  private writeSessionStash(): void {
    // Everything is written now, a waiting review stash included.
    this.cancelReviewStash();
    try {
      const session = this.buildSessionSnapshot();
      const stash: SessionStash = {
        dmsIdentifier: this.dmsIdentifier, fileName: this.fileName,
        ...(this.fileSize !== null ? {fileSize: this.fileSize} : {}),
        ...(this.uploadedAt !== null ? {uploadedAt: this.uploadedAt} : {}),
        ...(session ? {session} : {}),
      };
      sessionStorage.setItem(this.RESUME_SESSION_STORAGE_KEY, JSON.stringify(stash));
    } catch {
      // Blocked / private-mode storage - no recovery, not an error.
    }
  }

  private clearSessionStash(): void {
    this.cancelReviewStash();
    try {
      sessionStorage.removeItem(this.RESUME_SESSION_STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  /** On init: if a stash exists, verify the file still resolves before
   * offering to resume - a failed read means it is gone (past DMS
   * retention, or never landed), so the stash is cleared and
   * `previousFileGone` set rather than offering a resume that would just
   * fail. A file the media server cannot serve, uploaded less than
   * `SOURCE_NOT_READY_RESUME_WINDOW_MS` ago, is offered all the same: it
   * is taken as not ready yet, and taking it waits for the file the way a
   * new upload does. The media server answers a file past its retention
   * the same way, so an older upload, or a stash that does not say when,
   * is dropped. Once a file is uploaded while the check runs, the stash
   * is that file's: the check then offers and drops nothing. */
  async checkForResumableSession(): Promise<void> {
    let stashed: SessionStash | null = null;
    try {
      const raw = sessionStorage.getItem(this.RESUME_SESSION_STORAGE_KEY);
      stashed = raw ? JSON.parse(raw) : null;
    } catch {
      stashed = null;
    }
    if (!stashed?.dmsIdentifier || !stashed.fileName) {
      return;
    }
    try {
      const result = await this.cms.importModuleInspectSource({
        dms_identifier: stashed.dmsIdentifier, file_name: stashed.fileName,
      });
      if (this.fileUploaded) {
        return;
      }
      this.resumeInspect = {dmsIdentifier: stashed.dmsIdentifier, fileName: stashed.fileName, result};
      this.resumeCandidate = stashed;
    } catch (e) {
      if (this.fileUploaded) {
        return;
      }
      const uploadedAt = stashed.uploadedAt;
      if (isSourceNotReady(e) && typeof uploadedAt === 'number'
          && Date.now() - uploadedAt < SOURCE_NOT_READY_RESUME_WINDOW_MS) {
        this.resumeCandidate = stashed;
      } else {
        this.clearSessionStash();
        this.previousFileGone = true;
      }
    }
  }

  /** Restore the stashed file without re-uploading it and read its
   * tables, then everything else that was stashed against it - object type, table, mapping, group
   * cards, batch defaults and media - the same way a saved profile
   * restores (`applyEarlyConfig` / `pendingProfileApply`), and open the
   * step view it was stashed on, so the operator lands back where they
   * left off instead of re-picking object type and table by hand. A stash
   * with no `session` (nothing configured yet when it was written)
   * restores just the file and its tables. */
  async resumeSession(): Promise<void> {
    const candidate = this.resumeCandidate;
    if (!candidate) {
      return;
    }
    this.resumeCandidate = null;
    // A profile load or import running now is of a page the resume replaces.
    this.cancelProfileLoad();
    // A read running now is of a file the resume replaces.
    this.supersedeInspect();
    this.dmsIdentifier = candidate.dmsIdentifier;
    this.fileName = candidate.fileName;
    this.fileSize = typeof candidate.fileSize === 'number' ? candidate.fileSize : null;
    this.uploadedAt = typeof candidate.uploadedAt === 'number' ? candidate.uploadedAt : null;
    const session = candidate.session;
    if (!session) {
      await this.inspectSource();
      return;
    }

    const token = ++this.resumingToken;
    const isCurrent = () => token === this.resumingToken;
    this.clearRestoreWarnings();
    this.dropPendingRestore();
    this.resumingSession = true;
    try {
      // The tables are read while the object type is restored.
      const inspected = this.inspectSource();
      const proceeded = await this.applyEarlyConfig(session, isCurrent);
      if (!proceeded) {
        return;
      }

      this.pendingProfileApply = pendingRestore({session});
      this.pendingRestoreIsProfile = false;
      // A stash from before the baseline was stashed leaves the page's.
      if (session.profileBaseline !== undefined) {
        this.profileBaseline = session.profileBaseline && withoutLocked(session.profileBaseline);
      }

      await inspected;
      if (!isCurrent()) {
        return;
      }
      if (session.selectedTable && this.selectedTable !== session.selectedTable
          && this.tables.includes(session.selectedTable)) {
        await this.selectTable(session.selectedTable);
        if (!isCurrent()) {
          return;
        }
      } else {
        this.applyPendingWhenReady();
      }
      this.restoreSessionReview(session);
      if (session.view) {
        // Read as a query parameter is: a value no view has is the first
        // step.
        this.viewRequested.next(viewOfParam(session.view));
      }
    } finally {
      if (isCurrent()) {
        this.resumingSession = false;
      }
    }
  }

  /** The stashed profile and value-review scan, once the stashed mapping
   * is back, and the stash of the resumed page. The scan was stashed with
   * that mapping, so it counts as current for the mapping as restored;
   * group cards come back under new keys, which a scan request key made
   * before the reload would not match. A mapping still waiting for a table
   * whose columns load leaves the stash it was resumed from. */
  private restoreSessionReview(session: SessionSnapshot): void {
    if (session.profileId && this.profiles.some(p => p.profile_id === session.profileId)) {
      this.selectedProfileId = session.profileId;
    } else if (session.profileId) {
      // Nothing saved holds the stashed baseline any more.
      this.profileBaseline = null;
      this.pendingBaseline = null;
    }
    this.profileDecisions = session.profileDecisions ?? [];
    this.carriedDecisions = session.carriedDecisions ?? null;
    if (session.valueReview && this.selectedTable === session.selectedTable) {
      this.conceptFields = session.valueReview.fields;
      this.conceptRowsScanned = session.valueReview.rowsScanned;
      this.conceptScanRequestKey = JSON.stringify(this.buildConceptScanRequest());
      this.conceptScanStale = false;
      this.refreshValueReviewSummary();
    }
    if (!this.pendingProfileApply) {
      this.writeSessionStash();
    }
    this.refreshProfileDiffers();
  }

  dismissResumeCandidate(): void {
    this.resumeCandidate = null;
    this.resumeInspect = null;
    this.clearSessionStash();
  }

  /** Forgets everything read from the file - its tables, the selected
   * table's columns, preview and profile - and what `resetTargetDiscovery`
   * forgets. */
  private resetDiscovery(): void {
    this.supersedeInspect();
    this.inspectFailed = false;
    this.tables = [];
    this.clearSelectedTable();
    this.lookupColumns = {};
    this.lookupColumnsLoading.clear();
    this.lookupColumnsFailed.clear();
    this.resetTargetDiscovery();
  }

  /** Forgets the selected table with its columns, preview and profile;
   * a load of its columns on its way no longer lands. */
  private clearSelectedTable(): void {
    this.selectedTable = '';
    this.firstRows = [];
    this.clearColumnProfile();
    this.columnsRequestSeq++;
    this.loadingColumns = false;
    this.columnsReady = false;
    this.columnsFailed = false;
    this.columns = [];
    this.columnNames = new Set();
    this.mappingVersion++;
  }

  /** Forgets the object type's target fields and the mapping built on
   * them, with everything a new mapping starts without. */
  private resetTargetDiscovery(): void {
    this.targetFieldsRequestSeq++;
    this.targetFieldsLoad = null;
    this.loadingTargetFields = false;
    this.targetFieldsReady = false;
    this.targetFieldsFailed = false;
    this.allTargetFields = [];
    this.cardKindPlacements = new Map();
    this.specialTargetPlacements = {};
    this.otherFieldsPlacement = otherFieldsPlacement([]);
    this.adminEventTargetFields = [];
    this.placementEventTargetFields = [];
    this.mediaTargetFields = [];
    this.targetOptions = [];
    this.startMappingOver(true);
    this.rebuildGroupDescriptors();
    this.clearSourceError();
    // Not `pendingProfileApply`: a profile picked before the file, or
    // one whose own object type resets discovery, must still apply once
    // the table's columns and the target fields load.
    this.profileBatchValues = {};
    this.clearRestoreWarnings();
    this.conceptRootsCache.clear();
  }

  /** Drops the mapping lines, group cards, lookups, batch fields and
   * blank values with their subscriptions, and what was checked or shown
   * against them. The media join resets, emitting its change when
   * `emitEvent` is set. */
  private startMappingOver(emitEvent: boolean): void {
    this.rowSubscriptions.unsubscribe();
    this.rowSubscriptions = new Subscription();
    this.lookupSubscriptions.unsubscribe();
    this.lookupSubscriptions = new Subscription();
    this.assignments = [];
    this.mediaAssignments = [];
    this.openedLine = null;
    this.clearBatchFields();
    this.resetGroupModels();
    this.clearConceptScan();
    // linkMediaControl is a section-1 choice (like the object type) — the
    // operator toggles it, and it persists across a new table or object
    // type. The join column is per-table, so that one does reset.
    this.mediaJoinColumnControl.reset(null, {emitEvent});
    this.keptMedia = null;
    this.mediaOnMissingControl.setValue('block', {emitEvent});
    this.resetMediaJoinOptions(emitEvent);
    this.blankValues = {};
    this.blankValuesRejected = {};
    this.lookupDefinitions = [];
    this.resetLookupChecks();
    this.resetLivePreview();
    this.abandonPreflight();
    this.preflightError = null;
    this.runJob.clearResult();
    this.mappingVersion++;
  }
}
